// Sugalarm — Settings.
//
// Sidebar-tab layout, consolidated in cycle 29 from 11 sections to 6:
//   Display / Alerts / Voice / Members / CGM & devices / Account
//
// The Alerts pane bundles thresholds, notifications, and the test-alarm
// driver behind a single master toggle. CGM & devices
// stacks the connection block with the device list (separator between).
// Account absorbs the Export CSV buttons above the existing account
// actions. A back-compat shim resolves the old `?section=` deep-link
// ids so external bookmarks keep landing where they used to.
//
// Each section is a small inline component with shared access to the
// loaded `settings` / `devices` / `cgmStatus` state. Cross-field
// validation for thresholds is delegated to the server — the slider
// clamps locally to keep the invariant
// `urgent_low < target_low < target_high < urgent_high`, but the
// authoritative check still runs on PATCH.

import { HeroPictureChooser } from "../../components/heroes/HeroPictureChooser";
import { runUserInitiated } from "../../lib/alarmCue";
import { HeroAvatar } from "../../components/ui/HeroAvatar";
import { useHeroRosterVersion } from "../../lib/heroRosterSignal";
import { toCroppableBlob } from "../../lib/croppableImage";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { deleteMyAccount, getDeletionPreview } from "../../api/account";
import {
  deletionConfirmMessage,
  deletionFailureMessage,
} from "../../lib/accountDeletion";
import * as avatarsApi from "../../api/avatars";
import * as backgroundsApi from "../../api/backgrounds";
import { BackgroundCropper } from "../../components/BackgroundCropper";
// Cycle 330.6: avatars use a dedicated cropper (true 1:1 circle, zoom-out to
// fit, slider). Backgrounds keep BackgroundCropper — a display must never
// letterbox, so it must never zoom below cover.
import { AvatarCropper } from "../../components/AvatarCropper";
import {
  ACCEPT_IMAGE_CAMERA,
  ACCEPT_IMAGE_GALLERY,
} from "../../lib/imageAccept";
import { MemberAvatar } from "../../components/ui/MemberAvatar";
import { ActionSheet, type ActionSheetOption } from "../../components/ui/ActionSheet";
import {
  CameraIcon,
  ChevronDownIcon,
  DisconnectIcon,
  KeyIcon,
  TrashIcon,
} from "../../components/ui/icons";
import * as cgmApi from "../../api/cgm";
import * as devicesApi from "../../api/devices";
import * as emergencyContactsApi from "../../api/emergencyContacts";
import * as exportApi from "../../api/export";
import * as memberLinksApi from "../../api/memberLinks";
import * as membersApi from "../../api/members";
import * as remindersApi from "../../api/reminders";
import * as screenBrightness from "../../lib/screenBrightness";
import {
  openPhotoInput,
  photoBlockedByAlarm,
  usePhotoBlockedByAlarm,
} from "../../lib/nativeCamera";
import { ADD_FROM_PHONE_LINE, cameraOnlyDevice } from "../../lib/photoSource";
import * as keepAwake from "../../lib/keepAwake";
import * as kioskWifi from "../../lib/kioskWifi";
import * as settingsApi from "../../api/settings";
import * as ttsApi from "../../api/tts";
import { BackButton } from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Field, inputClass } from "../../components/ui/Field";
import { Pill } from "../../components/ui/Pill";
import { BolusRings } from "../../components/glucose/BolusRings";
import { MealContextLines } from "../../components/glucose/MealContextLines";
import {
  activeBolusesForRings,
  basalForContext,
  CORRECTION_BOLUS_COLOR,
  iobRingColorVars,
  isCorrectionBolus,
  MEAL_BOLUS_COLOR,
  mealForContext,
  type BolusForRings,
} from "../../lib/iob";
import type { AuthState } from "../../hooks/useAuth";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { TTSState } from "../../hooks/useTTS";
import type { UseActiveMember } from "../../hooks/useActiveMember";
import {
  usePushSubscriptions,
  type UsePushSubscriptions,
} from "../../hooks/usePushSubscriptions";
import { useNativePush } from "../../hooks/useNativePush";
import { useWifiAgent, type WifiAgentState } from "../../hooks/useWifiAgent";
import { useVoiceTest } from "../../hooks/useVoiceTest";
import { isVoiceDevEnabled } from "../../hooks/useVoiceCommands";
import { isPluginPresent as voiceCaptureIsPresent } from "../../lib/voiceCapture";
import * as wakeWord from "../../lib/wakeWord";
import {
  useWifiSignal,
  type WifiSignalState,
} from "../../hooks/useWifiSignal";
import { useVoiceAgent, type VoiceAgentState } from "../../hooks/useVoiceAgent";
import { WAKE_WORD_CATALOG, humanizeWakeKey } from "../../api/voice";
import type * as wifiApi from "../../api/wifi";
import {
  capacitorPlatform,
  isCapacitorNative,
  isNativeKioskShell,
} from "../../lib/capacitor";
import {
  isIosNative,
  isAvailable as notificationPluginAvailable,
  getStatus as getNotificationStatus,
  openAppNotificationSettings,
  openShortcutsApp,
  type NotificationStatus,
} from "../../lib/notificationSettings";
import {
  isAvailable as alarmReliabilityAvailable,
  getStatus as getAlarmReliabilityStatus,
  openFullScreenIntentSettings,
  openBatteryOptimizationSettings,
  openDndPolicySettings,
  type AlarmReliabilityStatus,
} from "../../lib/alarmReliability";
import { useAlarmReliability } from "../../hooks/useAlarmReliability";
import AlarmReliabilitySection from "./AlarmReliabilitySection";
import {
  isAndroidTabletClassRuntime,
  isIPadClassRuntime,
} from "../../lib/deviceClass";
import { useAlarmPreview } from "../../hooks/useAlarmPreview";
import { useDevInjectEnabled } from "../../hooks/useDevInjectEnabled";
import { useSettings } from "../../hooks/useSettings";
import { useThresholds } from "../../hooks/useThresholds";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import { GlancePill } from "../../components/kiosk/GlancePill";
import { SensorPausePrompt } from "../../components/kiosk/SensorPausePrompt";
import { WifiIndicator } from "../../components/kiosk/WifiIndicator";
import { SiriBridgeDiagnostics } from "./SiriBridgeDiagnostics";
import { hapticSelect } from "../../lib/haptics";
import { AlarmTestConsole } from "../../components/dev/AlarmTestConsole";
import type { AlarmEpisodeState } from "../../hooks/useAlarmEpisode";
import type { AlarmSounds } from "../../hooks/useAlarmSounds";
import {
  ALARM_SOUND_OPTIONS,
  ALARM_SOUND_SETTING_KEY,
  type AlarmZone,
} from "../../lib/alarmSoundOptions";
import {
  cancelPreview,
  PREVIEW_DURATION_MS,
  sgvForZone,
  startPreview,
  type PreviewZone,
} from "../../lib/alarmPreview";
import { selectCurrentGlucoseFor } from "../../lib/currentGlucose";
import {
  fmtAge,
  fmtTime,
  zoneColor,
  zoneOf,
  zoneSoft,
} from "../../lib/glucose";
// Cycle 330.2: the Settings → Voice "Speak sample" sentence. Single owner,
// unit-tested — do not re-derive it here.
import { voiceSampleText } from "../../lib/voiceSample";
import type { Reading, Zone } from "../../lib/types";
import { getDeviceId } from "../../lib/auth";
import { useConfirm } from "../../lib/confirmContext";
import { countActiveDevices, selectHasPairedKiosk } from "../../lib/kiosk";
import {
  ALARM_REPEAT_INTERVAL_DEFAULT_SECONDS,
  ALARM_REPEAT_INTERVAL_OPTIONS,
} from "../../lib/alarmRepeat";
import { pushSupported } from "../../lib/push";
import {
  BG_BY_ID,
  BG_FAMILIES,
  BG_PRESETS,
  type BgPreset,
  isImage as isImageBg,
  isScene as isSceneBg,
} from "../../lib/mobileBackgrounds";
import { useThemePacks } from "../../lib/themePacksContext";
import type {
  ApiResult,
  Background,
  Device,
  DisplayTheme,
  EmergencyContact,
  EmergencyContactCreateIn,
  EmergencyContactLevel,
  EmergencyContactUpdateIn,
  Member,
  MemberLinkGrant,
  MemberLinkInvite,
  MemberLinkInviteWithCode,
  MobileBackground,
  Treatment,
  UserSettings,
  UserSettingsPatch,
  VoiceInfo,
} from "../../lib/types";
import { CGMConnectForm } from "../login/CGMConnectForm";
import { ProviderPicker } from "../login/ProviderPicker";
import { CgmConnectModal } from "./CgmConnectModal";
import { findProvider, type Provider, type ProviderId } from "../login/providers";
import { PasskeysSection } from "./PasskeysSection";
// Cycle 464 — Settings → Account → Subscription + the kiosk status card.
import { KioskSubscriptionCard, SubscriptionCard } from "./SubscriptionCard";
import { isKioskSurface } from "../../lib/billing";
import { RemindersSection } from "./RemindersSection";
import {
  SettingsCardGlassContext,
  SettingsCollapseAllContext,
  SettingsCollapseContext,
  SettingsKioskContext,
  SettingsTabletThemeContext,
  SubsectionCard,
  type CollapseAllController,
  type CollapseAllRegistration,
} from "./SubsectionCard";
// Cycle 330: variant-aware HH:MM control for the Voice → Quiet hours card.
import { TimeField } from "./TimeField";
import { ThresholdSlider, type ThresholdSliderHandle } from "./ThresholdSlider";
import { AlarmClockModeSection } from "./AlarmClockModeSection";
import type { ClockMode } from "../../hooks/useAlarmClockMode";
import { SettingsHeroCard } from "./SettingsHeroCard";
import styles from "./SettingsScreen.module.css";
import { FitOrHideHint } from "../log/FitOrHideHint";
import mi from "./SettingsMobileIndex.module.css";
// Cycle 243.3: the per-zone sound-picker styles live in the ThresholdSlider
// CSS module (the picker renders inside its cards). `AlarmSoundPicker` is
// defined here, so it must use THIS module's hashed class names — not
// `styles` (SettingsScreen.module.css), where `.sound*` don't exist (that
// bug left the whole picker unstyled: white native dropdown, sentence-case
// label, plain button).
import ts from "./ThresholdSlider.module.css";
import {
  ACCOUNT_SECTION_LABEL,
  DEVICES_CARD_LABEL,
  MANAGE_DEVICES_LABEL,
  PAIR_A_DEVICE_LABEL,
} from "../../lib/pairDevicePath";

// ─── Cycle 74: small inline SVGs for the SubsectionCard `icon` slot.
//     Inline because importing icons by name would mean a new asset
//     pipeline; the existing app uses inline SVGs throughout. ────────
const Icon = {
  thresholds: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3 5 L15 5 M3 9 L15 9 M3 13 L15 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="6" cy="5" r="2" fill="currentColor" />
      <circle cx="12" cy="9" r="2" fill="currentColor" />
      <circle cx="9" cy="13" r="2" fill="currentColor" />
    </svg>
  ),
  moon: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M14 11A6 6 0 0 1 7 4a6 6 0 1 0 7 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  bell: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M4.5 13h9l-1.2-1.6V8a3.3 3.3 0 1 0-6.6 0v3.4L4.5 13Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7.5 14.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  shield: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2 3.5 4.2v4.6c0 3 2.3 5.7 5.5 7 3.2-1.3 5.5-4 5.5-7V4.2L9 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  speaker: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M4 7v4h2.5l3.5 2.5v-9L6.5 7H4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M12 6.5a3.5 3.5 0 0 1 0 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  display: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 15.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  voice: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="7" y="2.5" width="4" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4.5 9a4.5 4.5 0 0 0 9 0M9 13.5v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  clock: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 5.5V9l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  users: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 14.5c.6-2.4 2.7-4 5.5-4s4.9 1.6 5.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  cgm: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.5 11 6 7.5l3 3 4-5 2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  wifi: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2 7a10 10 0 0 1 14 0M4.5 9.5a6.5 6.5 0 0 1 9 0M7 12a3 3 0 0 1 4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="9" cy="14.5" r="1" fill="currentColor" />
    </svg>
  ),
  devices: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.5" y="3.5" width="9" height="7" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="11" y="6.5" width="4.5" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
  download: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 3v8m0 0 3-3M9 11 6 8M3.5 14.5h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Cycle 161 — mobile index ACCOUNT group "Log Out" row. Same stroke
  // style/sizing as the rest of the set; additive only.
  logout: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M7.5 2.5H4.5A1.5 1.5 0 0 0 3 4v10a1.5 1.5 0 0 0 1.5 1.5h3M12 12.5 15.5 9 12 5.5M15.5 9H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  account: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="9" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M4.7 14.2C5.5 12.5 7.1 11.5 9 11.5s3.5 1 4.3 2.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  alarm: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="10" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 7v3l2 1M3 5.5 5 3.5M15 5.5 13 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  // Cycle 84 — Theme section
  palette: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2.5a6.5 6.5 0 0 0 0 13c1 0 1.5-.8 1.5-1.5 0-.9-.7-1.2-.7-2 0-.6.5-1 1.1-1H12a3.5 3.5 0 0 0 3.5-3.5C15.5 4.9 12.6 2.5 9 2.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="6" cy="8" r="1" fill="currentColor" />
      <circle cx="9" cy="6" r="1" fill="currentColor" />
      <circle cx="12" cy="8" r="1" fill="currentColor" />
    </svg>
  ),
  image: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.5" y="3.5" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="6.5" cy="7" r="1.2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3 13l3.5-3.5 2.5 2.5 3-3 3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  ),
  // Cycle 308 — mobile row-based actions: circular-arrow reconnect/reset,
  // the Danger Zone warning triangle, and a padlock (passkeys / change
  // password rows). Same stroke style/sizing as the rest of the set.
  refresh: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M14.5 9a5.5 5.5 0 1 1-1.6-3.9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M14.5 2.5v3h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M7.7 3.2 2.4 12.4a1.5 1.5 0 0 0 1.3 2.3h10.6a1.5 1.5 0 0 0 1.3-2.3L10.3 3.2a1.5 1.5 0 0 0-2.6 0Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 7v3.2M9 12.6h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  lock: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="4" y="8" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6.5 8V6a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
};

interface Props {
  auth: AuthState;
  activeMember: UseActiveMember;
  tts: TTSState;
  variant: FrameVariant;
  /** Cycle 67: passed through to `<AlarmTestConsole>` (dev-only, only
   *  mounted when `SUGALARM_DEV_INJECT_ENABLED` is on). Optional so
   *  every other surface that already constructs `<SettingsScreen>`
   *  keeps compiling — the console no-ops without it. */
  episode?: AlarmEpisodeState;
  /** Cycle 82: the bound alert row's server-authored `recovered_at`
   *  (from `socket.lastAlert`), threaded through to the dev console's
   *  live-state readout so an operator can watch the engine stamp it.
   *  Optional / dev-only. */
  episodeRecoveredAt?: string | null;
  /** Cycle 115: live socket + clock for the landscape glance pill (the
   *  active member's reading drives value/trend/age/source). Optional so
   *  other constructors keep compiling; the pill only renders in mobile
   *  landscape. */
  socket?: GlucoseSocketState;
  now?: number;
  /** Cycle 145: alarm-clock-mode controller (from the shell) — drives
   *  the Display card's "Start now". Optional so other constructors
   *  keep compiling; the card hides Start-now without it. */
  clockMode?: ClockMode;
  /** Cycle 242: the alarm-sound owner — drives the Thresholds cards' per-zone
   *  sound preview (play/stop). Optional so other constructors keep
   *  compiling; the picker hides its preview button without it. */
  alarms?: AlarmSounds;
}

// Cycle 29a: mobile drill-down threshold. Above this width on a
// non-kiosk variant we keep the side-rail two-column layout; at or
// below it (or whenever the frame variant is mobile) Settings flips
// to an index-list ↔ detail-pane drill-down.
const MOBILE_BREAKPOINT_PX = 760;

export type SectionId =
  | "display"
  | "appearance"
  | "alerts"
  | "voice"
  | "reminders"
  | "members"
  | "connection"
  // Cycle 245: Emergency contacts as its own top-level tab — Android-tablet
  // kiosk ONLY (absent from every other variant's menu, where emergency
  // contacts stays inside the Alerts pane).
  | "contacts"
  // Cycle 224: a top-level Wi-Fi section — Android-tablet kiosk ONLY (it's
  // absent from every other variant's menu). The pane shows online/offline
  // status + a no-PIN "Change Wi-Fi network" button that opens the cycle-223
  // native connector on demand.
  | "wifi"
  // Cycle 315: the Alarm Reliability checklist — Android MOBILE shell ONLY
  // (gated on the native AlarmReliability plugin; absent from every other
  // variant's menu and resolver).
  | "alarm_reliability"
  | "account";

// Cycle 110 — soft-tone vocabulary for the section icon chips. Each tone
// is a soft background + saturated foreground pair derived from existing
// theme tokens (the same tone family `SubsectionCard` uses), so chips
// stay legible across all six themes and over photo backgrounds without
// any hardcoded hex. `urgent` is deliberately excluded — a settings
// section must never read as an active alarm.
type SectionTone = "accent" | "warn" | "ok" | "neutral";

// Cycle 110 — the single source of truth for how each section is
// *presented* across all three surfaces (kiosk tile grid + icon rail,
// mobile index rows, web rail). Order matches the existing 8-section IA;
// ids, the `SectionId` union, and section ORDER are unchanged. Icons
// reuse the cycle-74 inline library above so there's no new asset
// pipeline. The Account chip swaps in the active member's avatar when one
// exists (see `SectionChip`).
// Cycle 388: annotated with the shared `SectionMeta` (declared below —
// interfaces hoist) instead of an inline structural clone, so this and
// TABLET_SECTION_META are ONE type. The two are unioned at the menu render
// (`tabletKiosk ? TABLET : SECTION_META`), and a duplicated shape meant an
// optional field added to one was unreachable through that union.
const SECTION_META: SectionMeta[] = [
  { id: "display", title: "Display", icon: Icon.display, tone: "accent" },
  // Cycle 84 — Theme: Light/Dark (mobile), 6 themes + backgrounds (web+kiosk)
  { id: "appearance", title: "Theme", icon: Icon.palette, tone: "accent" },
  { id: "alerts", title: "Alerts", icon: Icon.bell, tone: "warn" },
  { id: "voice", title: "Voice", icon: Icon.voice, tone: "accent" },
  { id: "reminders", title: "Reminders", icon: Icon.clock, tone: "ok" },
  { id: "members", title: "Members", icon: Icon.users, tone: "accent" },
  { id: "connection", title: "CGM & devices", icon: Icon.cgm, tone: "ok" },
  { id: "account", title: ACCOUNT_SECTION_LABEL, icon: Icon.account, tone: "neutral" },
];

// Cycle 245 — Android-tablet kiosk ONLY section menu (used in place of
// SECTION_META when `tabletKiosk`). Reorganized into 9 cohesive,
// single-purpose tabs (each answers one user question), most-important
// first: **Alarms · Reminders · Sensor · Contacts · Members · Display ·
// Voice · Wi-Fi · Account**. Changes vs the cycle-224 layout:
//   - "Alerts" → "Alarms" (clearer name; the master toggle leads it).
//   - Reminders is its OWN tab (was folded into Alerts) — a scheduled nudge
//     is conceptually distinct from a glucose alarm.
//   - "CGM & devices" → "Sensor" (CGM connection only, the data source);
//     the paired-Devices card moves into Account.
//   - Emergency contacts is its OWN tab ("Contacts").
//   - "Display" + "Theme" MERGE into one "Display" tab (both are appearance).
// The relocated SectionIds + panes are untouched and still used by every
// other variant; deep-links to retired-here ids self-heal (resolveTabletSection).
const TABLET_SECTION_META: SectionMeta[] = [
  { id: "alerts", title: "Alarms", icon: Icon.bell, tone: "warn", emoji: "🔔" },
  { id: "reminders", title: "Reminders", icon: Icon.clock, tone: "ok", emoji: "⏰" },
  { id: "connection", title: "Sensor", icon: Icon.cgm, tone: "accent", emoji: "📡" },
  // Cycle 388: "Contacts" → "Heroes" (the people who come when a low
  // hits), with the literal old name kept as the muted sub-label so the
  // rename never costs anyone a beat of "wait, what lives in here?".
  { id: "contacts", title: "Heroes", subLabel: "(Contacts)", icon: Icon.shield, tone: "warn", emoji: "🦸" },
  { id: "members", title: "Members", icon: Icon.users, tone: "accent", emoji: "👥" },
  { id: "display", title: "Display", icon: Icon.display, tone: "accent", emoji: "🖥️" },
  { id: "voice", title: "Voice", icon: Icon.voice, tone: "accent", emoji: "🎙️" },
  // Wi-Fi: 🛜 (U+1F6DC, Unicode 15). EmojiGlyph falls back to the inline
  // concentric-arc `Icon.wifi` SVG if the device font can't paint it.
  { id: "wifi", title: "Wi-Fi", icon: Icon.wifi, tone: "ok", emoji: "🛜" },
  { id: "account", title: ACCOUNT_SECTION_LABEL, icon: Icon.account, tone: "neutral" },
];

const TONE_CLASS: Record<SectionTone, string | undefined> = {
  accent: styles.chipAccent,
  warn: styles.chipWarn,
  ok: styles.chipOk,
  neutral: styles.chipNeutral,
};

const RAIL_TONE_CLASS: Record<SectionTone, string | undefined> = {
  accent: styles.railToneAccent,
  warn: styles.railToneWarn,
  ok: styles.railToneOk,
  neutral: styles.railToneNeutral,
};

// Cycle 29: the section sidebar was consolidated from 11 ids down to
// 6. Old links (and the WebSocket settings_update deep-link path)
// keep working — we resolve each retired id to its new home so
// `/settings?section=cgm` lands on `connection` instead of falling
// through to the default.
// ─── Cycle 161: mobile-index grouping config ───────────────────────────
// The MOBILE index (portrait drill-down, no `?section=`) renders the
// hero card then these three labeled grouped cards. Presentation-only:
// section rows reference the EXISTING `SectionId`s + `SECTION_META`
// title/icon + live `sectionStatus` subtitle; the three ACCOUNT rows all
// map to the existing `account` section/actions (no new SectionId, no
// feature moved out of the pane). Tile accents are `--sec-*` tokens
// (cycle 129) so they track the theme — no raw colors.
type MobileIndexRow =
  | { kind: "section"; id: SectionId; accent: string }
  | {
      kind: "action";
      key: "profile" | "export" | "logout";
      title: string;
      subtitle: string;
      icon: React.ReactNode;
      accent: string;
      destructive?: boolean;
    };

const MOBILE_INDEX_GROUPS: { label: string; rows: MobileIndexRow[] }[] = [
  {
    label: "Monitoring",
    rows: [
      { kind: "section", id: "connection", accent: "var(--sec-green)" },
      { kind: "section", id: "alerts", accent: "var(--sec-amber)" },
      { kind: "section", id: "reminders", accent: "var(--sec-green)" },
      { kind: "section", id: "members", accent: "var(--sec-cyan)" },
    ],
  },
  {
    label: "Appearance",
    rows: [
      { kind: "section", id: "display", accent: "var(--sec-cyan)" },
      { kind: "section", id: "appearance", accent: "var(--sec-violet)" },
      { kind: "section", id: "voice", accent: "var(--sec-cyan)" },
    ],
  },
  {
    label: "Account",
    rows: [
      {
        kind: "action",
        key: "profile",
        title: "Profile",
        subtitle: "View and edit your profile",
        icon: Icon.account,
        accent: "var(--sec-steel)",
      },
      {
        kind: "action",
        key: "export",
        title: "Export Data",
        subtitle: "Export your data",
        icon: Icon.download,
        accent: "var(--sec-steel)",
      },
      {
        kind: "action",
        key: "logout",
        title: "Log Out",
        subtitle: "Sign out of this account",
        icon: Icon.logout,
        accent: "var(--sec-steel)",
        destructive: true,
      },
    ],
  },
];

// ─── Cycle 308: mobile index v2 — kiosk-aligned taxonomy ────────────────
// The `variant === "mobile"` portrait index renders FOUR eyebrow-labeled
// groups in the cycle-245 tablet's single-purpose-tab language (Alarms ·
// Contacts · Reminders · Sensor · Members · Display · Voice · Account),
// with bright SOLID color tiles (--settile-*, rounded squares on iOS/web,
// circles on Android-native). Presentation-only: rows reference EXISTING
// SectionIds + live `sectionStatus` subtitles; the cycle-161 three-group
// config above keeps serving the ≤760px narrow-web drill-down
// byte-identical. The three cycle-161 ACCOUNT action rows are superseded
// by the single Account row — Export and Log Out remain reachable INSIDE
// the recomposed Account pane (moved, not removed).
type MobileIndexRowV2 = {
  id: SectionId;
  title: string;
  tile: string;
  /** Cycle 388: muted parenthetical under the title (see SectionMeta). */
  subLabel?: string;
};

const MOBILE_INDEX_GROUPS_V2: { label: string; rows: MobileIndexRowV2[] }[] = [
  {
    label: "Alarms & safety",
    rows: [
      // Cycle 341: Reminders moved ahead of Contacts (design audit —
      // matches the kiosk's TABLET_SECTION_META relative order, so a
      // household on both surfaces finds things in the same place).
      { id: "alerts", title: "Alarms", tile: "var(--settile-alarms)" },
      { id: "reminders", title: "Reminders", tile: "var(--settile-reminders)" },
      // Cycle 388: renamed to "Heroes"; the old name rides along as the
      // muted sub-label (the kiosk tab does the same).
      {
        id: "contacts",
        title: "Heroes",
        subLabel: "(Contacts)",
        tile: "var(--settile-contacts)",
      },
    ],
  },
  {
    label: "Monitoring",
    rows: [
      { id: "connection", title: "Sensor", tile: "var(--settile-sensor)" },
      { id: "members", title: "Members", tile: "var(--settile-members)" },
    ],
  },
  {
    label: "Appearance",
    rows: [
      // Display + Theme MERGE into one pane on mobile (the cycle-245
      // tablet mechanism) — there is no separate Theme row.
      { id: "display", title: "Display", tile: "var(--settile-display)" },
      { id: "voice", title: "Voice", tile: "var(--settile-voice)" },
    ],
  },
  {
    label: "Account",
    rows: [{ id: "account", title: ACCOUNT_SECTION_LABEL, tile: "var(--settile-account)" }],
  },
];

// Detail-header titles on the mobile variant (the kiosk-aligned names);
// every other surface keeps the SECTION_META titles byte-identical.
const MOBILE_SECTION_TITLES: Partial<Record<SectionId, string>> = {
  alerts: "Alarms",
  connection: "Sensor",
  // Cycle 388: renamed category (the pane's card carries the "(Contacts)"
  // clarification in its subtitle).
  contacts: "Heroes",
  display: "Display",
  // Cycle 315 — Android-mobile-only section (see SectionId).
  alarm_reliability: "Alarm Reliability",
};

// Icon for a mobile-index row: SECTION_META first, then the tablet meta
// (which is where the `contacts` entry lives). `alarm_reliability` (cycle
// 315) lives in neither — it's Android-mobile-only, so it resolves here.
function mobileSectionIcon(id: SectionId): React.ReactNode {
  if (id === "alarm_reliability") return Icon.shield;
  return (
    SECTION_META.find((s) => s.id === id)?.icon ??
    TABLET_SECTION_META.find((s) => s.id === id)?.icon ??
    null
  );
}

// Platform-adaptive tile shape (deliverable: iOS-style rounded squares on
// iOS-native + non-native mobile-width web, Material circles on
// Android-native). One CSS modifier driven by this check — the components
// are not forked. Inside the official Android kiosk shell this code never
// runs (kiosk variant → no mobile index).
function mobileTileRound(): boolean {
  return isCapacitorNative() && capacitorPlatform() === "android";
}

const LEGACY_SECTION_MAP: Record<string, SectionId> = {
  targets: "alerts",
  // `quiet` (the retired Quiet hours pane, removed cycle 216) still
  // resolves to Alerts so old deep-links don't 404.
  quiet: "alerts",
  notifications: "alerts",
  test: "alerts",
  cgm: "connection",
  devices: "connection",
  export: "account",
  // Cycle 464: the Subscription card lives at the top of Account.
  subscription: "account",
};

function resolveSectionParam(raw: string | null): SectionId {
  if (raw == null) return "alerts";
  if (SECTION_META.find((s) => s.id === raw)) return raw as SectionId;
  if (raw in LEGACY_SECTION_MAP) return LEGACY_SECTION_MAP[raw]!;
  return "alerts";
}

// Cycle 245 — Android-tablet deep-link self-heal. The tablet menu has its
// own set of tabs; an incoming `?section=` for an id that isn't a tablet tab
// resolves to its tablet home (and the URL rewrites). `network`/`wifi` → the
// Wi-Fi tab; `appearance` (the former "Theme" tab) → the merged Display tab.
// `reminders` / `connection` (Sensor) / `contacts` are now their OWN tablet
// tabs, so they resolve to themselves (no remap).
const TABLET_SECTION_REMAP: Partial<Record<SectionId, SectionId>> = {
  appearance: "display",
};

function resolveTabletSection(raw: string | null): SectionId {
  if (raw === "wifi" || raw === "network") return "wifi";
  // A valid tablet tab id resolves to itself — covers `contacts`, which is a
  // tablet-only SectionId absent from SECTION_META (so resolveSectionParam
  // would otherwise default it to "alerts" and a Contacts reload would bounce
  // to Alarms).
  if (raw != null && TABLET_SECTION_META.find((s) => s.id === raw)) {
    return raw as SectionId;
  }
  const base = resolveSectionParam(raw);
  return TABLET_SECTION_REMAP[base] ?? base;
}

// Cycle 308 — mobile deep-link self-heal (the `variant === "mobile"`
// mirror of resolveTabletSection; kiosk/web resolution untouched).
// `contacts` is now a first-class mobile section; `appearance`/`theme`
// resolve to the merged Display pane; `devices` (whose card moved into
// Account, matching the tablet split) resolves to `account` instead of
// the legacy `connection` so old bookmarks land where the list now lives.
const MOBILE_SECTION_REMAP: Record<string, SectionId> = {
  appearance: "display",
  theme: "display",
  devices: "account",
};

function resolveMobileSection(raw: string | null): SectionId {
  if (raw === "contacts") return "contacts";
  // Cycle 315: the Alarm Reliability pane exists ONLY inside the Android
  // standard shell carrying the native plugin — a deep-link on any other
  // mobile surface (iOS, mobile web, an old APK) self-heals to Alarms.
  if (raw === "alarm_reliability") {
    return alarmReliabilityAvailable() ? "alarm_reliability" : "alerts";
  }
  // Object.hasOwn (not `in`): `raw` is user-controlled query input, and the
  // `in` operator would resolve prototype keys (`toString`, `constructor`)
  // to Function objects instead of a SectionId.
  if (raw != null && Object.hasOwn(MOBILE_SECTION_REMAP, raw)) {
    return MOBILE_SECTION_REMAP[raw]!;
  }
  const base = resolveSectionParam(raw);
  return MOBILE_SECTION_REMAP[base] ?? base;
}

/** True when the SPA is on the Android-tablet kiosk surface — the gate that
 *  selects the reorganized menu, emoji/avatar, Wi-Fi tab, and deep-link
 *  remap. Mirrors the in-component `tabletKiosk` (computed for the initial
 *  section-state initializer, which runs before that local is defined). */
function isTabletKioskSurface(variant: FrameVariant): boolean {
  return variant === "kiosk" && isAndroidTabletClassRuntime();
}

// ─── Cycle 110: shared section icon + live status helpers ──────────────

function capitalize(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

interface StatusInputs {
  settings: UserSettings | null;
  cgmStatus: cgmApi.CGMStatus | null;
  memberCount: number;
  reminderCount: number | null;
  reminderError: boolean;
  contactCount: number | null;
  contactError: boolean;
  wifi: WifiSignalState;
  variant: FrameVariant;
}

// One-line, best-effort live status for a section, derived ONLY from data
// the client already has or already fetched. Returns null when there's
// nothing meaningful to show yet (callers render a muted "—" / nothing).
function sectionStatus(
  id: SectionId,
  {
    settings,
    cgmStatus,
    memberCount,
    reminderCount,
    reminderError,
    contactCount,
    contactError,
    wifi,
    variant,
  }: StatusInputs,
): string | null {
  switch (id) {
    case "display":
      if (!settings) return null;
      return `${settings.units} · ${settings.display_24h ? "24h" : "12h"} clock`;
    case "appearance":
      if (!settings) return null;
      return capitalize(settings.display_theme);
    case "alerts": {
      if (!settings) return null;
      return settings.alerts_enabled ? "On" : "Off";
    }
    case "voice": {
      if (!settings) return null;
      // Cycle 330: the old `shortVoiceName` parsed the previous engine's
      // locale short-name ("en-US-AriaNeural" → "Aria"). `tts_voice` is now
      // an OPAQUE voice_id,
      // so that helper is gone (INTENTIONAL removal — it would have rendered
      // a raw id like "21m00Tcm4TlvDq8ikWAM" as the tile subtitle). This tile
      // has no voice catalog to resolve a friendly name from, so it reports
      // the cadence + quiet-hours state instead — the two things the member
      // can actually act on. No provider name, ever.
      const every = settings.tts_announce_interval_minutes;
      const cadence = every > 0 ? `Auto ${every}m` : "Manual";
      return settings.tts_quiet_hours_enabled
        ? `${cadence} · quiet hours on`
        : cadence;
    }
    case "reminders":
      if (reminderError) return "Scheduled alerts";
      if (reminderCount == null) return null;
      return `${reminderCount} scheduled`;
    case "members":
      return `${memberCount} member${memberCount === 1 ? "" : "s"}`;
    case "contacts":
      // Cycle 247 — emergency-contact count for the tablet "Heroes" tile.
      // Cycle 388: the failure string reads standalone AND behind the
      // "(Contacts) · " sub-label prefix (the old "Emergency contacts"
      // rendered as "(Contacts) · Emergency contacts").
      if (contactError) return "Count unavailable";
      if (contactCount == null) return null;
      return `${contactCount} contact${contactCount === 1 ? "" : "s"}`;
    case "wifi": {
      // Cycle 247 — the connected network + signal % on the tablet "Wi-Fi"
      // tile, from the cycle-235.4 native WifiSignal readout. `available` is
      // true only inside the Android-tablet kiosk APK; null elsewhere.
      if (!wifi.available) return null;
      if (!wifi.connected || wifi.percent < 0) return "Not connected";
      const net = wifi.ssid ?? "Wi-Fi";
      return `${net} · ${wifi.percent}%`;
    }
    case "connection":
      if (cgmStatus?.connected && cgmStatus.provider) {
        return `${capitalize(cgmStatus.provider)} · connected`;
      }
      return "Not connected";
    case "account":
      // Export is hidden on the kiosk (no filesystem to download into).
      return variant === "kiosk" ? "Log out" : "Export · log out";
    default:
      return null;
  }
}

interface SectionMeta {
  id: SectionId;
  title: string;
  icon: React.ReactNode;
  tone: SectionTone;
  /** Cycle 388: an optional smaller line rendered directly UNDER `title`
   *  in the section menus (kiosk tile grid + kiosk top bar + the mobile
   *  index row). Its only job is to keep a renamed, friendlier category
   *  self-explanatory — "Heroes" over a muted "(Contacts)". Absent on
   *  every other entry, so those render byte-identically. */
  subLabel?: string;
  /** Cycle 224: full-color emoji shown in the Android-tablet section menu
   *  (tile grid + top bar) in place of the SVG glyph. Set only on the
   *  tablet-only `TABLET_SECTION_META`; absent everywhere else, so every
   *  non-tablet surface renders `icon` byte-identically. The Account entry
   *  carries no emoji — it always renders the member avatar (SectionChip). */
  emoji?: string;
}

// Cycle 224 — full-color emoji rendering for the Android-tablet section
// menu. Samsung One UI ships a color-emoji font; we force emoji presentation
// via an emoji-first font stack so the glyphs paint in color, not as
// monochrome outline fallbacks. The emoji literals in TABLET_SECTION_META
// already carry U+FE0F where the base codepoint is text-default (🖥️ 🎙️), so
// no per-codepoint variation-selector fixup is needed here.
const EMOJI_FONT =
  '"Apple Color Emoji","Noto Color Emoji","Samsung Color Emoji",' +
  '"Segoe UI Emoji","EmojiOne Color","Android Emoji",sans-serif';

// 🛜 (U+1F6DC) is Unicode 15 (2022) — present on the A9+/A11+ (One UI 5.1+),
// but older fonts lack it and would paint tofu. Probe once (canvas .notdef
// compare): if the device can't render 🛜, fall back to the inline
// concentric-arc Wi-Fi SVG (`Icon.wifi`). Memoized; safe on any non-DOM
// context (returns false → SVG).
let _wifiEmojiOk: boolean | null = null;
function wifiEmojiSupported(): boolean {
  if (_wifiEmojiOk != null) return _wifiEmojiOk;
  _wifiEmojiOk = (() => {
    try {
      if (typeof document === "undefined") return false;
      const c = document.createElement("canvas");
      c.width = 24;
      c.height = 24;
      const ctx = c.getContext("2d", { willReadFrequently: true });
      if (!ctx) return false;
      ctx.textBaseline = "top";
      ctx.font = `20px ${EMOJI_FONT}`;
      const render = (ch: string): string => {
        ctx.clearRect(0, 0, 24, 24);
        ctx.fillText(ch, 0, 0);
        return c.toDataURL();
      };
      // A guaranteed-undefined codepoint renders the .notdef box; if 🛜
      // renders identically it has no glyph → unsupported.
      const tofu = render("\u{10FFFF}");
      const wifi = render("\u{1F6DC}");
      return wifi !== tofu;
    } catch {
      return false;
    }
  })();
  return _wifiEmojiOk;
}

// The emoji a section menu entry should paint, or null to fall back to its
// SVG `icon`. Only `wifi` is gated on font support; the rest are old/common
// emoji the device renders (the cycle-207 card emoji prove it).
function sectionMenuEmoji(meta: SectionMeta): string | null {
  if (!meta.emoji) return null;
  if (meta.id === "wifi" && !wifiEmojiSupported()) return null;
  return meta.emoji;
}

// A tinted rounded-square icon chip (kiosk tiles, kiosk rail, mobile
// rows). For the Account section we swap the generic glyph for the active
// member's circular avatar when one exists — reusing the cycle-105
// `MemberAvatar` (blob cache + shield fallback), exactly the avatar the
// header shows. No avatar → the neutral tinted glyph chip.
function SectionChip({
  meta,
  chipSize,
  glyphSize,
  avatarId,
  avatarName,
}: {
  meta: SectionMeta;
  chipSize: number;
  glyphSize: number;
  avatarId: string | null;
  avatarName: string;
}) {
  if (meta.id === "account" && avatarId) {
    return (
      <MemberAvatar
        avatarId={avatarId}
        name={avatarName}
        size={chipSize}
        shape="circle"
      />
    );
  }
  const emoji = sectionMenuEmoji(meta);
  return (
    <span
      className={`${styles.iconChip} ${TONE_CLASS[meta.tone]}`}
      style={
        {
          "--chip": `${chipSize}px`,
          "--glyph": `${glyphSize}px`,
        } as React.CSSProperties
      }
      aria-hidden
    >
      {emoji ? (
        <span className={styles.sectionEmoji} style={{ fontFamily: EMOJI_FONT }}>
          {emoji}
        </span>
      ) : (
        meta.icon
      )}
    </span>
  );
}

// An untinted glyph (web rail). Account swaps in the avatar when present.
function SectionRailGlyph({
  meta,
  size,
  avatarId,
  avatarName,
}: {
  meta: SectionMeta;
  size: number;
  avatarId: string | null;
  avatarName: string;
}) {
  if (meta.id === "account" && avatarId) {
    return (
      <MemberAvatar
        avatarId={avatarId}
        name={avatarName}
        size={size}
        shape="circle"
      />
    );
  }
  const emoji = sectionMenuEmoji(meta);
  return (
    <span
      className={styles.railGlyph}
      style={{ "--glyph": `${size}px` } as React.CSSProperties}
      aria-hidden
    >
      {emoji ? (
        <span className={styles.sectionEmoji} style={{ fontFamily: EMOJI_FONT }}>
          {emoji}
        </span>
      ) : (
        meta.icon
      )}
    </span>
  );
}

// Cycle 84: theme/dark-mode class application moved to App.tsx
// (`applyAppearance`, variant-keyed) so it's a single authority that
// reacts to the settings context (incl. live `settings_update`
// broadcasts). This screen now just PATCHes `display_dark_mode` /
// `display_theme` and lets that effect repaint.

// Cycle 207: Expand all / Collapse all controller for the Android-tablet
// Settings pane. Every collapsible card registers a `setOpen`/`isOpen`
// pair, so the pane's single button drives them all through the SAME
// per-card state + persistence as an individual header tap. `anyOpen` powers
// the button label. Cheap + inert when no cards register (the non-tablet
// surfaces never mount the controller).
function useCollapseAllController(): {
  controller: CollapseAllController;
  anyOpen: boolean;
  setAll: (open: boolean) => void;
} {
  const cardsRef = useRef<Set<CollapseAllRegistration>>(new Set());
  const [anyOpen, setAnyOpen] = useState(true);
  const recompute = useCallback(() => {
    let open = false;
    cardsRef.current.forEach((c) => {
      if (c.isOpen()) open = true;
    });
    setAnyOpen(open);
  }, []);
  const controller = useMemo<CollapseAllController>(
    () => ({
      register: (reg) => {
        cardsRef.current.add(reg);
        recompute();
        return () => {
          cardsRef.current.delete(reg);
          recompute();
        };
      },
      notifyChange: recompute,
    }),
    [recompute],
  );
  const setAll = useCallback((open: boolean) => {
    cardsRef.current.forEach((c) => c.setOpen(open));
    setAnyOpen(open);
  }, []);
  return { controller, anyOpen, setAll };
}

export function SettingsScreen({
  auth,
  activeMember,
  tts,
  variant,
  episode,
  episodeRecoveredAt,
  socket,
  now,
  clockMode,
  alarms,
}: Props) {
  const { confirm } = useConfirm();
  // Lazy-loaded only when this screen mounts — saves a `/api/push/*`
  // round-trip on every Now / History / Log render.
  const push = usePushSubscriptions(auth.status === "signedIn");
  // Cycle 28: load native-push tokens too. Hook short-circuits to an
  // unsupported state on non-Capacitor surfaces, so the round-trip
  // only fires for mobile-app sessions.
  const nativePush = useNativePush(
    auth.status === "signedIn" && isCapacitorNative(),
  );
  // Cycle 56: kiosk-local Wi-Fi agent. `available` is false on every
  // non-kiosk surface and on a kiosk whose agent is unreachable or
  // built without `nmcli`, so the Network subsection renders nothing.
  const wifi = useWifiAgent(variant);
  // Cycle 247 — native Wi-Fi signal/SSID readout (Android-tablet kiosk APK
  // only, via the cycle-235.4 WifiSignal plugin; inert everywhere else).
  // Drives the "Wi-Fi" tile status line + the Wi-Fi tab readout.
  const wifiSignal = useWifiSignal();
  // Cycle 100 — kiosk voice agent. `available` is false on every non-kiosk
  // surface; on the local kiosk it surfaces the agent's actual wake-word
  // options so the device row can prefer them + flag a local mismatch.
  const voice = useVoiceAgent(variant);
  // Cycle 315 — Alarm Reliability (Android standard shell only; inert
  // everywhere else). Drives the mobile index row, its live subtitle, and
  // the checklist pane; re-reads on app resume.
  const reliability = useAlarmReliability();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [section, setSection] = useState<SectionId>(() =>
    isTabletKioskSurface(variant)
      ? resolveTabletSection(params.get("section"))
      : variant === "mobile"
        ? resolveMobileSection(params.get("section"))
        : resolveSectionParam(params.get("section")),
  );
  // Cycle 29a: drill-down mode for the mobile variant (and any
  // non-kiosk surface narrower than 760 px). We track viewport width
  // here rather than leaning on useFrameVariant because that hook
  // only flips between `web` and `mobile` at 600 px — settings wants
  // the drill-down to kick in earlier so split-screen / tablet
  // portrait windows get a usable single-column flow too.
  const [windowWidth, setWindowWidth] = useState<number>(() =>
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const drilldownMode =
    variant !== "kiosk" &&
    (variant === "mobile" || windowWidth <= MOBILE_BREAKPOINT_PX);
  // Cycle 207: the Android-tablet kiosk surface — the same gate cycles
  // 199–203 used (kiosk variant AND `isAndroidTabletClassRuntime()`). The
  // Pi kiosk + iPad kiosk are the `kiosk` variant WITHOUT it. Drives the
  // emoji/accent theming, the collapse mount, and the Expand/Collapse-all
  // control — all behind this one flag.
  const tabletKiosk = variant === "kiosk" && isAndroidTabletClassRuntime();
  // Cycle 224: the Android-tablet kiosk uses the reorganized section menu
  // (Reminders→Alerts, CGM&devices→Account, +Wi-Fi, emoji/avatar); every
  // other variant (incl. the Pi kiosk) keeps the universal SECTION_META.
  const menuSections = tabletKiosk ? TABLET_SECTION_META : SECTION_META;
  const collapseAll = useCollapseAllController();
  const sectionParam = params.get("section");
  const hasSectionParam = sectionParam != null;
  // Cycle 115: mobile landscape renders the section index as a 3-column
  // card grid (vs the portrait row list) with a slim glance pill. The
  // glance computation lives below `settingsCtx` (it reads units).
  const isLandscape = useIsLandscape();
  const mobileLandscape = variant === "mobile" && isLandscape;
  const glanceThresholds = useThresholds();
  // Detail-pane lifecycle. `mobileDetailPresent` keeps the detail in
  // the DOM long enough for the exit transition to run; `…Open`
  // toggles the `translateX(0)` class. Cold mount with a `?section=`
  // deep link skips the entry animation so push-notification taps
  // land on the detail without a flash of the index.
  const [mobileDetailPresent, setMobileDetailPresent] = useState<boolean>(
    () => drilldownMode && hasSectionParam,
  );
  const [mobileDetailOpen, setMobileDetailOpen] = useState<boolean>(
    () => drilldownMode && hasSectionParam,
  );
  const initialMountRef = useRef(true);
  // Settings come from the app-wide context now (cycle 14) so
  // `units` / thresholds propagate to every screen
  // without a prop chain.
  const settingsCtx = useSettings();
  const settings = settingsCtx.settings;
  // Cycle 115: landscape glance-pill values (active member's reading via
  // the top-level socket mirror; reuses the hero/staleness plumbing).
  // Cycle 433: the glance pill shows the live number, so it follows the
  // same display rule — a value the client cannot prove is current renders
  // with the pill's existing stale treatment. DISPLAY ONLY.
  // ⚠ Cycle 435: through the ONE selector (`lib/currentGlucose.ts`), which
  // samples `Date.now()` for itself — never the ticking `now` prop. The
  // same result feeds the mobile hero card below, which until this cycle
  // had no freshness gate at all.
  const glanceView = selectCurrentGlucoseFor(socket, socket?.activeMemberId ?? null);
  const glanceCurrent = glanceView.displayValue;
  const glanceZone: Zone = glanceCurrent
    ? zoneOf(glanceCurrent.sgv, glanceThresholds)
    : "unknown";
  const glanceAge = glanceView.ageMinutes ?? 0;
  const glancePill =
    mobileLandscape && socket ? (
      <GlancePill
        current={glanceCurrent}
        units={settingsCtx.units}
        ageMin={glanceAge}
        zone={glanceZone}
        provider={
          socket?.dashboardCards.find((c) => c.member_id === socket.activeMemberId)
            ?.cgm_provider ?? null
        }
      />
    ) : null;
  const [devices, setDevices] = useState<Device[]>([]);
  const [cgmStatus, setCgmStatus] = useState<cgmApi.CGMStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Cycle 234 — "Connect to CGM" modal (active member) + a transient
  // success toast. The modal is mounted alongside `connectionSectionEl`
  // below, so it remounts fresh on each open (no carried-over provider).
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [cgmToast, setCgmToast] = useState<
    { msg: string; tone: "success" | "warning" } | null
  >(null);
  const cgmToastTimer = useRef<number | null>(null);
  // Success toasts auto-dismiss; a warning (e.g. "no sensor shared") PERSISTS
  // until tapped so the setup instruction isn't missed.
  const showCgmToast = useCallback(
    (msg: string, tone: "success" | "warning" = "success") => {
      setCgmToast({ msg, tone });
      if (cgmToastTimer.current) window.clearTimeout(cgmToastTimer.current);
      if (tone === "success") {
        cgmToastTimer.current = window.setTimeout(() => setCgmToast(null), 3200);
      }
    },
    [],
  );
  useEffect(
    () => () => {
      if (cgmToastTimer.current) window.clearTimeout(cgmToastTimer.current);
    },
    [],
  );
  // Re-pull `GET /api/cgm/status` so the connection card reflects the new
  // state immediately after a connect / disconnect — no page reload.
  const reloadCgmStatus = useCallback(async () => {
    const r = await cgmApi.status();
    if (r.ok) setCgmStatus(r.data);
  }, []);
  // Cycle 84 — uploaded background images (account-level). Loaded when
  // the screen mounts; the Theme section manages upload/select/delete.
  const [backgrounds, setBackgrounds] = useState<Background[]>([]);
  const [bgError, setBgError] = useState<string | null>(null);
  // Cycle 110 — reminders count for the section status line. One fetch
  // when the screen mounts; `null` until it resolves (rendered as a muted
  // "—"), `reminderError` on failure (a static fallback subtitle). This is
  // best-effort decoration — it never blocks the screen or adds a spinner.
  const [reminderCount, setReminderCount] = useState<number | null>(null);
  const [reminderError, setReminderError] = useState(false);
  // Cycle 247 — emergency-contact count for the tablet "Contacts" tile.
  // Same best-effort one-fetch pattern as the reminder count above.
  const [contactCount, setContactCount] = useState<number | null>(null);
  const [contactError, setContactError] = useState(false);
  const reloadBackgrounds = useCallback(async () => {
    const r = await backgroundsApi.list();
    if (r.ok) setBackgrounds(r.data);
  }, []);

  // Initial fetch — settings is owned by the context provider; only
  // devices and CGM status need fetching here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [d, c] = await Promise.all([devicesApi.list(), cgmApi.status()]);
      if (cancelled) return;
      if (d.ok) setDevices(d.data.devices);
      if (c.ok) setCgmStatus(c.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMember.active?.member_id]);

  // Cycle 305 — a connect/disconnect on another of the owner's devices fans
  // out a `member_update` frame; App re-broadcasts it as a window event
  // (the cycle-217 cross-boundary pattern) because this pane owns a local
  // `GET /api/cgm/status` snapshot with no WS wiring. Refetch on it so the
  // connection card flips to "not connected" (or the new provider) in step
  // with the other devices, no reload.
  useEffect(() => {
    const onCgmChanged = () => void reloadCgmStatus();
    window.addEventListener("sugalarm:cgm-changed", onCgmChanged);
    return () =>
      window.removeEventListener("sugalarm:cgm-changed", onCgmChanged);
  }, [reloadCgmStatus]);

  // Cycle 90 — backgrounds are per-member, so refetch whenever the active
  // member changes: the grid must show the right person's images and the
  // "max 5" cap must reflect that member's count. ThemeBackground already
  // keys off the settings context (the active member's
  // display_background_id), so it repaints with no change beyond this.
  useEffect(() => {
    void reloadBackgrounds();
  }, [reloadBackgrounds, activeMember.active?.member_id]);

  // Cycle 110 — one-shot reminders count for the status line. No retry
  // loop; a failure simply falls back to a static subtitle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await remindersApi.list();
      if (cancelled) return;
      if (r.ok) setReminderCount(r.data.length);
      else setReminderError(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cycle 247 — one-shot emergency-contact count for the "Contacts" tile
  // status line (same best-effort pattern as the reminder count). Refetched
  // when the active member changes so the count tracks the viewed person.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await emergencyContactsApi.list();
      if (cancelled) return;
      if (r.ok) setContactCount(r.data.contacts.length);
      else setContactError(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMember.active?.member_id]);

  // Rewrite a legacy / relocated `?section=…` so the bookmark / share link
  // cleans itself up the first time someone opens it. Cycle 224: on the
  // Android tablet, also self-heal the relocated ids (reminders→alerts,
  // cgm/devices/connection→account, network→wifi) to the tablet home.
  // Cycle 308: on mobile, self-heal the merged/relocated ids
  // (appearance/theme→display, devices→account).
  useEffect(() => {
    const raw = params.get("section");
    if (raw == null) return;
    const target = isTabletKioskSurface(variant)
      ? resolveTabletSection(raw)
      : variant === "mobile"
        ? resolveMobileSection(raw)
        : raw in LEGACY_SECTION_MAP
          ? LEGACY_SECTION_MAP[raw]!
          : null;
    if (target != null && target !== raw) {
      const nextParams = new URLSearchParams(params);
      nextParams.set("section", target);
      setParams(nextParams, { replace: true });
    }
    // Only run on mount — re-triggering on every params change would
    // fight users who navigate forward into a legacy URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadDevices = useCallback(async () => {
    const r = await devicesApi.list();
    if (r.ok) setDevices(r.data.devices);
  }, []);

  // Cycle 173: kiosk-only Settings cards lock unless this account has a
  // LIVE paired kiosk. Derived from the already-loaded devices list (no
  // extra fetch); before devices load, `devices` is empty → false →
  // fail-safe LOCKED. On the kiosk variant itself the session is by
  // definition paired, so its panes pass `true` directly (below).
  const hasPairedKiosk = useMemo(
    () => selectHasPairedKiosk(devices),
    [devices],
  );

  const setSectionAndUrl = useCallback(
    (next: SectionId) => {
      setSection(next);
      const nextParams = new URLSearchParams(params);
      nextParams.set("section", next);
      // Cycle 161: the Export Data deep-link's `focus` rider never
      // survives a normal section navigation.
      nextParams.delete("focus");
      // Mobile drill-down: push a real history entry so the OS / browser
      // back button closes the detail before leaving the route. Desktop
      // / kiosk: replace, so rapid rail clicks don't clog the back stack.
      setParams(nextParams, drilldownMode ? undefined : { replace: true });
    },
    [params, setParams, drilldownMode],
  );

  // Cycle 161: the mobile index's "Export Data" row — opens the existing
  // account pane with a `focus=export` rider that scrolls to / briefly
  // rings the pane's existing Export card. Same navigation mechanics as
  // setSectionAndUrl; nothing moves out of the pane.
  const openAccountExport = useCallback(() => {
    setSection("account");
    const nextParams = new URLSearchParams(params);
    nextParams.set("section", "account");
    nextParams.set("focus", "export");
    setParams(nextParams, drilldownMode ? undefined : { replace: true });
  }, [params, setParams, drilldownMode]);

  // Clear the `?section=` param via replace — returns the mobile drill to
  // its index and the kiosk detail to its tile grid, keeping the back
  // stack clean (a browser back from the index leaves the route entirely).
  const clearSection = useCallback(() => {
    const nextParams = new URLSearchParams(params);
    nextParams.delete("section");
    nextParams.delete("focus");
    setParams(nextParams, { replace: true });
  }, [params, setParams]);

  // Drive the slide animation off URL changes. Initial mount with a
  // section param already set skips the entry transition so a cold
  // deep-link landing doesn't flash the index.
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    if (!drilldownMode) return;
    if (hasSectionParam) {
      setMobileDetailPresent(true);
      const raf = requestAnimationFrame(() => setMobileDetailOpen(true));
      return () => cancelAnimationFrame(raf);
    }
    setMobileDetailOpen(false);
    const t = window.setTimeout(() => setMobileDetailPresent(false), 240);
    return () => window.clearTimeout(t);
  }, [hasSectionParam, drilldownMode]);

  const patch = useCallback(
    async (delta: UserSettingsPatch) => {
      setError(null);
      const r = await settingsApi.patch(delta);
      if (!r.ok) {
        setError(r.error.message);
        // Re-pull truth so the UI doesn't show a value the server rejected.
        void settingsCtx.refresh();
        return;
      }
      // Refresh the context so other screens (Now, dashboard, etc.)
      // pick up the new value immediately.
      void settingsCtx.refresh();
    },
    [settingsCtx],
  );

  // Cycle 84 — background upload / delete. Select/clear is a plain
  // settings PATCH (display_background_id), handled inline in the
  // section. Upload errors (409/413/415) surface as inline copy.
  const onUploadBackground = useCallback(
    async (
      blob: Blob,
      width: number,
      height: number,
      filename?: string,
    ) => {
      setBgError(null);
      const r = await backgroundsApi.upload(blob, width, height, filename);
      if (!r.ok) {
        const code =
          typeof r.error.message === "string" ? r.error.message : "";
        setBgError(
          code === "too_many_backgrounds"
            ? "Maximum of 5 background images. Delete one to add another."
            : code === "image_too_large"
              ? // Cycle 87: the upload is the cropped JPEG, so a 413 means
                // the framed region is still too big — zoom in / pick a
                // tighter area.
                "That image is still too large after cropping. Zoom in to a smaller area and try again."
              : code === "unsupported_image_type" || code === "empty_image"
                ? "Unsupported image. Use a JPEG, PNG, or WebP."
                : "Upload failed. Please try again.",
        );
        return;
      }
      await reloadBackgrounds();
    },
    [reloadBackgrounds],
  );

  const onDeleteBackground = useCallback(
    async (id: string) => {
      setBgError(null);
      const r = await backgroundsApi.remove(id);
      if (!r.ok) {
        setBgError("Could not delete that image. Please try again.");
        return;
      }
      // The server clears display_background_id if this was selected and
      // broadcasts settings_update; refresh the context so this screen's
      // selection ring updates too.
      await reloadBackgrounds();
      void settingsCtx.refresh();
    },
    [reloadBackgrounds, settingsCtx],
  );

  // Cycle 110 — shared inputs for the per-section live status lines, and
  // the active member's avatar (shown in the Account icon slot). Both are
  // best-effort decoration; any missing piece degrades gracefully.
  const statusInputs: StatusInputs = {
    settings,
    cgmStatus,
    memberCount: activeMember.members.length,
    reminderCount,
    reminderError,
    contactCount,
    contactError,
    wifi: wifiSignal,
    variant,
  };
  const accountAvatarId = activeMember.active?.avatar_id ?? null;
  const accountAvatarName =
    activeMember.active?.display_name ??
    auth.user?.display_name ??
    "Account";

  // Cycle 224: on the Android tablet the Reminders card (A3) folds into the
  // Alerts pane and the CGM-connection + Devices cards (A4) fold into the
  // Account pane. Build each once here and mount it where the IA needs it;
  // they're only attached when a branch/pane below references them (every
  // other variant keeps them as their own top-level sections). On the tablet,
  // ConnectionSection's Pi-loopback Network card is already hidden
  // (`wifi.available` is false — no loopback agent), so it renders CGM +
  // Devices only, exactly A4's order.
  const remindersEl = (
    <RemindersSection h24={settings?.display_24h ?? false} variant={variant} />
  );
  // Cycle 308: the Devices handlers, hoisted out of `connectionEl` so the
  // mobile Account pane's relocated Devices block reuses the EXACT same
  // callbacks (pair / forget / prune / wake-word) — one owner, no fork.
  const offersCode = auth.isDeviceSession || variant === "kiosk";
  const handlePairDevice = useCallback(
    () => nav(offersCode ? "/pair?relink=1" : "/pair-approve"),
    [nav, offersCode],
  );
  const handleSetWakeWord = useCallback(
    async (deviceId: string, key: string | null) => {
      const r = await devicesApi.patchDevice(deviceId, { wake_word: key });
      if (!r.ok) setError(r.error.message);
      else void reloadDevices();
    },
    [reloadDevices],
  );
  // Cycle 377: inline device rename, mirroring the cycle-300 member
  // rename. Display-only — `devices.name` is not read by auth, routing,
  // member resolution, or the kiosk-vs-mobile push split — and the device
  // token is untouched, so renaming never un-pairs a kiosk. Resolves to
  // whether it stuck, so the row keeps its editor open on failure instead
  // of silently discarding what was typed.
  const handleRenameDevice = useCallback(
    async (deviceId: string, name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed) return false;
      const r = await devicesApi.patchDevice(deviceId, { name: trimmed });
      if (!r.ok) {
        setError(r.error.message);
        return false;
      }
      void reloadDevices();
      return true;
    },
    [reloadDevices],
  );
  const handleForgetDevice = useCallback(
    async (d: Device) => {
      // Cycle 471 — ON A DEVICE SESSION, FORGET-OF-SELF IS LOG OUT. A paired
      // kiosk runs on its `dt_` token: `DELETE /api/devices/{id}` is JWT-only
      // since this cycle (operator decision 5: a kiosk could delete a sibling
      // kiosk's live row and darken it), and the list hides Forget on every
      // row but this screen's. Signing out is what revokes this row's token
      // and releases its push rows server-side (`teardownAccount` →
      // `POST /api/devices/self/revoke_token`), after which the daily sweep
      // removes the row. Any other row on a device session is a no-op.
      if (auth.isDeviceSession) {
        if (d.id !== getDeviceId()) return;
        if (
          !(await confirm({
            message: `Forget "${d.name}"? This screen will be signed out and must be paired again.`,
            destructive: true,
          }))
        )
          return;
        await auth.signOut();
        return;
      }
      if (
        !(await confirm({
          message: `Forget "${d.name}"? It will be signed out and removed.`,
          destructive: true,
        }))
      )
        return;
      if (d.has_token) {
        await devicesApi.revokeToken(d.id);
      }
      const r = await devicesApi.deleteDevice(d.id);
      if (!r.ok) setError(r.error.message);
      else void reloadDevices();
    },
    [confirm, auth, reloadDevices],
  );
  const handlePruneInactive = useCallback(async () => {
    if (
      !(await confirm({
        message:
          "Remove revoked and inactive devices? Devices with a live token or recent activity are kept.",
        destructive: true,
      }))
    )
      return;
    const r = await devicesApi.pruneInactive();
    if (!r.ok) setError(r.error.message);
    else void reloadDevices();
  }, [confirm, reloadDevices]);

  // Cycle 245: parameterized so the tablet can split this composite into a
  // CGM-only "Sensor" tab and a Devices-only block in Account. Called with
  // no arg elsewhere (web/Pi) → renders all cards as before. Cycle 308: the
  // mobile variant splits the same way (Sensor = CGM only, Devices moved to
  // Account) and renders the CGM actions as full-width rows.
  const connectionEl = (only?: "cgm" | "devices") => (
    <>
    <ConnectionSection
      only={only}
      actionRows={variant === "mobile"}
      cgmStatus={cgmStatus}
      activeMemberName={
        activeMember.active?.display_name ??
        auth.user?.display_name ??
        "this account"
      }
      onDisconnect={async () => {
        if (!(await confirm({ message: "Disconnect the CGM?", destructive: true }))) return;
        const r = await cgmApi.disconnect();
        if (!r.ok) setError(r.error.message);
        else {
          // Cycle 234: re-fetch CGM status so the card flips to the
          // not-connected state in place (no `nav("/login")` dead-end),
          // and refresh the active member so the Now screen drops the
          // stale stream. The not-connected "Connect to CGM" button then
          // re-opens the picker for a fresh link / provider switch.
          await reloadCgmStatus();
          await auth.refreshUser();
          void activeMember.refresh();
        }
      }}
      // Cycle 234: open the ProviderPicker → CGMConnectForm modal instead
      // of bouncing an authenticated session to `/login`. Drives both the
      // not-connected "Connect to CGM" and the connected "Reconnect"
      // (provider-switch) buttons in CGMSection.
      onReconnect={() => setConnectModalOpen(true)}
      devices={devices}
      isDeviceSession={auth.isDeviceSession}
      offersCode={offersCode}
      onPairDevice={handlePairDevice}
      wifi={wifi}
      voice={voice}
      onSetWakeWord={handleSetWakeWord}
            onRenameDevice={handleRenameDevice}
      onForget={handleForgetDevice}
      onPruneInactive={handlePruneInactive}
    />
    {/* Cycle 354: declare-ahead sensor-pause — pause the "sensor dark"
        blackout pages BEFORE pulling a sensor (or while away), from any
        surface incl. the kiosk. Renders under the CGM card; hidden on the
        devices-only variant. */}
    {only !== "devices" ? (
      <SensorPausePrompt
        memberId={activeMember.active?.member_id ?? null}
        memberName={activeMember.active?.display_name ?? ""}
        dark={false}
        mode="settings"
        h24={settingsCtx.settings?.display_24h ?? false}
        now={now ?? Date.now()}
        variant={variant}
      />
    ) : null}
    {/* Cycle 234: the connect-to-CGM modal lives with the connection card
        (mounted only while CGM & Connection / the tablet Account pane is
        shown — exactly when the Connect / Reconnect buttons are reachable).
        Both are fixed-position overlays, so the fragment placement is
        purely about lifecycle + state ownership. */}
    {connectModalOpen ? (
      <CgmConnectModal
        variant={variant}
        onClose={() => setConnectModalOpen(false)}
        onConnected={async (result) => {
          // Deliverable 4: await the status re-fetch BEFORE the toast.
          await reloadCgmStatus();
          setConnectModalOpen(false);
          // Cycle 234.6: if the link succeeded but needs setup (e.g. Libre
          // with no sensor shared), show the server's warning copy as a
          // PERSISTENT warning toast instead of the success toast.
          if (result.warning) {
            showCgmToast(result.warning, "warning");
          } else {
            const name = result.provider
              ? result.provider[0]!.toUpperCase() + result.provider.slice(1)
              : "CGM";
            showCgmToast(`Connected to ${name}.`);
          }
          // Refresh the active member so the Now screen picks up the
          // new stream.
          void activeMember.refresh();
        }}
      />
    ) : null}
    {cgmToast ? (
      <div
        role={cgmToast.tone === "warning" ? "alert" : "status"}
        aria-live={cgmToast.tone === "warning" ? "assertive" : "polite"}
        onClick={() => setCgmToast(null)}
        style={{
          position: "fixed",
          left: "50%",
          bottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
          transform: "translateX(-50%)",
          zIndex: 80,
          maxWidth: "calc(100vw - 32px)",
          width: cgmToast.tone === "warning" ? "min(420px, calc(100vw - 32px))" : undefined,
          background: "var(--bg-elev)",
          color: "var(--ink)",
          border:
            cgmToast.tone === "warning"
              ? "0.5px solid var(--zone-warn)"
              : "0.5px solid var(--line-strong)",
          borderRadius: 12,
          padding: cgmToast.tone === "warning" ? "12px 16px" : "10px 16px",
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.4)",
          fontSize: 14,
          fontWeight: cgmToast.tone === "warning" ? 500 : 600,
          lineHeight: cgmToast.tone === "warning" ? 1.45 : undefined,
          cursor: "pointer",
        }}
      >
        {cgmToast.tone === "warning" ? (
          <>
            <span style={{ fontWeight: 700, color: "var(--zone-warn)" }}>
              ⚠ Almost there —{" "}
            </span>
            {cgmToast.msg}
            <span style={{ display: "block", marginTop: 6, fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>
              Tap to dismiss
            </span>
          </>
        ) : (
          cgmToast.msg
        )}
      </div>
    ) : null}
    </>
  );

  // Cycle 308: the mobile variant mirrors the cycle-245 tablet merges —
  // Display+Theme in one pane, Sensor = CGM only, Devices in Account.
  const mobileVariant = variant === "mobile";

  // Cycle 315: inside the Android standard shell, the ALARMS & SAFETY group
  // gains an "Alarm Reliability" row right under Alarms. Derived (never a
  // mutation of the static config) so iOS / mobile web / old APKs render
  // the cycle-308 four groups byte-identically.
  const mobileIndexGroupsV2 = reliability.available
    ? MOBILE_INDEX_GROUPS_V2.map((g) =>
        g.label === "Alarms & safety"
          ? {
              ...g,
              rows: g.rows.flatMap((r) =>
                r.id === "alerts"
                  ? [
                      r,
                      {
                        id: "alarm_reliability" as SectionId,
                        title: "Alarm Reliability",
                        tile: "var(--settile-reliability)",
                      },
                    ]
                  : [r],
              ),
            }
          : g,
      )
    : MOBILE_INDEX_GROUPS_V2;

  const sectionContent = !settings ? (
    <div style={{ color: "var(--ink-3)" }}>Loading…</div>
  ) : section === "display" ? (
    // Cycle 245: on the tablet, "Display" merges the former Display + Theme
    // tabs — appearance cards (theme / background) render right after the
    // display cards. Cycle 308: the mobile variant merges the same way.
    // Other variants keep the separate "Theme" tab.
    <>
      <DisplaySection
        settings={settings}
        onPatch={patch}
        variant={variant}
        clockMode={clockMode}
      />
      {tabletKiosk || mobileVariant ? (
        <AppearanceSection
          settings={settings}
          onPatch={patch}
          variant={variant}
          backgrounds={backgrounds}
          atMax={backgrounds.length >= 5}
          bgError={bgError}
          onUpload={onUploadBackground}
          onDelete={onDeleteBackground}
        />
      ) : null}
    </>
  ) : section === "appearance" ? (
    <AppearanceSection
      settings={settings}
      onPatch={patch}
      variant={variant}
      backgrounds={backgrounds}
      atMax={
        backgrounds.length >= 5
      }
      bgError={bgError}
      onUpload={onUploadBackground}
      onDelete={onDeleteBackground}
    />
  ) : section === "alerts" ? (
    <AlertsSection
      settings={settings}
      onPatch={patch}
      push={push}
      nativePush={nativePush}
      nav={nav}
      activeMember={activeMember.fullMember}
      selfUserId={auth.user?.id ?? null}
      selfDisplayName={auth.user?.display_name ?? null}
      episode={episode}
      episodeRecoveredAt={episodeRecoveredAt}
      variant={variant}
      alarms={alarms}
      // Cycle 245: Reminders + Emergency contacts each have their own tablet
      // tab now, so the Alerts pane no longer folds them in. Cycle 308: the
      // mobile variant gets its own Contacts index row too, so its Alarms
      // pane drops the card the same way. (Web keeps it in Alerts.)
      showEmergencyContacts={!tabletKiosk && !mobileVariant}
    />
  ) : section === "voice" ? (
    // Cycle 224 (A2): one consolidated Voice & TTS card on the tablet.
    <>
      <VoiceSection
        settings={settings}
        onPatch={patch}
        tts={tts}
        variant={variant}
        memberName={activeMember.active?.display_name ?? null}
        // Cycle 435: resolved at CLICK time through the selector, so the
        // sample speaks the number on screen right now — or says it is not
        // available when the selector would withhold it.
        currentReading={() => {
          const v = selectCurrentGlucoseFor(socket, socket?.activeMemberId ?? null);
          return v.status === "current" ? v.reading : null;
        }}
        consolidated={tabletKiosk}
        alarmLive={episode?.isLive === true}
      />
      {/* Cycle 249: native-only Siri App-Group bridge probe (renders null
          off-native). Lives under Voice so the operator can localize the
          "sign in first" intent failure on-device. */}
      <SiriBridgeDiagnostics />
    </>
  ) : section === "reminders" ? (
    remindersEl
  ) : section === "members" ? (
    <MembersSection
      activeMemberId={activeMember.active?.member_id ?? null}
      refreshActive={activeMember.refresh}
      currentUserId={auth.user?.id ?? null}
      variant={variant}
    />
  ) : section === "alarm_reliability" ? (
    // Cycle 315: Android-mobile-only urgent-alarm setup checklist (the
    // resolver guarantees this section id only ever resolves inside the
    // Android standard shell carrying the native plugin).
    <AlarmReliabilitySection reliability={reliability} />
  ) : section === "wifi" ? (
    // Cycle 224 (A5): tablet-only Wi-Fi tab — status + no-PIN "Change Wi-Fi
    // network" launching the cycle-223 native connector on demand.
    <WifiTabSection />
  ) : section === "connection" ? (
    // Cycle 245: on the tablet this tab is "Sensor" — CGM only (Devices
    // moved to Account). Cycle 308: the mobile variant splits identically.
    // Every other variant shows the full CGM & devices.
    tabletKiosk || mobileVariant ? connectionEl("cgm") : connectionEl()
  ) : section === "contacts" ? (
    // Cycle 245: Emergency contacts as its own tablet tab.
    // Cycle 388: renamed "Heroes"; the subtitle keeps the old name so the
    // card still reads as the emergency-contact list.
    <SubsectionCard
      title="Heroes"
      subtitle="Your emergency contacts — texted + called on low and urgent low alerts."
      icon={Icon.bell}
      tone="warn"
    >
      <EmergencyContactsSection
        variant={variant}
        selfUserId={auth.user?.id ?? null}
        selfDisplayName={auth.user?.display_name ?? null}
      />
    </SubsectionCard>
  ) : section === "account" ? (
    <AccountSection
      auth={auth}
      variant={variant}
      // Cycle 245: on the Android tablet, the paired-Devices card folds into
      // Account ABOVE Export/Account (CGM moved to its own "Sensor" tab).
      leadingCards={tabletKiosk ? connectionEl("devices") : undefined}
      // Cycle 308: the mobile variant recomposes this pane as three
      // row-based cards (Account / Devices / Danger Zone). The Devices
      // block content is built HERE so it shares the exact hoisted
      // handlers `connectionEl` uses — moved, not forked.
      mobileRows={mobileVariant}
      // Cycle 471 (regression guard, rounds 1 and 2): the phone's account
      // pane counts ACTIVE rows too, like the Devices header, and is gated
      // on the total so an all-inactive household still sees "0 devices".
      deviceCount={countActiveDevices(devices)}
      deviceTotal={devices.length}
      devicesBody={
        mobileVariant ? (
          <DevicesSection
            devices={devices}
            isDeviceSession={auth.isDeviceSession}
            offersCode={offersCode}
            onPairDevice={handlePairDevice}
            onForget={handleForgetDevice}
            onPruneInactive={handlePruneInactive}
            voice={voice}
            onSetWakeWord={handleSetWakeWord}
            onRenameDevice={handleRenameDevice}
          />
        ) : undefined
      }
      focusExport={params.get("focus") === "export"}
      onExportError={(m) => setError(m)}
      onReset={async () => {
        if (
          !(await confirm({
            message:
              "Reset all settings to defaults? CGM credentials and device tokens are not affected.",
            confirmLabel: "Reset",
          }))
        )
          return;
        const r = await settingsApi.reset();
        if (!r.ok) setError(r.error.message);
        else void settingsCtx.refresh();
      }}
    />
  ) : null;

  if (drilldownMode) {
    // Cycle 308: the mobile variant titles with the kiosk-aligned names
    // (Alarms / Sensor / Contacts / Display); narrow-web keeps SECTION_META.
    const detailTitle =
      (mobileVariant ? MOBILE_SECTION_TITLES[section] : undefined) ??
      SECTION_META.find((s) => s.id === section)?.title ??
      TABLET_SECTION_META.find((s) => s.id === section)?.title ??
      "";
    // Cycle 165: glass Settings on the MOBILE variant only — the
    // drill-down also serves narrow web windows, which keep the solid
    // grounds + solid cards. The provider flips every SubsectionCard in
    // the panes to its glass surface; the aurora modifier drops the
    // solid root/detail grounds; the covered class fades the index out
    // beneath the now-transparent open detail.
    const mobileGlass = variant === "mobile";
    return (
      <SettingsCardGlassContext.Provider value={mobileGlass}>
      <div
        className={`${styles.mobileRoot} ${
          mobileLandscape ? styles.mobileRootLandscape : ""
        } ${mobileGlass ? styles.mobileRootAurora : ""}`}
      >
        <div
          className={`${styles.mobileIndex} ${
            mobileLandscape ? styles.mobileIndexLandscape : ""
          } ${
            mobileGlass && mobileDetailOpen ? styles.mobileIndexCovered : ""
          }`}
        >
          {/* Cycle 115: landscape header — title left, slim glance pill
              right; portrait keeps the bare title. */}
          <div className={styles.lsIndexHead}>
            <h2 className={styles.railTitle}>Settings</h2>
            {glancePill}
          </div>
          {mobileLandscape ? (
            // Landscape: a 3-column card grid (icon tile + title + status
            // + badge), same navigation targets as the portrait rows.
            <div className={styles.lsCardGrid}>
              {SECTION_META.map((s) => {
                const status = sectionStatus(s.id, statusInputs);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      hapticSelect();
                      setSectionAndUrl(s.id);
                    }}
                    className={styles.lsCard}
                  >
                    <span className={styles.lsCardChipRow}>
                      <SectionChip
                        meta={s}
                        chipSize={34}
                        glyphSize={17}
                        avatarId={accountAvatarId}
                        avatarName={accountAvatarName}
                      />
                      {s.id === "connection" && cgmStatus?.connected ? (
                        <span
                          className={`${styles.connDot} ${styles.connDotOk}`}
                          aria-hidden
                        />
                      ) : s.id === "alerts" && settings?.alerts_enabled ? (
                        <span className={styles.onPill}>On</span>
                      ) : null}
                    </span>
                    <span className={styles.lsCardTitle}>{s.title}</span>
                    <span className={styles.lsCardStatus}>{status ?? "—"}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            // Cycle 161: the portrait mobile index — hero card on top,
            // then the three labeled grouped cards (tinted tiles, live
            // sectionStatus subtitles, status accessories). Same
            // navigation targets and `?section=` mechanics as the
            // cycle-110 flat row list this replaces; the landscape grid
            // above and the kiosk/web indexes are untouched.
            // Cycle 308: the MOBILE variant renders the redesigned v2
            // index (kiosk-aligned four-group taxonomy, solid color
            // tiles, glass + iridescent cards); the ≤760px narrow-web
            // drill-down below keeps the cycle-161 index byte-identical.
            <>
              <SettingsHeroCard
                view={glanceView}
                recentReadings={socket?.recentReadings ?? []}
                units={settingsCtx.units}
                memberName={accountAvatarName}
                avatarId={accountAvatarId}
                provider={cgmStatus?.provider ?? null}
                connected={!!cgmStatus?.connected}
                onEditAvatar={() => setSectionAndUrl("members")}
                iri={mobileVariant}
              />
              {mobileVariant
                ? mobileIndexGroupsV2.map((group) => {
                    const tileRound = mobileTileRound();
                    return (
                      <div key={group.label}>
                        <div className={mi.eyebrow}>{group.label}</div>
                        <div className={`${mi.groupCard} ${mi.groupIri}`}>
                          {group.rows.map((row) => {
                            // Live subtitles reused verbatim from
                            // sectionStatus; the Account row shows the
                            // signed-in email (the pane's live status);
                            // the cycle-315 Alarm Reliability row shows
                            // its live unmet count.
                            const subtitle =
                              row.id === "account"
                                ? (auth.user?.email ??
                                  sectionStatus(row.id, statusInputs) ??
                                  "—")
                                : row.id === "alarm_reliability"
                                  ? reliability.unmetCount == null
                                    ? "Urgent alarm setup"
                                    : reliability.unmetCount > 0
                                      ? `${reliability.unmetCount} to set up`
                                      : "All protections set"
                                  : (sectionStatus(row.id, statusInputs) ??
                                    "—");
                            const statusRight =
                              row.id === "connection" && cgmStatus?.connected
                                ? "Connected"
                                : row.id === "alerts" &&
                                    settings?.alerts_enabled
                                  ? "On"
                                  : row.id === "alarm_reliability" &&
                                      reliability.unmetCount === 0
                                    ? "Ready"
                                    : null;
                            return (
                              <button
                                key={row.id}
                                type="button"
                                className={`${mi.row} ${mi.rowV2}`}
                                onClick={() => {
                                  hapticSelect();
                                  setSectionAndUrl(row.id);
                                }}
                              >
                                <span
                                  className={`${mi.tileSolid} ${
                                    tileRound ? mi.tileRound : ""
                                  }`}
                                  style={
                                    {
                                      "--tile-accent": row.tile,
                                    } as React.CSSProperties
                                  }
                                  aria-hidden
                                >
                                  {mobileSectionIcon(row.id)}
                                </span>
                                <span className={mi.rowText}>
                                  <span className={mi.rowTitle}>
                                    {row.title}
                                  </span>
                                  {/* Cycle 388: sub-label leads the status
                                      line (see the kiosk tile) so a renamed
                                      row keeps the list's one-height rhythm
                                      instead of standing 15px taller. */}
                                  <span className={mi.rowSub}>
                                    {row.subLabel ? (
                                      <span className={mi.rowSubLabel}>
                                        {row.subLabel}
                                      </span>
                                    ) : null}
                                    {subtitle}
                                  </span>
                                </span>
                                {statusRight ? (
                                  <span
                                    className={`${mi.rowStatus} ${mi.rowStatusV2}`}
                                  >
                                    <span
                                      className={mi.statusDot}
                                      aria-hidden
                                    />
                                    {statusRight}
                                  </span>
                                ) : null}
                                <span className={mi.chevron} aria-hidden>
                                  ›
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                : MOBILE_INDEX_GROUPS.map((group) => (
                <div key={group.label}>
                  <div className={mi.eyebrow}>{group.label}</div>
                  <div className={mi.groupCard}>
                    {group.rows.map((row) => {
                      let title: string;
                      let subtitle: string;
                      let icon: React.ReactNode;
                      if (row.kind === "section") {
                        const meta = SECTION_META.find(
                          (s) => s.id === row.id,
                        )!;
                        title = meta.title;
                        subtitle = sectionStatus(row.id, statusInputs) ?? "—";
                        icon = meta.icon;
                      } else {
                        title = row.title;
                        subtitle = row.subtitle;
                        icon = row.icon;
                      }
                      const statusRight =
                        row.kind === "section" &&
                        row.id === "connection" &&
                        cgmStatus?.connected
                          ? "Connected"
                          : row.kind === "section" &&
                              row.id === "alerts" &&
                              settings?.alerts_enabled
                            ? "On"
                            : null;
                      const destructive =
                        row.kind === "action" && row.destructive === true;
                      const onTap = () => {
                        hapticSelect();
                        if (row.kind === "section") {
                          setSectionAndUrl(row.id);
                        } else if (row.key === "profile") {
                          setSectionAndUrl("account");
                        } else if (row.key === "export") {
                          openAccountExport();
                        } else {
                          // Log Out — the same handler the account
                          // pane's existing button calls (no new logic).
                          void auth.signOut();
                        }
                      };
                      return (
                        <button
                          key={row.kind === "section" ? row.id : row.key}
                          type="button"
                          className={mi.row}
                          onClick={onTap}
                        >
                          <span
                            className={mi.tile}
                            style={
                              {
                                "--tile-accent": row.accent,
                              } as React.CSSProperties
                            }
                            aria-hidden
                          >
                            {icon}
                          </span>
                          <span className={mi.rowText}>
                            <span
                              className={`${mi.rowTitle} ${
                                destructive ? mi.rowTitleDestructive : ""
                              }`}
                            >
                              {title}
                            </span>
                            <span className={mi.rowSub}>{subtitle}</span>
                          </span>
                          {statusRight ? (
                            <span className={mi.rowStatus}>{statusRight}</span>
                          ) : null}
                          {!destructive ? (
                            <span className={mi.chevron} aria-hidden>
                              ›
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        {mobileDetailPresent ? (
          <div
            className={`${styles.mobileDetail} ${
              mobileDetailOpen ? styles.mobileDetailOpen : ""
            }`}
            // Settle the unmount on the real transition end so a slow
            // device doesn't get a hard-cut, with the 240ms timer
            // above as a belt-and-suspenders fallback.
            onTransitionEnd={(e) => {
              if (
                e.propertyName === "transform" &&
                !mobileDetailOpen &&
                !hasSectionParam
              ) {
                setMobileDetailPresent(false);
              }
            }}
          >
            <div className={styles.mobileDetailHeader}>
              {/* Cycle 162: the shared glass BackButton; `clearSection`
                  keeps its exact semantics (replace, `?section=` clear,
                  the slide-out transition). Cycle 169: a COMPACT pill on
                  mobile (the full-size one was crowding the header); the
                  narrow-web drill-down keeps the standard size. */}
              <BackButton
                label="Settings"
                compact={variant === "mobile"}
                onBack={clearSection}
              />
              <span className={styles.mobileDetailTitle}>{detailTitle}</span>
            </div>
            <div className={styles.mobileDetailBody}>
              {error && <div className={styles.error}>{error}</div>}
              {/* Cycle 172/173: collapsible detail-pane cards (mobile) +
                  kiosk-only-card gating from the live paired-kiosk state. */}
              <SettingsKioskContext.Provider value={hasPairedKiosk}>
                <SettingsCollapseContext.Provider value={section}>
                  {sectionContent}
                </SettingsCollapseContext.Provider>
              </SettingsKioskContext.Provider>
            </div>
          </div>
        ) : null}
      </div>
      </SettingsCardGlassContext.Provider>
    );
  }

  // ─── Cycle 110: kiosk hybrid — tile grid (index) ↔ icon rail + pane.
  //     The index renders iff `?section=` is absent (the same rule mobile
  //     uses); a cold mount with the param already set lands on the detail
  //     directly. `LEGACY_SECTION_MAP` resolution + the one-shot URL
  //     self-rewrite continue to work exactly as before. ─────────────────
  if (variant === "kiosk") {
    if (!hasSectionParam) {
      return (
        <div className={styles.kioskIndex}>
          <h2 className={styles.kioskIndexTitle}>Settings</h2>
          <div
            className={`${styles.tileGrid} ${
              tabletKiosk ? styles.tileGridTablet : ""
            }`}
          >
            {menuSections.map((s) => {
              const status = sectionStatus(s.id, statusInputs);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSectionAndUrl(s.id)}
                  className={styles.tile}
                >
                  <span className={styles.tileChipRow}>
                    <SectionChip
                      meta={s}
                      chipSize={44}
                      glyphSize={20}
                      avatarId={accountAvatarId}
                      avatarName={accountAvatarName}
                    />
                    {s.id === "connection" && cgmStatus?.connected ? (
                      <span
                        className={`${styles.connDot} ${styles.connDotOk}`}
                        aria-hidden
                      />
                    ) : null}
                  </span>
                  <span className={styles.tileTitle}>{s.title}</span>
                  {/* Cycle 388: the sub-label leads the EXISTING status line
                      rather than adding a third one — `.tile` is a fixed
                      132px grid row with `overflow: hidden`, so a third line
                      silently clips the status off the bottom (it does not
                      register as overflow, which is exactly how it slipped
                      past a scrollHeight check during review). */}
                  <span className={styles.tileStatus}>
                    {s.subLabel ? (
                      <span className={styles.tileSubLabel}>{s.subLabel}</span>
                    ) : null}
                    {status ?? "—"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    return (
      <div className={styles.kioskDetail}>
        {/* Cycle 209: the section nav is a horizontal top bar (was a 128px
            left rail) so the pane spans the full width below it. Same 8
            SECTION_META items / icons / order / active soft-pill tint;
            relocation only. */}
        <div className={styles.sectionTopBar}>
          {menuSections.map((s) => {
            const active = section === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setSectionAndUrl(s.id)}
                className={`${styles.railIconItem} ${
                  active ? RAIL_TONE_CLASS[s.tone] : ""
                }`}
                aria-current={active ? "page" : undefined}
              >
                <SectionRailGlyph
                  meta={s}
                  size={21}
                  avatarId={accountAvatarId}
                  avatarName={accountAvatarName}
                />
                <span className={styles.railIconLabel}>{s.title}</span>
                {s.subLabel ? (
                  <span className={styles.railIconSubLabel}>{s.subLabel}</span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div className={styles.kioskPane}>
          {/* Cycle 207: Expand all / Collapse all — Android tablet ONLY. A
              single header-row button toggling every card in the pane; its
              label reflects the current state. Pi/iPad kiosk never render
              it (the row is absent), so their pane layout is byte-identical. */}
          {tabletKiosk ? (
            <div className={styles.tabletPaneHeader}>
              <button
                type="button"
                className={styles.expandAllBtn}
                onClick={() => collapseAll.setAll(!collapseAll.anyOpen)}
              >
                {collapseAll.anyOpen ? "Collapse all" : "Expand all"}
              </button>
            </div>
          ) : null}
          {error && <div className={styles.error}>{error}</div>}
          {/* Cycle 173: a kiosk session is by definition paired, so kiosk-
              only cards are always unlocked here.
              Cycle 207: the Android tablet kiosk pane ALSO mounts the
              collapse context (collapsible cards, default expanded) + the
              tablet theme context (emoji + per-card accent) + the
              Expand/Collapse-all controller. The Pi/iPad kiosk leave all
              three null so their fixed layout stays byte-identical (no
              collapse, no emoji, non-interactive headers). */}
          <SettingsKioskContext.Provider value={true}>
            {tabletKiosk ? (
              <SettingsTabletThemeContext.Provider value={section}>
                <SettingsCollapseAllContext.Provider
                  value={collapseAll.controller}
                >
                  <SettingsCollapseContext.Provider value={section}>
                    {sectionContent}
                  </SettingsCollapseContext.Provider>
                </SettingsCollapseAllContext.Provider>
              </SettingsTabletThemeContext.Provider>
            ) : (
              sectionContent
            )}
          </SettingsKioskContext.Provider>
        </div>
      </div>
    );
  }

  // ─── Web desktop: the unchanged 180px two-column layout. Cycle 110 adds
  //     the section icon to the left of each rail label. ─────────────────
  return (
    <div className={styles.root}>
      <div className={styles.rail}>
        <h2 className={styles.railTitle}>Settings</h2>
        {SECTION_META.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSectionAndUrl(s.id)}
            className={`${styles.railItem} ${
              section === s.id ? styles.railItemActive : ""
            }`}
          >
            <SectionRailGlyph
              meta={s}
              size={18}
              avatarId={accountAvatarId}
              avatarName={accountAvatarName}
            />
            <span className={styles.railItemLabel}>{s.title}</span>
          </button>
        ))}
      </div>
      <div className={styles.pane}>
        {error && <div className={styles.error}>{error}</div>}
        {/* Cycle 172/173: collapsible detail-pane cards (web) + kiosk-only
            gating from the live paired-kiosk state. */}
        <SettingsKioskContext.Provider value={hasPairedKiosk}>
          <SettingsCollapseContext.Provider value={section}>
            {sectionContent}
          </SettingsCollapseContext.Provider>
        </SettingsKioskContext.Provider>
      </div>
    </div>
  );
}

// ─── Section primitives ────────────────────────────────────────────────

// Cycle 308 — full-width tappable action row (mobile Sensor + Account
// panes): small solid color tile (--settile-* via --tile-accent,
// platform-shaped like the index tiles), label, optional muted inline
// note, right chevron (rotates when the row expands inline content).
// Destructive rows read urgent-red. Rendered ONLY from mobile-gated
// paths — kiosk/web/narrow-web keep their existing button clusters.
function MobileActionRow({
  icon,
  tile,
  label,
  note,
  red = false,
  disabled = false,
  expanded,
  title,
  onClick,
}: {
  icon: React.ReactNode;
  tile: string;
  label: string;
  note?: string;
  red?: boolean;
  disabled?: boolean;
  /** When the row toggles inline content, its chevron rotates open. */
  expanded?: boolean;
  title?: string;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.mRow} ${red ? styles.mRowRed : ""}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-expanded={expanded}
    >
      <span
        className={`${styles.mRowMini} ${
          mobileTileRound() ? styles.mRowMiniRound : ""
        }`}
        style={{ "--tile-accent": tile } as React.CSSProperties}
        aria-hidden
      >
        {icon}
      </span>
      <span className={styles.mRowLabel}>
        {label}
        {/* Cycle 452.1: a row hint is shown whole or not at all (the Log
            tab rule) — at the 13 px floor it no longer fits a narrow row. */}
        {note ? (
          <FitOrHideHint style={{ color: "var(--ink-3)", fontWeight: 500, fontSize: 13 }}>
            {` · ${note}`}
          </FitOrHideHint>
        ) : null}
      </span>
      <span
        className={`${styles.mRowChevron} ${
          expanded ? styles.mRowChevronOpen : ""
        }`}
        aria-hidden
      >
        ›
      </span>
    </button>
  );
}

function Header({ title, sub }: { title: string; sub?: string }) {
  return (
    <div>
      <h3 className={styles.headerTitle}>{title}</h3>
      {sub ? <div className={styles.headerSub}>{sub}</div> : null}
    </div>
  );
}

export function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{children}</span>
    </div>
  );
}

export function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`${styles.toggle} ${on ? styles.toggleOn : ""}`}
    >
      <span className={styles.toggleKnob} />
    </button>
  );
}

function Seg<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (next: T) => void;
}) {
  return (
    <span className={styles.seg}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`${styles.segItem} ${
            value === o.id ? styles.segItemActive : ""
          }`}
        >
          {o.label}
        </button>
      ))}
    </span>
  );
}

// ─── Display ───────────────────────────────────────────────────────────

function DisplaySection({
  settings,
  onPatch,
  variant,
  clockMode,
}: {
  settings: UserSettings;
  onPatch: (p: UserSettingsPatch) => void;
  variant: FrameVariant;
  clockMode?: ClockMode;
}) {
  return (
    <>
    <SubsectionCard
      title="Display"
      subtitle="Units and clock format."
      icon={Icon.display}
      tone="neutral"
    >
      <Row label="Units">
        <Seg<"mg/dL" | "mmol/L">
          value={settings.units}
          options={[
            { id: "mg/dL", label: "mg/dL" },
            { id: "mmol/L", label: "mmol/L" },
          ]}
          onChange={(next) => onPatch({ units: next })}
        />
      </Row>
      <Row label="24-hour time">
        <Toggle
          on={settings.display_24h}
          onClick={() => onPatch({ display_24h: !settings.display_24h })}
        />
      </Row>
      {/* Cycle 84: Light/Dark + theme + backgrounds moved to the new
          "Theme" section. */}
    </SubsectionCard>
    {/* Cycle 300: the kiosk-nav bottom-vs-side control was removed — the
        kiosk always uses the side rail now. */}
    {/* Cycle 197: tablet-local screen brightness + overnight auto-dim.
        Rendered ONLY where the native plugin exists — the Android kiosk
        APK — so it's absent on web, the Pi kiosk, iPhone, the phone
        layout, and the `standard` flavor. Stored on the tablet, never
        synced (no /api/settings PATCH). */}
    {screenBrightness.isAvailable() ? <BrightnessCard /> : null}
    {/* Cycle 282: "Keep screen on" — pins the tablet display so it never
        sleeps (fixes units going black). Rendered ONLY where the native
        KeepAwake plugin exists — the Android kiosk APK — so it's absent on
        web, the Pi kiosk, iPhone, the phone layout, and the `standard`
        flavor. Stored on the tablet, never synced. */}
    {keepAwake.isAvailable() ? <KeepAwakeCard /> : null}
    <IobRingColorsCard settings={settings} variant={variant} />
    {/* Cycle 145: alarm-clock-mode card — Start now + nightly schedule.
        The schedule is the additive `alarm_clock` settings key; a stale
        cached settings blob predating the key falls back to the default
        shape so the card always renders. */}
    <AlarmClockModeSection
      schedule={
        settings.alarm_clock ?? {
          enabled: false,
          start: "22:00",
          end: "07:00",
          days: [true, true, true, true, true, true, true],
          show_iob_rings: true,
        }
      }
      h24={settings.display_24h}
      variant={variant}
      onPatch={onPatch}
      onStartNow={(durationMs) =>
        clockMode?.enter("manual", { durationMs })
      }
    />
    </>
  );
}

// ─── Cycle 197: tablet-local screen brightness + overnight auto-dim ───
// Android-kiosk-APK only (the card is gated on screenBrightness.isAvailable()
// at its mount site). All values write through the native plugin into the
// tablet's SharedPreferences — never the account, never /api/settings.

const BRIGHTNESS_FLOOR_PCT = Math.round(screenBrightness.MIN_LEGIBLE * 100); // 15

/** A never-configured tablet should still show sensible values while the
 *  first getConfig() resolves. Mirrors the native defaults. */
const BRIGHTNESS_FALLBACK: screenBrightness.ScreenBrightnessConfig = {
  dayLevel: 1.0,
  autoDim: false,
  fromMinutes: 22 * 60,
  untilMinutes: 7 * 60,
  dimLevel: 0.25,
};

function levelToPct(level: number): number {
  return Math.round(Math.max(screenBrightness.MIN_LEGIBLE, Math.min(1, level)) * 100);
}
function pctToLevel(pct: number): number {
  return Math.max(screenBrightness.MIN_LEGIBLE, Math.min(1, pct / 100));
}
function minutesToHHMM(m: number): string {
  const h = Math.floor(m / 60) % 24;
  const mm = ((m % 60) + 60) % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}
function hhmmToMinutes(s: string): number {
  const parts = s.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return (((h * 60 + m) % 1440) + 1440) % 1440;
}

/** A 15%-floored brightness slider. The locked 0–15% sub-floor zone is
 *  shown to the left of the live thumb (min=15) so the floor is visible,
 *  per the approved mockup — the thumb can't enter it. */
function BrightnessSlider({
  pct,
  onChange,
  disabled,
  ariaLabel,
}: {
  pct: number;
  onChange: (pct: number) => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <div className={styles.brightSliderRow}>
      <span
        className={styles.brightLockedZone}
        aria-hidden="true"
        title="Locked — keeps the screen legible"
      />
      <input
        type="range"
        className={styles.brightSlider}
        min={BRIGHTNESS_FLOOR_PCT}
        max={100}
        step={1}
        value={pct}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className={styles.brightPct}>{pct}%</span>
    </div>
  );
}

function BrightnessCard() {
  const [cfg, setCfg] = useState<screenBrightness.ScreenBrightnessConfig | null>(
    null,
  );

  useEffect(() => {
    let alive = true;
    void screenBrightness.getConfig().then((c) => {
      if (alive) setCfg(c ?? BRIGHTNESS_FALLBACK);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Optimistic local update + tablet-local write. No /api/settings PATCH,
  // no WS broadcast — brightness is device hardware state.
  const push = (next: screenBrightness.ScreenBrightnessConfig) => {
    setCfg(next);
    void screenBrightness.setConfig(next);
  };

  const c = cfg ?? BRIGHTNESS_FALLBACK;

  return (
    <SubsectionCard
      title="Brightness"
      subtitle="Screen brightness + overnight auto-dim (this tablet only)."
      icon={Icon.display}
      tone="neutral"
    >
      <Row label="Daytime brightness">
        <BrightnessSlider
          pct={levelToPct(c.dayLevel)}
          ariaLabel="Daytime brightness"
          onChange={(pct) => push({ ...c, dayLevel: pctToLevel(pct) })}
        />
      </Row>
      <Row label="Overnight auto-dim">
        <Toggle
          on={c.autoDim}
          onClick={() => push({ ...c, autoDim: !c.autoDim })}
        />
      </Row>
      <Row label="From">
        <input
          type="time"
          className={styles.timeField}
          value={minutesToHHMM(c.fromMinutes)}
          disabled={!c.autoDim}
          onChange={(e) =>
            push({ ...c, fromMinutes: hhmmToMinutes(e.target.value) })
          }
        />
      </Row>
      <Row label="Until">
        <input
          type="time"
          className={styles.timeField}
          value={minutesToHHMM(c.untilMinutes)}
          disabled={!c.autoDim}
          onChange={(e) =>
            push({ ...c, untilMinutes: hhmmToMinutes(e.target.value) })
          }
        />
      </Row>
      <Row label="Dim to">
        <BrightnessSlider
          pct={levelToPct(c.dimLevel)}
          ariaLabel="Overnight dim level"
          disabled={!c.autoDim}
          onChange={(pct) => push({ ...c, dimLevel: pctToLevel(pct) })}
        />
      </Row>
      <p className={styles.rowHint}>
        Stored on this tablet only — won't dim below {BRIGHTNESS_FLOOR_PCT}% so
        the screen stays readable.
      </p>
    </SubsectionCard>
  );
}

// ─── Cycle 282: keep-screen-always-on ────────────────────────────────
// Android-kiosk-APK only (the card is gated on keepAwake.isAvailable() at its
// mount site). The choice writes through the native plugin into the tablet's
// SharedPreferences — never the account, never /api/settings. When ON the
// plugin pins the OS screen-off timeout to "never" + stay-on-while-plugged
// (device owner) on top of FLAG_KEEP_SCREEN_ON. Default ON.

function KeepAwakeCard() {
  const [on, setOn] = useState<boolean>(true);

  useEffect(() => {
    let alive = true;
    void keepAwake.getConfig().then((c) => {
      if (alive && c != null) setOn(c.keepAwake);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Optimistic local update + tablet-local write. No /api/settings PATCH,
  // no WS broadcast — this is device hardware state.
  const toggle = () => {
    const next = !on;
    setOn(next);
    void keepAwake.setConfig({ keepAwake: next });
  };

  return (
    <SubsectionCard
      title="Keep screen on"
      subtitle="Stop this tablet from sleeping or going dark."
      icon={Icon.display}
      tone="neutral"
    >
      <Row label="Keep screen always on">
        <Toggle on={on} onClick={toggle} />
      </Row>
      <p className={styles.rowHint}>
        Stored on this tablet only. When on, the screen never sleeps — a
        glucose display should always stay lit.
      </p>
    </SubsectionCard>
  );
}

// ─── Cycle 118: IOB ring colors ──────────────────────────────────────
// Let the household recolor the two IOB rings. The two settings drive the
// two theme-invariant `--iob-ring-*` tokens (cycle 104d) through the
// app-root applier; every IOB color surface already reads those tokens,
// so this card just edits the settings — no call-site threading.

const IOB_MEAL_DEFAULT = "#355e3b";
const IOB_CORRECTION_DEFAULT = "#ffd60a";
// Cycle 147: the third (long-acting/basal) ring stock default.
const IOB_BASAL_DEFAULT = "#8b7cf6";
// 8 vivid swatches — the two stock defaults plus six legible options.
// Tuned for legibility against all six themes + photo backgrounds; not a
// wire contract. Keeping the stock defaults in the set means the reset
// state shows a selected swatch in each picker.
const IOB_SWATCHES = [
  "#355e3b", // hunter green (stock meal)
  "#ffd60a", // yellow (stock correction)
  "#8b7cf6", // violet (stock long-acting, cycle 147)
  "#e24b4a", // red
  "#f0871e", // orange
  "#378add", // blue
  "#2bbfae", // teal
  "#9b6dff", // purple
  "#ed6da4", // pink
] as const;

const HEX6_RE = /^#[0-9a-f]{6}$/;

/** A fixed synthetic treatment set for the live preview: a meal bolus
 *  (2.5u ~90m ago, paired to a carbs row → green meal ring) and a later
 *  correction bolus (1.0u ~30m ago, no carbs → yellow correction ring).
 *  The 60-minute gap keeps the correction OUTSIDE the carbs row's 30-min
 *  `MEAL_PAIR_WINDOW_MS` so `isCorrectionBolus` classifies it as a real
 *  correction (the inclusive window boundary at exactly 30m would
 *  otherwise pair it to the meal's carbs and color BOTH rings green, so
 *  the correction swatch would appear to do nothing). Anchored to a `now`
 *  frozen at mount so the ring arcs stay stable while the in-ring
 *  countdown ticks naturally. */
function buildPreviewTreatments(now: number): Treatment[] {
  const base = {
    member_id: "preview",
    user_id: "preview",
    entered_by: null,
    provenance: { source: "manual" } as Record<string, unknown>,
    created_at: new Date(now).toISOString(),
  };
  const mealAt = now - 90 * 60 * 1000; // 90m ago
  const correctionAt = now - 30 * 60 * 1000; // 30m ago
  const basalAt = now - 6 * 60 * 60 * 1000; // 6h ago (cycle 147)
  return [
    {
      ...base,
      id: "preview-carbs",
      event_type: "carbs",
      timestamp: new Date(mealAt).toISOString(),
      carbs_g: 30,
      insulin_u: null,
      notes: "Sandwich",
    },
    {
      ...base,
      id: "preview-meal",
      event_type: "insulin",
      timestamp: new Date(mealAt).toISOString(),
      carbs_g: null,
      insulin_u: 2.5,
      notes: null,
    },
    {
      ...base,
      id: "preview-correction",
      event_type: "insulin",
      timestamp: new Date(correctionAt).toISOString(),
      carbs_g: null,
      insulin_u: 1.0,
      notes: null,
    },
    // Cycle 147: a synthetic long-acting dose so the preview renders all
    // THREE rings + the Long-acting line. Excluded from the rapid rings
    // by the unchanged ring-exclusion invariant (insulin_kind='basal').
    {
      ...base,
      id: "preview-basal",
      event_type: "insulin",
      timestamp: new Date(basalAt).toISOString(),
      carbs_g: null,
      insulin_u: 18,
      notes: null,
      insulin_kind: "basal",
      insulin_duration_min: 1440,
      insulin_label: "Lantus",
    },
  ];
}

function IobRingColorsCard({
  settings,
  variant,
}: {
  settings: UserSettings;
  variant: FrameVariant;
}) {
  const { applyRemoteUpdate, refresh } = useSettings();
  const [error, setError] = useState<string | null>(null);

  // Effective (showing) colors: a null setting resolves to the stock
  // token default. These drive both the preview override and the
  // selected/dimmed swatch states.
  const effMeal = settings.iob_meal_color ?? IOB_MEAL_DEFAULT;
  const effCorrection = settings.iob_correction_color ?? IOB_CORRECTION_DEFAULT;
  // Cycle 147: the third (long-acting/basal) ring.
  const effBasal = settings.iob_basal_color ?? IOB_BASAL_DEFAULT;

  // Frozen `now` for the preview so the arcs don't jump every render.
  const previewNow = useMemo(() => Date.now(), []);
  const previewTreatments = useMemo(
    () => buildPreviewTreatments(previewNow),
    [previewNow],
  );
  const previewBlocks = useMemo(
    () => mealForContext(previewTreatments, previewNow),
    [previewTreatments, previewNow],
  );
  const previewBoluses = useMemo<BolusForRings[]>(() => {
    const base = activeBolusesForRings(previewTreatments, previewNow);
    return base.map((b) => {
      const row = previewTreatments.find((t) => t.id === b.id);
      const correction = row
        ? isCorrectionBolus(row, previewTreatments, previewNow)
        : false;
      return {
        ...b,
        color: correction ? CORRECTION_BOLUS_COLOR : MEAL_BOLUS_COLOR,
      };
    });
  }, [previewTreatments, previewNow]);

  // Optimistic patch: apply locally first (so the global applier re-runs
  // and the live Now-screen rings on this device recolor without a
  // reload), then PATCH; revert via a server refresh on failure.
  const patchColors = useCallback(
    async (delta: UserSettingsPatch) => {
      setError(null);
      applyRemoteUpdate({ ...settings, ...delta } as UserSettings);
      const r = await settingsApi.patch(delta);
      if (!r.ok) {
        setError(r.error.message);
        void refresh();
        return;
      }
      applyRemoteUpdate(r.data);
    },
    [settings, applyRemoteUpdate, refresh],
  );

  const reset = useCallback(() => {
    void patchColors({
      iob_meal_color: null,
      iob_correction_color: null,
      iob_basal_color: null,
    });
  }, [patchColors]);

  // Cycle 147: the synthetic basal for the three-ring preview.
  const previewBasal = useMemo(
    () => basalForContext(previewTreatments, previewNow),
    [previewTreatments, previewNow],
  );

  return (
    <SubsectionCard
      title="IOB ring colors"
      subtitle="Applies to the rings, dose dots, and timers on every device."
      icon={Icon.display}
      tone="neutral"
    >
      <div className={styles.iobColorBody}>
        {/* Live preview — the real <BolusRings>, recolored locally from
            the current selection via inline custom properties. */}
        <div
          className={styles.iobColorPreview}
          style={
            // Cycle 118b: same field→token mapping the app-root applier
            // uses (iobRingColorVars), so the preview can't invert it.
            // effMeal/effCorrection are never null (defaults applied), so
            // both tokens are set — isolating the preview from the
            // document-root override.
            iobRingColorVars({
              meal: effMeal,
              correction: effCorrection,
              basal: effBasal,
            }) as React.CSSProperties
          }
        >
          {/* Cycle 452.1: on phones the preview stacks the rings above the
              lines (the Now card's phone-portrait layout, cycle 122.1) — side
              by side, the 13 px lines ran past the card edge. */}
          <BolusRings
            boluses={previewBoluses}
            now={previewNow}
            basal={previewBasal}
            stack={variant === "mobile"}
          >
            <MealContextLines
              blocks={previewBlocks}
              h24={settings.display_24h}
              variant={variant}
              basal={previewBasal}
            />
          </BolusRings>
          <span className={styles.iobColorPreviewLabel}>Live preview</span>
        </div>

        <div className={styles.iobColorControls}>
          <RingColorPicker
            label="Bolus"
            current={effMeal}
            otherColors={[effCorrection, effBasal]}
            onPick={(hex) => void patchColors({ iob_meal_color: hex })}
          />
          <RingColorPicker
            label="Correction bolus"
            current={effCorrection}
            otherColors={[effMeal, effBasal]}
            onPick={(hex) => void patchColors({ iob_correction_color: hex })}
          />
          <RingColorPicker
            label="Long-acting"
            current={effBasal}
            otherColors={[effMeal, effCorrection]}
            onPick={(hex) => void patchColors({ iob_basal_color: hex })}
          />
          {error ? <div className={styles.error}>{error}</div> : null}
          <Button
            variant="ghost"
            onClick={reset}
            className={styles.iobColorReset}
          >
            Reset to default
          </Button>
        </div>
      </div>
    </SubsectionCard>
  );
}

function RingColorPicker({
  label,
  current,
  otherColors,
  onPick,
}: {
  label: string;
  /** The ring's effective color (stock default when unset). */
  current: string;
  /** Cycle 147: the OTHER rings' effective colors (two of them) — their
   *  swatches are dimmed + no two rings can share a color. */
  otherColors: string[];
  /** PATCHes this ring to a normalized #rrggbb hex. */
  onPick: (hex: string) => void;
}) {
  const [hexText, setHexText] = useState(current);
  const [hexError, setHexError] = useState<string | null>(null);
  // Re-sync the hex field whenever the saved color changes (incl. reset).
  useEffect(() => {
    setHexText(current);
    setHexError(null);
  }, [current]);

  const selectedInPalette = IOB_SWATCHES.some((s) => s === current);
  const others = otherColors.map((c) => c.toLowerCase());

  const commitHex = () => {
    const next = hexText.trim().toLowerCase();
    if (next === current) {
      setHexText(current);
      setHexError(null);
      return;
    }
    if (!HEX6_RE.test(next)) {
      setHexError("Enter a 6-digit hex, e.g. #e24b4a");
      return;
    }
    if (others.includes(next)) {
      setHexError("Can't match another ring");
      return;
    }
    setHexError(null);
    onPick(next);
  };

  return (
    <div>
      <div className={styles.iobColorPickerLabel}>{label}</div>
      <div className={styles.iobSwatchRow}>
        {IOB_SWATCHES.map((hex) => {
          const isOther = others.includes(hex.toLowerCase());
          const isSelected =
            !isOther && hex.toLowerCase() === current.toLowerCase();
          return (
            <button
              key={hex}
              type="button"
              aria-label={hex}
              aria-pressed={isSelected}
              disabled={isOther}
              className={`${styles.iobSwatch} ${
                isSelected ? styles.iobSwatchSelected : ""
              } ${isOther ? styles.iobSwatchDimmed : ""}`}
              style={{ background: hex }}
              onClick={() => {
                if (isOther) return;
                setHexError(null);
                onPick(hex);
              }}
            >
              {isSelected ? <span className={styles.iobSwatchCheck}>✓</span> : null}
            </button>
          );
        })}
      </div>
      <div className={styles.iobHexRow}>
        <input
          className={`${inputClass} ${styles.iobHexInput}`}
          value={hexText}
          spellCheck={false}
          autoCapitalize="none"
          autoCorrect="off"
          aria-label={`${label} custom hex`}
          placeholder="#rrggbb"
          onChange={(e) => setHexText(e.target.value)}
          onBlur={commitHex}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitHex();
            }
          }}
        />
        {!selectedInPalette && !hexError ? (
          <span className={styles.subtleHint}>Custom</span>
        ) : null}
      </div>
      {hexError ? <div className={styles.error}>{hexError}</div> : null}
    </div>
  );
}

// ─── Theme (cycle 84) ────────────────────────────────────────────────
//
// Mobile: Light/Dark (display_dark_mode). Web + kiosk: pick one of 6
// themes (display_theme) + an optional uploaded background image
// (display_background_id). The swatch ground/accent literals mirror
// tokens.css; the zone trio is identical across all themes by design
// (alarm semantics never desaturate — a glucose monitor must keep
// urgent red unmistakable).

const THEME_META: {
  id: DisplayTheme;
  label: string;
  ground: string;
  accent: string;
}[] = [
  {
    id: "midnight",
    label: "Midnight",
    ground: "linear-gradient(160deg,#0a1628,#163461)",
    accent: "#5b9dff",
  },
  {
    id: "deep-sea",
    label: "Deep Sea",
    ground: "linear-gradient(160deg,#0a1a22,#123845)",
    accent: "#5fc8d6",
  },
  {
    id: "evergreen",
    label: "Evergreen",
    ground: "linear-gradient(160deg,#0b1810,#163a28)",
    accent: "#7fc99a",
  },
  {
    id: "twilight",
    label: "Twilight",
    ground: "linear-gradient(160deg,#120f22,#261d44)",
    accent: "#a99cf0",
  },
  {
    id: "slate",
    label: "Slate",
    ground: "linear-gradient(160deg,#12161d,#222b38)",
    accent: "#8fb3d9",
  },
  {
    id: "lagoon",
    label: "Lagoon",
    ground: "linear-gradient(160deg,#081c20,#0f3a3a)",
    accent: "#6fd6b8",
  },
];

// Salient zone trio (urgent / warn / ok) — same in every theme.
const ZONE_TRIO = ["#ff6b6b", "#f5b942", "#6dd4a3"];

function ThemeSwatch({
  meta,
  selected,
  onSelect,
}: {
  meta: (typeof THEME_META)[number];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`${styles.themeSwatch} ${
        selected ? styles.themeSwatchSelected : ""
      }`}
    >
      <span
        className={styles.themeSwatchPreview}
        style={{ background: meta.ground }}
      >
        <span
          className={styles.themeSwatchAccent}
          style={{ background: meta.accent }}
        />
        <span className={styles.themeSwatchZones}>
          {ZONE_TRIO.map((c) => (
            <span
              key={c}
              className={styles.themeSwatchZone}
              style={{ background: c }}
            />
          ))}
        </span>
        {selected ? <span className={styles.themeSwatchCheck}>✓</span> : null}
      </span>
      <span className={styles.themeSwatchLabel}>{meta.label}</span>
    </button>
  );
}

function BackgroundThumb({
  bg,
  selected,
  onSelect,
  onDelete,
}: {
  bg: Background;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    let obj: string | null = null;
    void backgroundsApi.fetchImageObjectUrl(bg.id).then((u) => {
      if (cancelled) {
        if (u) URL.revokeObjectURL(u);
        return;
      }
      obj = u;
      setUrl(u);
    });
    return () => {
      cancelled = true;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [bg.id]);
  return (
    <div
      className={`${styles.bgTile} ${selected ? styles.bgTileSelected : ""}`}
    >
      <button
        type="button"
        className={styles.bgThumbSelect}
        style={url ? { backgroundImage: `url(${url})` } : undefined}
        onClick={onSelect}
        aria-label="Use this background"
      />
      <button
        type="button"
        className={styles.bgThumbDelete}
        onClick={onDelete}
        aria-label="Delete background"
      >
        ×
      </button>
    </div>
  );
}

// Cycle 87: HEIC → JPEG for the cropper lives in `lib/croppableImage.ts`
// (shared since cycle 457).

// Cycle 171: the mobile app-background picker — grouped Calm / Scenic /
// Adventure preview chips (the cycle-84 theme-swatch UX). Each chip sets
// its own `data-bg` so the `[data-bg]` token blocks resolve for the
// preview (Light/Dark per the dark root ancestor); scene chips overlay a
// simplified ridge. Member-supplied text is never involved — every label
// is a built-in constant. Tap → PATCH display_mobile_background → the
// applyAppearance effect repaints the live MobileBackground layer.
function MobileBgChip({
  preset,
  selected,
  unlocked,
  thumbSrc,
  onPick,
}: {
  preset: (typeof BG_PRESETS)[number];
  selected: boolean;
  unlocked: boolean;
  // Cycle 5: image presets render this lazy thumb instead of a gradient chip.
  thumbSrc?: string;
  onPick: () => void;
}) {
  // Cycle 189: a runtime-uploaded image preset isn't in the static catalog,
  // so treat "has a resolved thumb" as image-kind too.
  const imageKind = isImageBg(preset.id) || !!thumbSrc;
  return (
    <button
      type="button"
      className={`${styles.mbgChip} ${selected ? styles.mbgChipSelected : ""}`}
      aria-pressed={selected}
      onClick={() => {
        hapticSelect();
        onPick();
      }}
    >
      <span
        className={`${styles.mbgChipPreview} ${imageKind ? styles.mbgChipImage : ""}`}
        data-bg={imageKind || preset.id === "aurora" ? undefined : preset.id}
        data-aurora={preset.id === "aurora" ? "1" : undefined}
      >
        {/* Cycle 5: image preset — lazy thumb, or a neutral placeholder until
            the pack art is uploaded (thumbSrc absent). */}
        {imageKind && thumbSrc ? (
          <img className={styles.mbgChipThumb} src={thumbSrc} loading="lazy" alt="" />
        ) : null}
        {isSceneBg(preset.id) ? (
          <span className={styles.mbgChipRidge} aria-hidden />
        ) : null}
        {selected && unlocked ? (
          <span className={styles.mbgChipCheck} aria-hidden>
            ✓
          </span>
        ) : null}
        {!unlocked ? (
          <span className={styles.mbgChipLock} aria-hidden>
            <svg width="13" height="13" viewBox="0 0 14 14">
              <rect x="3" y="6.2" width="8" height="5.5" rx="1.2" fill="currentColor" />
              <path
                d="M4.6 6.2 V4.7 a2.4 2.4 0 0 1 4.8 0 V6.2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          </span>
        ) : null}
      </span>
      <span className={styles.mbgChipName}>{preset.name}</span>
    </button>
  );
}

// Cycle 174: the picker groups the FREE catalog by family (Calm / Scenic /
// Adventure) and each PREMIUM pack as its own section with a lock + an
// "Unlock — {price}" CTA. Selection of a locked preset is blocked client-
// side (and 422 `pack_required` server-side); tapping a locked chip or the
// CTA runs the purchasePack seam (a dev simulate-purchase this cycle;
// "Coming soon" in production). Restore re-fetches ownership.
function MobileBgPicker({
  selected,
  onSelect,
}: {
  selected: MobileBackground;
  onSelect: (id: MobileBackground) => void;
}) {
  const {
    packs,
    ownedPackIds,
    purchase,
    refresh,
    imageSrcOf,
    loaded,
    offeredPresetIds,
  } = useThemePacks();
  const [busyPack, setBusyPack] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Cycle 191: every non-core pack (free OR paid). A FREE non-core pack
  // previously fell through both sections (the free section is hardcoded to
  // `core`, the premium section was `!is_free`) → invisible. Render them all;
  // the lock/Unlock UI only shows for a paid pack the account doesn't own.
  const nonCorePacks = packs.filter((p) => p.pack_id !== "core");

  const unlock = async (packId: string) => {
    setBusyPack(packId);
    setNotice(null);
    const r = await purchase(packId);
    setBusyPack(null);
    if (r.comingSoon) setNotice("Theme packs are coming soon.");
    else if (!r.ok) setNotice(r.error ?? "Couldn't complete that — try again.");
  };

  return (
    <div className={styles.mbgPicker}>
      {/* Free catalog — grouped by family (pack "core"). Cycle 289: only the
          built-ins the server still OFFERS render — a Core-hidden built-in
          (admin Theme Packs → hide) is subtracted from core's `preset_ids`,
          so we filter by `offeredPresetIds`. Before the catalog loads we show
          all (no cold-mount flash); `aurora` can never be hidden. A family
          with nothing left to show is dropped. */}
      {BG_FAMILIES.map((family) => {
        const familyPresets = BG_PRESETS.filter(
          (p) =>
            p.pack === "core" &&
            p.family === family &&
            (!loaded || offeredPresetIds.has(p.id)),
        );
        if (familyPresets.length === 0) return null;
        return (
          <div key={family} className={styles.mbgGroup}>
            <div className={styles.mbgGroupLabel}>{family}</div>
            <div className={styles.mbgGrid}>
              {familyPresets.map((p) => (
                <MobileBgChip
                  key={p.id}
                  preset={p}
                  selected={selected === p.id}
                  unlocked
                  onPick={() => onSelect(p.id)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Non-core packs — each its own section. A paid pack is locked until
          owned; a free pack is always unlocked (no badge). */}
      {nonCorePacks.map((pk) => {
        const owned = pk.is_free || ownedPackIds.has(pk.pack_id);
        // Cycle 189: render from the SERVER's pack membership (pk.preset_ids)
        // so runtime-uploaded image presets appear, not just the static
        // catalog. Resolve each to its static BgPreset, or synthesize one
        // (image kind, name title-cased from the slug) for an uploaded id.
        const presets: BgPreset[] = pk.preset_ids.map(
          (pid) =>
            BG_BY_ID[pid] ?? {
              id: pid,
              name: pid
                .split("-")
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" "),
              family: "Adventure",
              kind: "image",
              pack: pk.pack_id,
            },
        );
        return (
          <div key={pk.pack_id} className={styles.mbgGroup}>
            <div className={styles.mbgPackHeader}>
              <span className={styles.mbgGroupLabel}>{pk.title}</span>
              {pk.is_free ? null : owned ? (
                <span className={styles.mbgOwned}>Owned</span>
              ) : (
                <button
                  type="button"
                  className={styles.mbgUnlock}
                  disabled={busyPack === pk.pack_id}
                  onClick={() => void unlock(pk.pack_id)}
                >
                  {busyPack === pk.pack_id
                    ? "…"
                    : `Unlock — ${pk.price_display}`}
                </button>
              )}
            </div>
            <div className={styles.mbgGrid}>
              {presets.map((p) => (
                <MobileBgChip
                  key={p.id}
                  preset={p}
                  selected={selected === p.id}
                  unlocked={owned}
                  thumbSrc={imageSrcOf(p.id)?.thumb_src}
                  onPick={() =>
                    owned ? onSelect(p.id) : void unlock(pk.pack_id)
                  }
                />
              ))}
            </div>
          </div>
        );
      })}

      {notice ? <p className={styles.rowHint}>{notice}</p> : null}
      <button
        type="button"
        className={styles.mbgRestore}
        onClick={() => {
          setNotice(null);
          void refresh();
        }}
      >
        Restore purchases
      </button>
    </div>
  );
}

function AppearanceSection({
  settings,
  onPatch,
  variant,
  backgrounds,
  atMax,
  bgError,
  onUpload,
  onDelete,
}: {
  settings: UserSettings;
  onPatch: (p: UserSettingsPatch) => void;
  variant: FrameVariant;
  backgrounds: Background[];
  atMax: boolean;
  bgError: string | null;
  // Cycle 87: receives the client-cropped JPEG blob + its dimensions, not
  // the original file.
  onUpload: (
    blob: Blob,
    width: number,
    height: number,
    filename?: string,
  ) => void;
  onDelete: (id: string) => void;
}) {
  const isMobile = variant === "mobile";
  const bgCameraOnly = cameraOnlyDevice();
  // Cycle 87: separate inputs so the camera intent (`capture`) isn't
  // attached to plain file selection. The chosen file is HEIC-normalised
  // then handed to the 16:9 cropper before any upload.
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const pickRef = useRef<HTMLInputElement | null>(null);
  const [cropSource, setCropSource] = useState<Blob | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [prepError, setPrepError] = useState<string | null>(null);

  const handlePicked = async (f: File | undefined) => {
    if (!f) return;
    setPrepError(null);
    setPreparing(true);
    try {
      setCropSource(await toCroppableBlob(f));
    } catch {
      setPrepError(
        "Couldn't read that image. Try a different photo or format.",
      );
    } finally {
      setPreparing(false);
    }
  };
  const selectedBg = settings.display_background_id;

  return (
    <>
      {/* Cycle 348: the mobile-only Light/Dark card (Appearance → Mode) is
          retired — Dark is now the sole, permanent mobile appearance (see
          App.tsx's applyAppearance). `display_dark_mode` stays a real
          settings field on the wire (a stored `false` is left as-is, just
          inert); this card is simply gone, not disabled. */}

      {/* Cycle 171: mobile app background catalog — phones only (web +
          kiosk use the Theme + uploaded-background cards below and ignore
          this key). 18 built-in presets, grouped Calm / Scenic / Adventure. */}
      {isMobile ? (
        <SubsectionCard
          title="App background"
          subtitle="The backdrop behind your data."
          icon={Icon.image}
          tone="accent"
        >
          <MobileBgPicker
            selected={settings.display_mobile_background ?? "aurora"}
            onSelect={(id) => onPatch({ display_mobile_background: id })}
          />
          <p className={styles.rowHint}>Applies to phones.</p>
        </SubsectionCard>
      ) : null}

      {/* Theme — all surfaces (set the look from a phone, web, or kiosk). */}
      <SubsectionCard
        title="Theme"
        subtitle="A relaxing look for the web app and kiosk."
        icon={Icon.palette}
        tone="accent"
      >
        <div className={styles.themeSwatchGrid}>
          {THEME_META.map((t) => (
            <ThemeSwatch
              key={t.id}
              meta={t}
              selected={settings.display_theme === t.id}
              onSelect={() => onPatch({ display_theme: t.id })}
            />
          ))}
        </div>
        <p className={styles.rowHint}>
          Applies to the web app and the kiosk display.
        </p>
      </SubsectionCard>

      {/* Background image — manage up to 5. */}
      <SubsectionCard
        title="Background image"
        subtitle="Optional — replaces the theme gradient."
        icon={Icon.image}
        tone="neutral"
      >
        <div className={styles.bgGrid}>
          <button
            type="button"
            onClick={() => onPatch({ display_background_id: null })}
            className={`${styles.bgTile} ${styles.bgNoneTile} ${
              selectedBg == null ? styles.bgTileSelected : ""
            }`}
          >
            None
          </button>
          {backgrounds.map((b) => (
            <BackgroundThumb
              key={b.id}
              bg={b}
              selected={selectedBg === b.id}
              onSelect={() => onPatch({ display_background_id: b.id })}
              onDelete={() => onDelete(b.id)}
            />
          ))}
        </div>

        <p className={styles.rowHint}>
          Landscape (16:9) images look best — they fill the display edge
          to edge. Portrait or square images can be cropped to fit before
          uploading.
        </p>

        {/* Add an image — phone/web only; selecting/deleting still works at
            the kiosk. Two inputs so the camera intent isn't attached to plain
            file selection. Both routes go through the 16:9 cropper.

            Cycle 458: "kiosk" here is the kiosk APK (lib/photoSource), never
            the layout — the standard Android app on a tablet in landscape
            renders the kiosk VARIANT and keeps its upload. The kiosk APK has
            no camera path for backgrounds, so it shows the plain-text line
            "Add it from your phone." in the controls' place (not a button). */}
        {bgCameraOnly ? (
          <p className={styles.bgAddFromPhone} data-testid="bg-add-from-phone">
            {ADD_FROM_PHONE_LINE}
          </p>
        ) : (
          <div className={styles.bgUploadRow}>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={(e) => {
                void handlePicked(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <input
              ref={pickRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              style={{ display: "none" }}
              onChange={(e) => {
                void handlePicked(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              disabled={atMax || preparing}
              onClick={() => cameraRef.current?.click()}
            >
              Take photo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={atMax || preparing}
              onClick={() => pickRef.current?.click()}
            >
              Choose image
            </Button>
            {preparing ? (
              <p className={styles.rowHint}>Preparing image…</p>
            ) : atMax ? (
              <p className={styles.rowHint}>
                Maximum of 5 background images. Delete one to add another.
              </p>
            ) : null}
          </div>
        )}

        {prepError ? <p className={styles.bgError}>{prepError}</p> : null}
        {bgError ? <p className={styles.bgError}>{bgError}</p> : null}
        <p className={styles.rowHint}>
          Backgrounds appear on the web app and the kiosk display.
        </p>
      </SubsectionCard>

      {/* Cycle 87: 16:9 pan/zoom cropper — every upload is re-encoded to
          JPEG through it, so the server only ever receives JPEG. */}
      {cropSource ? (
        <BackgroundCropper
          source={cropSource}
          onCancel={() => setCropSource(null)}
          onConfirm={(blob, w, h) => {
            setCropSource(null);
            onUpload(blob, w, h, "background.jpg");
          }}
        />
      ) : null}
    </>
  );
}

// ─── Alerts (cycle 29: master section that consolidates the
//     cycle-pre-29 Targets, Notifications, and Test alarms panes into
//     one scrolling pane. Cycle 216 removed the Quiet hours card.) ─────

function AlertsSection({
  settings,
  onPatch,
  push,
  nativePush,
  nav,
  activeMember,
  episode,
  episodeRecoveredAt,
  variant,
  showEmergencyContacts = true,
  alarms,
  selfUserId,
  selfDisplayName,
}: {
  settings: UserSettings;
  onPatch: (p: UserSettingsPatch) => void;
  push: UsePushSubscriptions;
  nativePush: ReturnType<typeof useNativePush>;
  nav: ReturnType<typeof useNavigate>;
  activeMember: Member | null;
  episode?: AlarmEpisodeState;
  episodeRecoveredAt?: string | null;
  variant: FrameVariant;
  /** Cycle 245: render the Emergency-contacts card inside Alerts. True on
   *  web/mobile (one Alerts pane); false on the tablet, where it has its own
   *  "Contacts" tab. */
  showEmergencyContacts?: boolean;
  /** Cycle 242: alarm-sound owner for the Thresholds cards' preview. */
  alarms?: AlarmSounds;
  /** Cycle 403: the signed-in account, so the contacts pane can offer it
   *  as a link target and name it. Null on a device (kiosk) session,
   *  where linking is read-only anyway. */
  selfUserId?: string | null;
  selfDisplayName?: string | null;
}) {
  const enabled = settings.alerts_enabled;
  // Cycle 67: probe `/api/health.dev_inject_enabled` once on mount.
  // Production servers return `false`, so the dev console never
  // mounts there even though the code is in the bundle.
  const devInjectOn = useDevInjectEnabled();
  const thresholds = useThresholds();
  // ARCHIVED (hidden, may return): the "Test alarms" card is gated off behind
  // SHOW_TEST_ALARMS below per operator request. Flip the flag to true to
  // bring it back — all wiring (state, component) is left intact.
  const SHOW_TEST_ALARMS = false;
  // Cycle 74: Test alarms is a closed-by-default collapsible card.
  // The state lives at the section level so toggling persists across
  // intra-pane re-renders (e.g. an alert preview fires and re-renders
  // the body — the user's "I opened it" decision shouldn't reset).
  const [testOpen, setTestOpen] = useState(false);
  return (
    <div className={styles.cardStack}>
      {/* The master toggle is a header-only primary card with the toggle
          in the header-right slot. */}
      <SubsectionCard
        accent="primary"
        icon={Icon.alarm}
        title="Alerts enabled"
        description="Master switch for thresholds and every push rail below."
        headerRight={
          <Toggle
            on={enabled}
            onClick={() => onPatch({ alerts_enabled: !enabled })}
          />
        }
      />

      <div className={styles.cardStack} style={{ opacity: enabled ? 1 : 0.55 }}>
        <ThresholdsSubsection
          settings={settings}
          onPatch={onPatch}
          variant={variant}
          alarms={alarms}
        />
        <SubsectionCard
          title="Notifications"
          subtitle="Push to this device when alerts fire."
          icon={Icon.bell}
          accent="cyan"
        >
          <NotificationsSection
            push={push}
            nativePush={nativePush}
            nav={nav}
            variant={variant}
          />
        </SubsectionCard>
        {/* Cycle 228: repeating critical-alarm escalation. Re-sends the
            critical push on a cadence while an alarm is still sounding
            (Dexcom-style persistence on a locked phone). Push only — it
            never re-texts/re-calls the emergency contacts.
            Cycle 422: the options come from `lib/alarmRepeat.ts` (2/5/10/
            15/30 min; 30 s and 60 s are gone from the API and the DB too),
            and the toggle governs a plain low only — an urgent low always
            repeats. Same control, same row, new values. */}
        <SubsectionCard
          title="Repeat alarms"
          subtitle="Keep re-sending the critical alert until it's acknowledged (like Dexcom). An urgent low always repeats. Push only — your emergency contacts aren't re-texted."
          icon={Icon.alarm}
          accent="amber"
          headerRight={
            <Toggle
              on={settings.alarm_repeat_enabled ?? true}
              onClick={() =>
                onPatch({
                  alarm_repeat_enabled: !(settings.alarm_repeat_enabled ?? true),
                })
              }
            />
          }
        >
          <div
            style={{
              opacity: (settings.alarm_repeat_enabled ?? true) ? 1 : 0.55,
            }}
          >
            <Row label="Repeat every">
              <Seg
                value={String(
                  settings.alarm_repeat_interval_seconds ??
                    ALARM_REPEAT_INTERVAL_DEFAULT_SECONDS,
                )}
                options={ALARM_REPEAT_INTERVAL_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                }))}
                onChange={(id) =>
                  onPatch({ alarm_repeat_interval_seconds: Number(id) })
                }
              />
            </Row>
          </div>
        </SubsectionCard>
        {/* Cycle 245: Emergency contacts has its own tablet tab now; only
            render it inside Alerts on non-tablet surfaces. Reminders likewise
            has its own tab (the cycle-224 fold is removed). */}
        {showEmergencyContacts ? (
          <SubsectionCard
            title="Heroes"
            subtitle="Your emergency contacts — texted + called on low and urgent low alerts."
            icon={Icon.shield}
            accent="rose"
          >
            <EmergencyContactsSection
              variant={variant}
              selfUserId={selfUserId}
              selfDisplayName={selfDisplayName}
            />
          </SubsectionCard>
        ) : null}
        {/* ARCHIVED (hidden, may return): Test alarms preview card — gated off
            via SHOW_TEST_ALARMS. Flip that flag to true to restore it. */}
        {SHOW_TEST_ALARMS ? (
          <SubsectionCard
            title="Test alarms"
            subtitle="Preview each alert level on this device."
            icon={Icon.speaker}
            accent="green"
            collapsible
            open={testOpen}
            onToggle={() => setTestOpen((o) => !o)}
          >
            <TestAlarmsSubsection />
          </SubsectionCard>
        ) : null}
        {devInjectOn && episode ? (
          <AlarmTestConsole
            activeMember={activeMember}
            thresholds={thresholds}
            episode={episode}
            recoveredAt={episodeRecoveredAt}
          />
        ) : null}
      </div>
    </div>
  );
}

// Cycle 242: per-zone custom-sound picker (dropdown + play/stop preview),
// rendered inside each Thresholds card via ThresholdSlider's render-prop.
// Selecting PATCHes the zone's setting key immediately (optimistic via the
// shared settings patch → synced cross-device by `settings_update`). Preview
// routes through the shared `useAlarmSounds` owner (no new Audio element);
// it never fires a real alarm and a live urgent alarm always wins.
// Cycle 243: the zone color token driving the play button (zone-colored,
// per the spec) — distinct from the threshold card's accent.
const ALARM_SOUND_ZONE_COLOR: Record<AlarmZone, string> = {
  urgent_low: "var(--zone-urgent)",
  low: "var(--zone-warn)",
  inrange: "var(--zone-ok)",
  high: "var(--zone-warn)",
  urgent_high: "var(--zone-urgent)",
};

function AlarmSoundPicker({
  zone,
  value,
  onChange,
  alarms,
  kioskButton,
}: {
  zone: AlarmZone;
  value: string;
  onChange: (id: string) => void;
  alarms?: AlarmSounds;
  /** Cycle 243: Android-tablet kiosk → the 52px solid rounded-square button;
   *  iOS / web → the 44px glass circle. */
  kioskButton?: boolean;
}) {
  const options = ALARM_SOUND_OPTIONS[zone];
  const playing = alarms?.previewingZone === zone;
  const isInRange = zone === "inrange";
  const iconSize = kioskButton ? 18 : 16;
  return (
    <div className={ts.soundPicker} style={{ ["--zone-c" as string]: ALARM_SOUND_ZONE_COLOR[zone] }}>
      <div className={ts.soundLabel}>
        {isInRange ? "Recovery sound" : "Alarm sound"}
      </div>
      <div className={ts.soundRow}>
        <div className={ts.soundSelectWrap}>
          <select
            className={`${ts.soundSelect} ${kioskButton ? ts.soundSelectKiosk : ""}`}
            value={value}
            aria-label={isInRange ? "Recovery sound" : "Alarm sound"}
            onChange={(e) => {
              alarms?.stopPreview();
              onChange(e.target.value);
            }}
          >
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {alarms ? (
          <button
            type="button"
            className={`${ts.soundPreviewBtn} ${kioskButton ? ts.soundPreviewBtnKiosk : ""}`}
            aria-label={playing ? "Stop preview" : "Play preview"}
            aria-pressed={playing}
            onClick={() =>
              playing ? alarms.stopPreview() : alarms.previewSound(zone, value)
            }
          >
            {playing ? (
              <svg width={iconSize} height={iconSize} viewBox="0 0 16 16" aria-hidden="true">
                <rect x="3" y="3" width="10" height="10" rx="1.5" fill="currentColor" />
              </svg>
            ) : (
              <svg width={iconSize} height={iconSize} viewBox="0 0 16 16" aria-hidden="true">
                <path d="M4 3 L13 8 L4 13 Z" fill="currentColor" />
              </svg>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ThresholdsSubsection({
  settings,
  onPatch,
  variant,
  alarms,
}: {
  settings: UserSettings;
  onPatch: (p: UserSettingsPatch) => void;
  variant: FrameVariant;
  alarms?: AlarmSounds;
}) {
  const { confirm } = useConfirm();
  const { units } = useSettings();
  const sliderRef = useRef<ThresholdSliderHandle>(null);

  // Cycle 242: current saved sound id per zone (default "default").
  const soundIdFor = (zone: AlarmZone): string => {
    switch (zone) {
      case "urgent_low":
        return settings.alarm_sound_urgent_low ?? "default";
      case "low":
        return settings.alarm_sound_low ?? "default";
      case "inrange":
        return settings.alarm_sound_inrange ?? "default";
      case "high":
        return settings.alarm_sound_high ?? "default";
      case "urgent_high":
        return settings.alarm_sound_urgent_high ?? "default";
    }
  };

  // Cycle 74.1: auto-commit happens on every stepper press via
  // `onCommit`, so the explicit Save button from cycle 40 is removed.
  // The only action that remains is **Reset to default**, which snaps
  // the four thresholds to the system defaults (55 / 70 / 180 / 250)
  // and fires `onCommit` for each changed key through the slider's
  // imperative `resetToDefaults()` method.
  const onResetToDefaults = async () => {
    if (
      !(await confirm({
        message: "Reset thresholds to the defaults (55 / 70 / 180 / 250 mg/dL)?",
        confirmLabel: "Reset",
      }))
    )
      return;
    sliderRef.current?.resetToDefaults();
  };

  return (
    <SubsectionCard
      title="Thresholds"
      subtitle="Your glucose range — drag a handle or use − / + on each card."
      icon={Icon.thresholds}
      accent="amber"
    >
      <ThresholdSlider
        ref={sliderRef}
        value={{
          urgent_low: settings.urgent_low,
          target_low: settings.target_low,
          target_high: settings.target_high,
          urgent_high: settings.urgent_high,
        }}
        units={units}
        variant={variant}
        onCommit={(key, next) =>
          onPatch({ [key]: next } as UserSettingsPatch)
        }
        renderSoundPicker={(zone) => (
          <AlarmSoundPicker
            zone={zone}
            value={soundIdFor(zone)}
            alarms={alarms}
            kioskButton={variant === "kiosk" && isAndroidTabletClassRuntime()}
            onChange={(id) =>
              onPatch({
                [ALARM_SOUND_SETTING_KEY[zone]]: id,
              } as UserSettingsPatch)
            }
          />
        )}
      />
      <div className={styles.thresholdActions}>
        <Button variant="ghost" size="sm" onClick={onResetToDefaults}>
          Reset to default
        </Button>
      </div>
      {/* Cycle 137: set expectations — alerts are reading-driven, not
          instant on a threshold change. */}
      <div className={styles.subtleHint}>
        Alerts fire on the next CGM reading after a change — usually within
        ~5 minutes, not instantly.
      </div>
      <div className={styles.subtleHint}>
        Stored in mg/dL on the wire regardless of your units preference
        {units === "mmol/L" ? " (showing mmol/L)" : ""}.
      </div>
    </SubsectionCard>
  );
}

// ─── Voice (cycle 16) ─────────────────────────────────────────────────

// Cycle 330.2 — the Speak sample previews a REAL announcement (member's own
// name + glucose + trend). The implementation lives in `lib/voiceSample.ts`
// so the unit tests exercise the SAME function this screen calls, rather
// than a copy that could drift. See that file for the two rules it honors
// (no zone claim; never assert a stale value).

const ANNOUNCE_INTERVAL_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Off" },
  { value: 5, label: "Every 5 minutes" },
  { value: 10, label: "Every 10 minutes" },
  { value: 15, label: "Every 15 minutes" },
  { value: 20, label: "Every 20 minutes" },
  { value: 25, label: "Every 25 minutes" },
  { value: 30, label: "Every 30 minutes" },
  { value: 35, label: "Every 35 minutes" },
  { value: 40, label: "Every 40 minutes" },
  { value: 45, label: "Every 45 minutes" },
  { value: 50, label: "Every 50 minutes" },
  { value: 55, label: "Every 55 minutes" },
  { value: 60, label: "Every 60 minutes" },
];

function VoiceSection({
  settings,
  onPatch,
  tts,
  variant,
  memberName,
  currentReading,
  consolidated = false,
  alarmLive = false,
}: {
  settings: UserSettings;
  onPatch: (p: UserSettingsPatch) => void;
  tts: TTSState;
  /** Cycle 330: kiosk gets keyboard-free time steppers for quiet hours. */
  variant: FrameVariant;
  /** Cycle 330.2: the Speak sample previews a real announcement, so it
   *  needs the active member's name + their latest reading. */
  memberName: string | null;
  /** Cycle 435: a getter, resolved at click time through the selector —
   *  `null` when the selector would withhold the number. */
  currentReading: () => Reading | null;
  /** Cycle 224 (A2): Android tablet — fold the Auto-announce cadence into
   *  the single Voice & TTS card (no separate Auto-announce card). */
  consolidated?: boolean;
  /** Cycle 385: `episode.isLive` — READ ONLY. The voice-command test card
   *  defers to a sounding alarm exactly as the real session does. */
  alarmLive?: boolean;
}) {
  const [voices, setVoices] = useState<VoiceInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // Cycle 330: no locale filter — the voice library isn't
      // locale-partitioned. (The server still tolerates the old
      // `locale_prefix` param from stale clients; we just stop sending it.)
      const r = await ttsApi.listVoices();
      if (cancelled) return;
      if (r.ok) {
        setVoices(r.data.voices);
        setLoadError(null);
      } else {
        setLoadError(r.error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onPickVoice = (next: string) => {
    onPatch({ tts_voice: next });
    // Push immediately so the Speak Sample button below uses the new
    // choice without waiting for the settings PATCH round-trip.
    tts.setVoice(next);
  };

  return (
    <div className={styles.cardStack}>
      <SubsectionCard
        title="Voice & TTS"
        subtitle="The voice Sugar Alarm uses for spoken status updates."
        icon={Icon.voice}
        tone="accent"
      >
        <Row label="Voice">
          {voices == null && !loadError ? (
            <select className={inputClass} disabled>
              <option>Loading voices…</option>
            </select>
          ) : loadError ? (
            <select
              className={inputClass}
              value={settings.tts_voice}
              onChange={(e) => onPickVoice(e.target.value)}
            >
              <option value={settings.tts_voice}>{settings.tts_voice}</option>
            </select>
          ) : (
            <select
              className={inputClass}
              value={settings.tts_voice}
              onChange={(e) => onPickVoice(e.target.value)}
            >
              {/* A saved voice that's no longer in the catalog would
                  otherwise leave the <select> with no matching option and
                  render blank. Pin it as an option so the control always
                  shows the member's current choice. (Speaking still works:
                  the server silently substitutes the default voice.) */}
              {voices!.some((v) => v.voice_id === settings.tts_voice) ? null : (
                <option value={settings.tts_voice}>Saved voice</option>
              )}
              {voices!.map((v) => (
                <option key={v.voice_id} value={v.voice_id}>
                  {v.friendly_name}
                </option>
              ))}
            </select>
          )}
        </Row>
        {loadError ? (
          <div className={styles.subtleHint} style={{ color: "var(--zone-urgent)" }}>
            Couldn't load the voice list. Showing the saved voice only.
          </div>
        ) : null}
        <Row label="Test voice">
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() =>
              // Composed at CLICK time (not render time) so it reflects the
              // reading on screen right now, not one captured earlier.
              // Manual + explicitly requested → speaks at ANY hour; quiet
              // hours never gate this button. Cycle 474: the user's own tap,
              // so the post-resume settle never swallows it.
              runUserInitiated(() => tts.speak(
                voiceSampleText(
                  memberName,
                  currentReading(),
                  settings.units,
                  Date.now(),
                ),
              ))
            }
          >
            Speak sample
          </button>
        </Row>
        {/* Cycle 330 — engine-status pill. STATIC and GENERIC by design.
            It previously read "Now speaking via <provider>". The household
            is never told which engine spoke: the server's primary tier, its
            internal fallback, and the device's own built-in voice are
            indistinguishable here, on purpose. Do NOT reintroduce a
            provider name in ANY state of this pill. */}
        <div className={styles.voiceReadyPill}>
          <span className={styles.voiceReadyDot} aria-hidden="true" />
          Voice ready
        </div>
        <div className={styles.subtleHint}>
          Spoken updates stream from Sugar Alarm. If this device can't reach
          the service, it falls back to the device's built-in voice
          automatically.
        </div>
        {/* Cycle 224 (A2): in the consolidated (Android-tablet) card, the
            auto-announce cadence lives here as a row instead of a second
            card. Same setting/behavior. */}
        {consolidated ? (
          <Row label="Auto-announce">
            <select
              className={inputClass}
              value={String(settings.tts_announce_interval_minutes)}
              onChange={(e) =>
                onPatch({
                  tts_announce_interval_minutes: Number(e.target.value),
                })
              }
            >
              {ANNOUNCE_INTERVAL_OPTIONS.map((o) => (
                <option key={o.value} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </Row>
        ) : null}
      </SubsectionCard>
      {/* Non-tablet: the separate Auto-announce card (byte-identical). */}
      {consolidated ? null : (
        <SubsectionCard
          title="Auto-announce"
          subtitle="Periodic spoken status updates while you're in range."
          icon={Icon.clock}
          tone="neutral"
        >
          <Row label="Interval">
            <select
              className={inputClass}
              value={String(settings.tts_announce_interval_minutes)}
              onChange={(e) =>
                onPatch({
                  tts_announce_interval_minutes: Number(e.target.value),
                })
              }
            >
              {ANNOUNCE_INTERVAL_OPTIONS.map((o) => (
                <option key={o.value} value={String(o.value)}>
                  {o.label}
                </option>
              ))}
            </select>
          </Row>
        </SubsectionCard>
      )}

      {/* ── Cycle 330: Quiet hours ──────────────────────────────────────
          NARROW BY DESIGN. This pauses the auto-announce cadence and the
          SPOKEN half of a fired reminder, and nothing else.

          It is NOT the alert/notification quiet-hours feature that was
          permanently removed in cycle 216 at the operator's explicit
          request. It must never gate an alert, a push, an alarm sound, the
          urgent overlay, or the manual Speak Status / Speak sample buttons
          — all of which keep working at any hour. The scope note below is
          the promise we make to the household; keep it visible. */}
      <SubsectionCard
        title="Quiet hours"
        subtitle="Pause status announcements & reminders overnight."
        icon={Icon.clock}
        accent="violet"
        headerRight={
          <span className={styles.quietHeaderRight}>
            {/* The mockup places a "New" badge beside the card title. It
                lives in `headerRight` rather than inside `title` because
                `title` is a STRING BY CONTRACT — it is slugified into the
                cycle-172 collapse-persistence key
                (`sugalarm.settingsCollapse.<sectionId>.<title-slug>`) and the
                cycle-207 accent registry key. Widening it to a ReactNode
                would silently break both. */}
            <span className={styles.newBadge}>New</span>
            <Toggle
              on={settings.tts_quiet_hours_enabled}
              onClick={() =>
                onPatch({
                  tts_quiet_hours_enabled: !settings.tts_quiet_hours_enabled,
                })
              }
            />
          </span>
        }
      >
        {/* Mockup: a single "From <time> to <time>" row, not two stacked
            rows. Wraps on narrow widths so the kiosk's keyboard-free
            steppers (which are much wider than a native time input) can
            never overflow. */}
        <div className={styles.quietTimeRow}>
          <span className={styles.quietTimeLabel}>From</span>
          <TimeField
            value={settings.tts_quiet_hours_start}
            h24={settings.display_24h}
            variant={variant}
            ariaLabel="Quiet hours start"
            onChange={(next) => onPatch({ tts_quiet_hours_start: next })}
          />
          <span className={styles.quietTimeSep}>to</span>
          <TimeField
            value={settings.tts_quiet_hours_end}
            h24={settings.display_24h}
            variant={variant}
            ariaLabel="Quiet hours end"
            onChange={(next) => onPatch({ tts_quiet_hours_end: next })}
          />
        </div>
        {/* Load-bearing safety copy, verbatim from the approved mockup. It is
            the household's assurance that this control cannot silence an
            alarm — do not shorten it. */}
        <div className={styles.scopeNote}>
          <span className={styles.scopeNoteIcon} aria-hidden="true">
            &#9432;
          </span>
          <p>
            <b>Voice announcements only.</b> Alarms, urgent alerts, and the
            manual Speak Status button always work — this never silences an
            alarm.
          </p>
        </div>
      </SubsectionCard>
      {/* Cycle 385 — voice command diagnostic. Self-gating (kiosk APK + dev
          flag + native plugin), so it renders nowhere else. */}
      <VoiceCommandTestCard alarmLive={alarmLive} />
    </div>
  );
}

// ─── Voice command test (cycle 385) ───────────────────────────────────
//
// Answers the one question the voice layer could not answer for itself:
// when it says "Sorry, I didn't catch that", was it MISHEARD, or is the
// phrase simply not a supported command? Those have opposite fixes, and
// before this card the only way to tell them apart was a USB debug bridge.
//
// DIAGNOSTIC ONLY — it parses and reports. It never dispatches the intent,
// never speaks, and never writes a treatment. See `hooks/useVoiceTest.ts`.
function VoiceCommandTestCard({ alarmLive }: { alarmLive: boolean }) {
  // The same gate as the rest of the cycle-383 voice layer: the Android
  // kiosk APK, with the dev flag set. Web, mobile, iPad, the Pi kiosk and
  // the phone app never mount it.
  const gated =
    isNativeKioskShell() && isVoiceDevEnabled() && voiceCaptureIsPresent();
  const test = useVoiceTest({ enabled: gated, alarmLive });
  if (!gated) return null;

  const listening = test.phase === "listening";
  const o = test.outcome;

  return (
    <SubsectionCard
      title="Test voice commands"
      subtitle="See what the kiosk hears — nothing is logged."
      icon={Icon.voice}
      accent="cyan"
    >
      <div className={styles.voiceTestRow}>
        <button
          className={styles.btnSecondary}
          onClick={() => (listening ? test.stop() : test.start())}
          disabled={!test.available || (alarmLive && !listening)}
        >
          {listening ? "Stop" : "Start test"}
        </button>
        <span className={styles.voiceTestHint}>
          {!test.available
            ? "Speech recognition unavailable on this device."
            : alarmLive
              ? "Paused — an alarm is active."
              : listening
                ? "Listening… say a command."
                : "Press start, then speak a command."}
        </span>
      </div>

      {listening && (
        <div className={styles.voiceTestHeard}>
          {test.partial ? `\u201C${test.partial}\u201D` : "…"}
        </div>
      )}

      {!listening && o && (
        <div className={styles.voiceTestResult}>
          <div className={styles.voiceTestHeard}>
            {o.transcript ? `\u201C${o.transcript}\u201D` : "— nothing heard —"}
          </div>
          <div>
            {o.error ? (
              <span className={styles.voiceTestMiss}>
                {o.error === "no_match"
                  ? "Audio captured, but no words were recognised — try speaking closer, or more clearly."
                  : o.error === "timeout"
                    ? "No speech detected."
                    : `Recogniser error: ${o.error}`}
              </span>
            ) : o.intent ? (
              <span className={styles.voiceTestMatch}>
                Understood as <b>{o.intent.intent}</b>
                {o.intent.slots.units ? ` \u2014 ${o.intent.slots.units} units` : ""}
              </span>
            ) : (
              <span className={styles.voiceTestMiss}>
                Heard clearly, but this phrasing is not a supported command.
              </span>
            )}
          </div>
        </div>
      )}

      <div className={styles.scopeNote}>
        <span className={styles.scopeNoteIcon} aria-hidden="true">
          &#9432;
        </span>
        <p>
          <b>Nothing is logged or sent.</b> This only shows what was heard and
          how it was understood — no dose is recorded and no answer is spoken.
        </p>
      </div>
      <WakeWordBench />
    </SubsectionCard>
  );
}

// ─── Wake-word bench controls (cycle 385) ─────────────────────────────
//
// NOT a consumer feature. This drives the always-listening service so the
// MICROPHONE HANDOFF — release → session → re-acquire — can be proven on
// real hardware BEFORE any wake model exists. "Simulate wake" fires the
// identical code path a real detection would, which is the whole point:
// the risky part of the wake word is the mic lifecycle, not the inference.
function WakeWordBench() {
  const [status, setStatus] = useState<wakeWord.WakeStatus | null>(null);
  const present = wakeWord.isPresent();

  const refresh = useCallback(async () => {
    setStatus(await wakeWord.getStatus());
  }, []);

  useEffect(() => {
    if (!present) return;
    void refresh();
    const un = wakeWord.subscribe({ onState: () => void refresh() });
    const id = window.setInterval(() => void refresh(), 2000);
    return () => {
      un();
      window.clearInterval(id);
    };
  }, [present, refresh]);

  if (!present) return null;
  const state = status?.state ?? "stopped";
  const running = state === "listening" || state === "yielded";

  return (
    <div className={styles.wakeBench}>
      <div className={styles.wakeBenchTitle}>
        Wake word <span className={styles.wakeBenchTag}>bench</span>
      </div>
      <div className={styles.voiceTestRow}>
        <button
          className={styles.btnSecondary}
          onClick={async () => {
            running ? await wakeWord.stop() : await wakeWord.start();
            await refresh();
          }}
        >
          {running ? "Stop service" : "Start service"}
        </button>
        <button
          className={styles.btnSecondary}
          onClick={() => void wakeWord.simulateDetection()}
          disabled={state !== "listening"}
        >
          Simulate wake
        </button>
        <span className={styles.voiceTestHint}>
          state: <b>{state}</b>
          {status?.error ? ` (${status.error})` : ""}
          {status && !status.modelInstalled ? " · no model installed" : ""}
        </span>
      </div>
    </div>
  );
}

// ─── Notifications (cycle 17) ─────────────────────────────────────────

function shortDeviceLabel(ua: string | null | undefined): string {
  if (!ua) return "Unknown device";
  const tests: Array<[RegExp, string]> = [
    [/iPhone/, "iPhone"],
    [/iPad/, "iPad"],
    [/iPod/, "iPod"],
    [/Macintosh/, "Mac"],
    [/Android/, "Android"],
    [/Windows NT/, "Windows"],
    [/Linux/, "Linux"],
  ];
  let device = "";
  for (const [re, label] of tests) {
    if (re.test(ua)) {
      device = label;
      break;
    }
  }
  let browser = "";
  if (/EdgiOS|Edg\//.test(ua)) browser = "Edge";
  else if (/Firefox/.test(ua)) browser = "Firefox";
  else if (/CriOS|Chrome\//.test(ua)) browser = "Chrome";
  else if (/Safari\//.test(ua)) browser = "Safari";
  if (device && browser) return `${device} · ${browser}`;
  if (device) return device;
  return ua.slice(0, 60);
}

function isIosUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

function NotificationsSection({
  push,
  nativePush,
  nav,
  variant,
}: {
  push: UsePushSubscriptions;
  nativePush: ReturnType<typeof useNativePush>;
  nav: ReturnType<typeof useNavigate>;
  variant: FrameVariant;
}) {
  const { confirm } = useConfirm();
  const supported = pushSupported();
  // Cycle 218: hide the cycle-17 Web Push controls on kiosk-class
  // surfaces — the Pi kiosk and the Android tablet kiosk — which can't
  // receive web push. A kiosk-class surface is the kiosk frame variant
  // that is NOT an iPad-class device (iPad-in-landscape renders the
  // kiosk layout but keeps Web Push, per the variant-gating contract;
  // Pi + Android-tablet kiosks are the only ones that lose it). Web,
  // iPhone, Android phone, and iPad stay byte-identical.
  const kioskClass = variant === "kiosk" && !isIPadClassRuntime();
  const showWebPush = !kioskClass;
  const [busy, setBusy] = useState<null | "enable" | "disable" | string>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);

  const matched = push.subs.find(
    (s) =>
      push.currentBrowserEndpoint != null &&
      s.endpoint === push.currentBrowserEndpoint &&
      s.disabled_at == null,
  );
  const enabledHere = matched != null;

  const onEnable = async () => {
    setActionError(null);
    setBusy("enable");
    try {
      await push.enable();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
    setBusy(null);
  };
  const onDisable = async () => {
    setActionError(null);
    setBusy("disable");
    try {
      await push.disable();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
    setBusy(null);
  };
  const onRemove = async (id: string) => {
    if (!(await confirm({ message: "Remove this device's push subscription?", destructive: true }))) return;
    setActionError(null);
    setBusy(`remove:${id}`);
    try {
      await push.remove(id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
    setBusy(null);
  };

  return (
    <>
      {/* Cycle 218: the cycle-17 Web Push surface — status row,
          Enable/Disable, and the subscription list with per-row Remove —
          renders only off kiosk-class surfaces (Pi + Android tablet
          kiosks can't receive web push). */}
      {showWebPush ? (
        <>
          {push.serverNotConfigured ? (
        <div
          className={styles.subtleHint}
          style={{
            color: "var(--zone-warn)",
            background: "var(--zone-warn-soft)",
            border: "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
          }}
        >
          Push notifications are not configured on the server. Ask the
          operator to generate VAPID keys
          (<code>python scripts/generate_vapid_keys.py</code>) and add
          them to <code>.env</code>.
        </div>
      ) : null}

      {!supported ? (
        <div className={styles.subtleHint}>
          {isIosUserAgent() ? (
            <>
              On iPhone/iPad, install Sugar Alarm to your home screen first
              (Share &rarr; Add to Home Screen), then open it from the
              home screen to enable push.
            </>
          ) : (
            <>
              This browser doesn't support Web Push. Try Chrome, Edge,
              Firefox, or Safari 16.4 or later.
            </>
          )}
        </div>
      ) : null}

      {actionError ? (
        <div
          className={styles.subtleHint}
          style={{
            color: "var(--zone-urgent)",
            background: "var(--zone-urgent-soft)",
            border: "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
          }}
        >
          {actionError}
        </div>
      ) : null}

      {/* This-device status */}
      <Row label="This device">
        <Pill tone={enabledHere ? "ok" : "neutral"}>
          {enabledHere ? "Enabled" : "Not enabled"}
        </Pill>
      </Row>
      <div className={styles.actionRow}>
        {enabledHere ? (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onDisable}
            disabled={busy != null}
          >
            {busy === "disable" ? "Disabling…" : "Disable on this device"}
          </button>
        ) : (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onEnable}
            disabled={busy != null || !supported || push.serverNotConfigured}
          >
            {busy === "enable" ? "Enabling…" : "Enable on this device"}
          </button>
        )}
      </div>

      {/* All subscriptions */}
      <Header title="Devices receiving push" />
      {push.loading ? (
        <div className={styles.subtleHint}>Loading…</div>
      ) : push.subs.length === 0 ? (
        <div className={styles.subtleHint}>No push subscriptions yet.</div>
      ) : (
        <div className={styles.deviceList}>
          {push.subs.map((s) => {
            const isThis =
              push.currentBrowserEndpoint != null &&
              s.endpoint === push.currentBrowserEndpoint;
            return (
              <div key={s.id} className={styles.deviceCard}>
                <div className={styles.deviceMeta}>
                  <div className={styles.deviceName}>
                    {shortDeviceLabel(s.user_agent)}
                    {isThis ? (
                      <span
                        style={{
                          color: "var(--accent)",
                          fontSize: "var(--type-floor)",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        this device
                      </span>
                    ) : null}
                    {s.disabled_at != null ? (
                      <span style={{ marginLeft: 6 }}>
                        <Pill tone="warn">disabled</Pill>
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.deviceSub}>
                    added {fmtTime(s.created_at)}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => onRemove(s.id)}
                  disabled={busy === `remove:${s.id}`}
                >
                  {busy === `remove:${s.id}` ? "Removing…" : "Remove"}
                </button>
              </div>
            );
          })}
        </div>
      )}
        </>
      ) : null}

      {/* Cycle 28: native APNS / FCM rail — only visible inside the
          Capacitor shell. The Pi kiosk has no native rail; the Android
          tablet kiosk keeps it (its working push channel). */}
      {nativePush.supported ? (
        <NativeAppPushSection nativePush={nativePush} nav={nav} />
      ) : null}

      {/* Cycle 218: when Web Push is hidden (kiosk-class) and there's no
          native rail either (the Pi kiosk), the card body would be empty
          — explain instead of leaving a blank/broken card. The Android
          tablet kiosk hits the native section above, so this never
          shows there. */}
      {!showWebPush && !nativePush.supported ? (
        <div className={styles.subtleHint}>
          Alarms play on this kiosk's screen and speaker — no push needed.
        </div>
      ) : null}

      {/* Cycle 226: iOS-only Critical Alerts status/recovery + Hey Siri
          reference. Rendered ONLY on the iOS native shell — every other
          surface (web, Android, Pi kiosk, Android tablet kiosk) renders
          nothing. */}
      {isIosNative() ? (
        <>
          <CriticalAlertsCard />
          <HeySiriCard />
        </>
      ) : null}

      {/* Cycle 303: Android-native-only "Alarm reliability" card. Renders
          ONLY on the Android standard phone APK carrying the AlarmReliability
          plugin — nothing on web, iOS, the Pi kiosk, or the Android tablet
          kiosk. */}
      {alarmReliabilityAvailable() ? <AlarmReliabilityCard /> : null}
    </>
  );
}

// ─── Critical Alerts status + recovery (iOS-native only, cycle 226) ───────
//
// Shows whether the iOS Critical Alerts capability is actually granted for
// this app and, when it isn't, a deep-link to this app's iOS Settings page
// to turn it on. Read-only over the native NotificationSettings plugin; no
// network, no PHI, no auth. Refreshes on mount and on foreground so flipping
// the toggle in iOS Settings and returning updates the badge.

function CriticalAlertsCard() {
  const [status, setStatus] = useState<NotificationStatus | null>(null);
  // Whether the native NotificationSettings plugin is registered on the
  // bridge. The SPA card is pure-remote so it renders on any iOS-native
  // surface, but if the installed BUILD doesn't carry the plugin (an older
  // archive, before the cycle-226 native code shipped), getStatus() returns
  // null and the deep-link is a no-op. Detect that and show an honest
  // "Unavailable" + update note instead of a misleading "Off" + dead button.
  const pluginAvailable = notificationPluginAvailable();

  const refresh = useCallback(() => {
    void getNotificationStatus().then((s) => {
      if (s != null) setStatus(s);
    });
  }, []);

  useEffect(() => {
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  const isOn =
    status != null &&
    status.criticalAlerts === "enabled" &&
    status.authorization === "authorized";
  // Unavailable when the build can't support it: either the plugin isn't in
  // the installed archive, or the OS reports the capability notSupported.
  const isUnavailable =
    !pluginAvailable ||
    (status != null && status.criticalAlerts === "notSupported");

  const badge: { tone: "ok" | "warn" | "neutral"; label: string } = isOn
    ? { tone: "ok", label: "On" }
    : isUnavailable
      ? { tone: "neutral", label: "Unavailable" }
      : { tone: "warn", label: "Off" };

  return (
    <>
      <Header
        title="Critical Alerts"
        sub="Let urgent glucose alarms break through silent mode, Focus, and Do Not Disturb."
      />
      <Row label="Status">
        <Pill tone={badge.tone}>{badge.label}</Pill>
      </Row>
      {!pluginAvailable ? (
        <div className={styles.subtleHint}>
          Update to the latest Sugar Alarm build to manage Critical Alerts here.
          You can still turn them on in the iOS Settings app under
          Notifications &rarr; Sugar Alarm.
        </div>
      ) : !isOn ? (
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.btnPrimary}
            style={{ minHeight: 44 }}
            onClick={() => {
              void openAppNotificationSettings();
            }}
          >
            Turn on in iOS Settings
          </button>
        </div>
      ) : null}
    </>
  );
}

// ─── Hey Siri reference (iOS-native only, cycle 226) ──────────────────────
//
// Presentational list of the six already-registered Siri phrases + a button
// to open the Shortcuts app. It does NOT call, modify, donate, or
// re-register any intent — the intents auto-donate via the cycle-41
// AppShortcutsProvider.

const SIRI_PHRASES: string[] = [
  "log insulin",
  "what's my glucose?",
  "log carbs",
  "log a blood sugar",
  "log exercise",
  "snooze my alarm",
];

function HeySiriCard() {
  return (
    <>
      <Header
        title="Hey Siri"
        sub="Say any of these to Siri — they work even when the app is closed."
      />
      <div className={styles.deviceList}>
        {SIRI_PHRASES.map((phrase) => (
          <div key={phrase} className={styles.deviceCard}>
            <div className={styles.deviceName}>“Hey Siri, {phrase}”</div>
          </div>
        ))}
      </div>
      {/* Only show the deep-link when the native plugin is actually present
          in the installed build — otherwise the tap is a no-op. */}
      {notificationPluginAvailable() ? (
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.btnSecondary}
            style={{ minHeight: 44 }}
            onClick={() => {
              void openShortcutsApp();
            }}
          >
            Open Shortcuts
          </button>
        </div>
      ) : null}
    </>
  );
}

// ─── Alarm reliability (Android-native only, cycle 303) ───────────────────
//
// Android phones can silently drop or delay a background alarm unless three
// system settings are granted: full-screen intent (Android 14+, wakes the
// screen), battery-optimization exemption (immediate delivery while dozing),
// and Notification Policy Access (makes the alarm channel's DND bypass real).
// This card reads each state over the native AlarmReliability plugin and, for
// any that's not granted, offers a button opening the matching system screen.
// Read-only + open-a-screen only; no network, no PHI. Refreshes on mount and
// on foreground so returning from a system screen updates the badges. Renders
// ONLY on the Android standard APK carrying the plugin (never web / iOS /
// kiosk). When a permission is missing, delivery still uses the best available
// path (a loud heads-up) — this only upgrades it.
function AlarmReliabilityCard() {
  const [status, setStatus] = useState<AlarmReliabilityStatus | null>(null);

  const refresh = useCallback(() => {
    void getAlarmReliabilityStatus().then((s) => {
      if (s != null) setStatus(s);
    });
  }, []);

  useEffect(() => {
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  const items: {
    key: string;
    label: string;
    granted: boolean;
    open: () => void;
  }[] = [
    {
      key: "fsi",
      label: "Wake the screen for urgent alarms",
      // Optimistic default while the first read is in flight; native
      // getStatus resolves within a few ms and flips it if needed.
      granted: status?.fullScreenIntent ?? true,
      open: () => void openFullScreenIntentSettings(),
    },
    {
      key: "battery",
      label: "Ignore battery optimization",
      granted: status?.batteryOptimizationsIgnored ?? true,
      open: () => void openBatteryOptimizationSettings(),
    },
    {
      key: "dnd",
      label: "Break through Do Not Disturb",
      granted: status?.dndPolicyAccess ?? false,
      open: () => void openDndPolicySettings(),
    },
  ];

  return (
    <>
      <Header
        title="Alarm reliability"
        sub="Grant these so a glucose alarm always sounds, even with the screen off, in Do Not Disturb, or on a sleeping phone."
      />
      {items.map((it) => (
        <Row key={it.key} label={it.label}>
          {it.granted ? (
            <Pill tone="ok">Granted</Pill>
          ) : (
            <button
              type="button"
              className={styles.btnPrimary}
              style={{ minHeight: 44 }}
              onClick={it.open}
            >
              Set up
            </button>
          )}
        </Row>
      ))}
    </>
  );
}

// ─── Native app push (cycle 28) ──────────────────────────────────────

function NativeAppPushSection({
  nativePush,
  nav,
}: {
  nativePush: ReturnType<typeof useNativePush>;
  nav: ReturnType<typeof useNavigate>;
}) {
  const { confirm } = useConfirm();
  const [busy, setBusy] = useState<
    null | "enable" | "disable" | string
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const serverNotConfigured =
    !nativePush.loading && !nativePush.apnsEnabled && !nativePush.fcmEnabled;

  const onEnable = async () => {
    setActionError(null);
    setBusy("enable");
    try {
      await nativePush.enable({
        onAlertId: (id) =>
          nav(`/alerts?id=${encodeURIComponent(id)}`),
      });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
    setBusy(null);
  };
  const onDisable = async () => {
    setActionError(null);
    setBusy("disable");
    try {
      await nativePush.disable();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
    setBusy(null);
  };
  const onRemove = async (id: string) => {
    if (!(await confirm({ message: "Remove this device's native push token?", destructive: true }))) return;
    setActionError(null);
    setBusy(`remove:${id}`);
    try {
      await nativePush.remove(id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    }
    setBusy(null);
  };

  return (
    <>
      <Header
        title="Native app push"
        sub="OS-level banner via APNS (iOS) or FCM (Android). Independent of the Web Push rail above."
      />

      {serverNotConfigured ? (
        <div
          className={styles.subtleHint}
          style={{
            color: "var(--zone-warn)",
            background: "var(--zone-warn-soft)",
            border: "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
          }}
        >
          Native push is not configured on the server. Ask the
          operator to set <code>APNS_*</code> and/or <code>FCM_*</code>
          secrets and redeploy.
        </div>
      ) : null}

      {actionError ? (
        <div
          className={styles.subtleHint}
          style={{
            color: "var(--zone-urgent)",
            background: "var(--zone-urgent-soft)",
            border: "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
          }}
        >
          {actionError}
        </div>
      ) : null}

      <Row label="This device">
        <Pill tone={nativePush.locallyEnabled ? "ok" : "neutral"}>
          {nativePush.locallyEnabled ? "Enabled" : "Not enabled"}
        </Pill>
      </Row>
      <div className={styles.actionRow}>
        {nativePush.locallyEnabled ? (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onDisable}
            disabled={busy != null}
          >
            {busy === "disable" ? "Disabling…" : "Disable on this device"}
          </button>
        ) : (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onEnable}
            disabled={busy != null || serverNotConfigured}
          >
            {busy === "enable" ? "Enabling…" : "Enable on this device"}
          </button>
        )}
      </div>

      <Header title="Devices with native tokens" />
      {nativePush.loading ? (
        <div className={styles.subtleHint}>Loading…</div>
      ) : nativePush.tokens.length === 0 ? (
        <div className={styles.subtleHint}>No native push tokens yet.</div>
      ) : (
        <div className={styles.deviceList}>
          {nativePush.tokens.map((t) => {
            return (
              <div key={t.id} className={styles.deviceCard}>
                <div className={styles.deviceMeta}>
                  <div className={styles.deviceName}>
                    {t.platform === "ios" ? "iOS" : "Android"}
                    {" · "}
                    {shortDeviceLabel(t.user_agent)}
                    {t.disabled_at != null ? (
                      <span style={{ marginLeft: 6 }}>
                        <Pill tone="warn">disabled</Pill>
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.deviceSub}>
                    added {fmtTime(t.created_at)}
                  </div>
                </div>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => onRemove(t.id)}
                  disabled={busy === `remove:${t.id}`}
                >
                  {busy === `remove:${t.id}` ? "Removing…" : "Remove"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ─── Test alarms (cycle 36: five-button preview) ──────────────────────
//
// Replaces the cycle-15 / cycle-29 sequencer. Each button fires a
// 5-second synthetic preview against `alarmPreview.ts`; the actual
// overlay is rendered by `<PreviewOverlay />` mounted in App.tsx.
// This component is purely the trigger surface + the per-button
// "Playing… Ns" countdown.

const PREVIEW_ZONES: {
  zone: PreviewZone;
  label: string;
  semanticZone: "urgent_low" | "low" | "in_range" | "high" | "urgent_high";
}[] = [
  { zone: "urgent_low", label: "Urgent Low", semanticZone: "urgent_low" },
  { zone: "low", label: "Low", semanticZone: "low" },
  { zone: "in_range", label: "In Range", semanticZone: "in_range" },
  { zone: "high", label: "High", semanticZone: "high" },
  { zone: "urgent_high", label: "Urgent High", semanticZone: "urgent_high" },
];

// ─── Emergency contacts / SMS (cycle 59) ─────────────────────────────
//
// Lives inside the Alerts pane, below Notifications. Lists every
// emergency contact for the active member, with row-level edit /
// delete / enable toggle and an inline add+verify flow. The server's
// list response carries `sms_enabled` (the operator-configured flag);
// when false, we render the same style of "not configured on the
// server" hint the Notifications subsection uses for missing VAPID
// keys. Contacts are texted on lows + urgent lows (per their notify
// levels) — never suppressed by time of day (quiet hours were removed
// in cycle 216).

const NOTIFY_LEVELS: { value: EmergencyContactLevel; label: string }[] = [
  { value: "urgent_low", label: "Urgent low" },
  { value: "low", label: "Low" },
];

// Cycle 161.1: normalize a human-typed phone number into E.164 so the
// server's `^\+[1-9]\d{6,14}$` validator never bounces a normally-
// formatted number. "+1 (507) 555-1234", "507-555-1234", and
// "15075551234" all → "+15075551234"; a bare 10-digit number assumes
// the US +1 country code (the field is also pre-seeded with "+1 ").
function normalizePhoneE164(raw: string): string {
  const trimmed = raw.trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return `+${digits}`;
}
const E164_RE = /^\+[1-9]\d{6,14}$/;

// Cycle 388: order-insensitive notify-level comparison, so the inline row
// editor only PATCHes `notify_levels` when the SET actually changed (the
// chip row can reorder them without that counting as an edit).
function sameLevels(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b);
  return a.every((v) => set.has(v));
}

function maskPhone(phone: string): string {
  // "+15075551234" → "•••-•••-1234". The full number lives on the row
  // + at Twilio; the masked render keeps a roomful of guests from
  // memorizing the operator's caregiver's phone number from across
  // the kitchen.
  const tail = phone.replace(/\D/g, "").slice(-4) || phone.slice(-4);
  return `•••-•••-${tail}`;
}

// Cycle 129: notify-level → labeled chip, reusing the existing
// glucose-zone / alert-level tokens so urgent/low read identically to
// the chart + alarm UI.
const EC_LEVEL_META: Record<string, { label: string; color: string }> = {
  urgent_low: { label: "Urgent low", color: "var(--zone-urgent)" },
  low: { label: "Low", color: "var(--zone-warn)" },
  high: { label: "High", color: "var(--zone-warn)" },
  urgent_high: { label: "Urgent high", color: "var(--zone-urgent)" },
};

// ─── Cycle 403: who a contact's phone belongs to ──────────────────────
//
// A row of this list is one household ACCOUNT a contact may be linked to.
// The list is a CONVENIENCE ONLY: the server re-derives the household from
// `member_audience` and refuses anything outside it with a 404 that is
// byte-identical to a nonexistent user id, so a stale or wrong list here
// can produce a refusal but never a bad link.
interface LinkCandidate {
  user_id: string;
  display_name: string;
}

function EmergencyContactsSection({
  variant,
  selfUserId,
  selfDisplayName,
}: {
  variant?: FrameVariant;
  selfUserId?: string | null;
  selfDisplayName?: string | null;
}) {
  const { confirm } = useConfirm();
  // Cycle 403: the KIOSK shows link state READ-ONLY. It is a shared
  // household surface on a counter, and a link is a suppression
  // capability — pointing a contact at an account decides whose phone
  // goes quiet during a call. That belongs on a signed-in personal
  // session, not on the tablet anyone in the house can pick up.
  const linkReadOnly = variant === "kiosk";
  const [linkCandidates, setLinkCandidates] = useState<LinkCandidate[]>([]);
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);
  // Cycle 128: operator-configured voice-call rail status (banner gate).
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [busy, setBusy] = useState<string | null>(null); // key like "add" / `delete:${id}` / `start:${id}`
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Cycle 388: inline row editor. `editingId` is the contact whose row is
  // currently in edit mode (at most one at a time); the three `edit*`
  // fields are seeded from that row when the pencil is pressed and are
  // the ONLY things the editor can change — verification state, call
  // order, and the two rail toggles all keep their own dedicated paths.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState<string>("");
  const [editPhone, setEditPhone] = useState<string>("");
  const [editLevels, setEditLevels] = useState<EmergencyContactLevel[]>([]);
  // Cycle 132: contact rows are collapsed by default; this tracks which
  // are expanded.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    const wasOpen = expandedIds.has(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Cycle 388: collapsing a row abandons its edit — the editor lives in
    // the expanded body, so leaving `editingId` set would re-open it later
    // still holding values the user walked away from.
    if (wasOpen) setEditingId((cur) => (cur === id ? null : cur));
  };

  // "Add contact" inline editor state.
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [addLabel, setAddLabel] = useState<string>("");
  const [addPhone, setAddPhone] = useState<string>("");
  // Cycle 132: notify levels start UNSELECTED in the add editor (the
  // operator must explicitly pick at least one).
  const [addLevels, setAddLevels] = useState<EmergencyContactLevel[]>([]);
  // Cycle 94 (A2P CTA compliance): the owner must attest they have the
  // contact's permission before we collect the number. This is the
  // point-of-collection consent the 10DLC campaign CTA is verified
  // against — see consent.sugalarm.com "What the opt-in looks like".
  const [addConsent, setAddConsent] = useState<boolean>(false);

  // Cycle 89: resend cooldown for the "reply YES" confirmation text.
  const [verifyCooldown, setVerifyCooldown] = useState<number>(0);

  const reload = useCallback(async () => {
    const r = await emergencyContactsApi.list();
    if (!r.ok) {
      setError(r.error.message);
      setLoading(false);
      return;
    }
    setContacts(r.data.contacts);
    setSmsEnabled(r.data.sms_enabled);
    setVoiceEnabled(r.data.voice_enabled);
    setLoading(false);
  }, []);

  // Cycle 457: a picture change anywhere in the household (this device or
  // another) re-fetches the list, so every row shows the new picture.
  const rosterVersion = useHeroRosterVersion(null);
  useEffect(() => {
    void reload();
  }, [reload, rosterVersion]);

  // Cycle 457: the hero whose "[Name]'s picture" is open, in place of the
  // list (in flow — never an overlay).
  const [pictureFor, setPictureFor] = useState<string | null>(null);

  // ─── Cycle 403: load the household accounts a contact may point at ──
  //
  // Derived from the contacts themselves (`contacts[0].member_id`) rather
  // than by threading the active member down: with no contacts there is
  // nothing to link, so the id is available exactly when it is needed.
  //
  // `listGrants` is OWNER-ONLY server-side, and that is used as the
  // signal rather than fought: if it succeeds the caller owns this member
  // and the owner (them) plus every grant holder are candidates; if it
  // fails the caller is a grant holder, and they simply get no picker and
  // the read-only state line. Nothing is retried and no error is
  // surfaced — an absent picker is not a fault worth a banner in a pane
  // about emergency contacts.
  const linkMemberId = contacts[0]?.member_id ?? null;
  useEffect(() => {
    if (linkReadOnly || !linkMemberId || !selfUserId) {
      setLinkCandidates([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const r = await memberLinksApi.listGrants(linkMemberId);
      if (cancelled) return;
      if (!r.ok) {
        setLinkCandidates([]);
        return;
      }
      const grants = r.data.links
        .filter((g) => g.user_id !== selfUserId)
        .map((g) => ({
          user_id: g.user_id,
          display_name: g.display_name || g.email || "Household account",
        }));
      setLinkCandidates([
        { user_id: selfUserId, display_name: selfDisplayName || "You" },
        ...grants,
      ]);
    })();
    return () => {
      cancelled = true;
    };
  }, [linkMemberId, linkReadOnly, selfUserId, selfDisplayName]);

  const linkNameFor = (userId: string | null | undefined): string | null => {
    if (!userId) return null;
    const hit = linkCandidates.find((c) => c.user_id === userId);
    // A link to somebody this caller cannot enumerate still renders — as
    // "a household account". Showing nothing would make an ACTIVE
    // suppression capability invisible to the person looking straight at
    // the row, which is worse than a vague name.
    return hit ? hit.display_name : "a household account";
  };

  const onSetLink = async (
    c: EmergencyContact,
    userId: string | null,
  ) => {
    clearMsgs();
    setBusy(`link:${c.id}`);
    const r = await emergencyContactsApi.update(c.id, {
      linked_user_id: userId,
    });
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    await reload();
  };

  // Cycle 127: auto-refresh while a contact is awaiting its inbound-YES
  // opt-in. The contact is verified server-side by `handle_inbound` with
  // no live push (there is no WS frame for emergency-contact
  // verification), so without this the pane would keep showing
  // "pending — waiting for reply" until the user navigated away and back.
  // Poll every 5s ONLY while something is pending (self-limiting — the
  // interval clears the moment the last pending contact flips to
  // verified), and refresh on tab/app focus so returning from the
  // Messages app after replying YES updates immediately.
  const hasPendingOptin = contacts.some(
    (c) => !c.verified && c.awaiting_optin,
  );
  useEffect(() => {
    if (!hasPendingOptin) return;
    const id = window.setInterval(() => void reload(), 5000);
    const onFocus = () => {
      if (document.visibilityState !== "hidden") void reload();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [hasPendingOptin, reload]);

  const clearMsgs = () => {
    setError(null);
    setInfo(null);
  };

  const onAdd = async () => {
    clearMsgs();
    // Cycle 161.1: normalize whatever the user typed ("507-555-1234",
    // "(507) 555 1234", "+1 …") into E.164 before it reaches the wire —
    // a formatted number must never bounce with a validation error. The
    // bare "+1 " seed counts as empty.
    const phone = normalizePhoneE164(addPhone);
    const label = addLabel.trim();
    // The untouched "+1 " seed normalizes to bare "+1" — still empty.
    if (!label || !phone || phone === "+1") {
      setError("Label and phone are both required.");
      return;
    }
    if (!E164_RE.test(phone)) {
      setError(
        "That phone number doesn't look right — include the area code, e.g. +1 (507) 555-1234.",
      );
      return;
    }
    if (addLevels.length === 0) {
      setError("Pick at least one alert level.");
      return;
    }
    if (!addConsent) {
      setError(
        "Please confirm you have this person's permission before adding them.",
      );
      return;
    }
    const body: EmergencyContactCreateIn = {
      label,
      phone_e164: phone,
      notify_levels: addLevels,
      sms_enabled: true,
    };
    setBusy("add");
    const r = await emergencyContactsApi.create(body);
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setAddLabel("");
    setAddPhone("");
    setAddLevels([]);
    setAddConsent(false);
    setShowAdd(false);
    await reload();
  };

  // ── Cycle 388: inline edit (name / phone / alert levels) ─────────────
  //
  // The row already had `sms_enabled` / `voice_enabled` toggles and a
  // delete, but no way to fix a typo'd name or a changed number short of
  // deleting and re-adding (which loses the call order and forces a fresh
  // opt-in). PATCH /api/emergency-contacts/{id} has accepted all three of
  // these fields since cycle 59 — this just gives them a UI.
  const onStartEdit = (c: EmergencyContact) => {
    clearMsgs();
    setEditingId(c.id);
    setEditLabel(c.label);
    setEditPhone(c.phone_e164);
    setEditLevels([...(c.notify_levels ?? [])]);
    // Editing lives in the expanded body, so make sure it's open.
    setExpandedIds((prev) => new Set(prev).add(c.id));
  };

  const onCancelEdit = () => {
    setEditingId(null);
    clearMsgs();
  };

  const onSaveEdit = async (c: EmergencyContact) => {
    clearMsgs();
    const label = editLabel.trim();
    // Same normalize-then-validate path the add editor uses, so a
    // human-typed "507-555-1234" never bounces off the server's E.164
    // validator.
    const phone = normalizePhoneE164(editPhone);
    if (!label || !phone || phone === "+1") {
      setError("Name and phone are both required.");
      return;
    }
    if (!E164_RE.test(phone)) {
      setError(
        "That phone number doesn't look right — include the area code, e.g. +1 (507) 555-1234.",
      );
      return;
    }
    if (editLevels.length === 0) {
      setError("Pick at least one alert level.");
      return;
    }

    const phoneChanged = phone !== c.phone_e164;

    // One number per member is a DB uniqueness rule, so moving this hero
    // onto a number another hero already holds is a 409. Catch it here to
    // name the other hero — the server's `phone_already_in_use` can't.
    if (phoneChanged) {
      const clash = contacts.find(
        (o) => o.id !== c.id && o.phone_e164 === phone,
      );
      if (clash) {
        setError(`${clash.label} already uses that number.`);
        return;
      }
    }

    // A changed number is re-verified from scratch server-side (it clears
    // verified_at + the opt-in state), which means this hero stops being
    // texted or called until they reply YES again. That is a real gap in
    // the alarm path, so it gets an explicit confirm rather than a
    // surprise.
    if (phoneChanged && (c.verified || c.awaiting_optin)) {
      const ok = await confirm({
        message: `Change ${c.label}'s number? They'll need to reply YES to a new confirmation text before Sugar Alarm can text or call them again.`,
      });
      if (!ok) return;
    }

    const body: EmergencyContactUpdateIn = {};
    if (label !== c.label) body.label = label;
    if (phoneChanged) body.phone_e164 = phone;
    if (!sameLevels(editLevels, c.notify_levels ?? [])) {
      body.notify_levels = editLevels;
    }
    // The server rejects an empty PATCH (422) — nothing changed, so just
    // close the editor.
    if (Object.keys(body).length === 0) {
      setEditingId(null);
      return;
    }

    setBusy(`edit:${c.id}`);
    const r = await emergencyContactsApi.update(c.id, body);
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setEditingId(null);
    if (phoneChanged) {
      setInfo(
        "Number updated. Send them an opt-in text so they can reply YES.",
      );
    }
    await reload();
  };

  const onToggleEnabled = async (c: EmergencyContact) => {
    clearMsgs();
    setBusy(`toggle:${c.id}`);
    const r = await emergencyContactsApi.update(c.id, {
      sms_enabled: !c.sms_enabled,
    });
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    await reload();
  };

  const onDelete = async (c: EmergencyContact) => {
    if (!(await confirm({ message: `Remove ${c.label}?`, destructive: true }))) return;
    clearMsgs();
    setBusy(`delete:${c.id}`);
    const r = await emergencyContactsApi.remove(c.id);
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    if (editingId === c.id) setEditingId(null);
    await reload();
  };

  const onStartVerify = async (id: string) => {
    clearMsgs();
    setVerifyCooldown(0);
    setBusy(`start:${id}`);
    const r = await emergencyContactsApi.verifyStart(id);
    setBusy(null);
    if (!r.ok) {
      if (r.error.kind === "rate_limit") {
        const det = r.error.details as { cooldown_seconds?: number } | null;
        const cooldown =
          det && typeof det.cooldown_seconds === "number"
            ? det.cooldown_seconds
            : 60;
        setVerifyCooldown(cooldown);
        setError(`Wait ${cooldown}s before resending the confirmation.`);
        return;
      }
      setError(r.error.message);
      return;
    }
    // Cycle 89: the contact is verified by their inbound YES, not a code.
    setInfo(
      r.data.sent
        ? "Confirmation sent. They'll be verified once they reply YES."
        : "A confirmation was already sent.",
    );
    await reload();
  };

  const onSendTest = async (id: string) => {
    clearMsgs();
    setBusy(`test:${id}`);
    const r = await emergencyContactsApi.sendTest(id);
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setInfo(r.data.sent ? "Test SMS sent." : "Test failed to send.");
  };

  // Cycle 128: voice opt-in toggle, ordered-list reorder, and test call.
  const onToggleVoice = async (c: EmergencyContact) => {
    clearMsgs();
    setBusy(`voice:${c.id}`);
    const r = await emergencyContactsApi.update(c.id, {
      voice_enabled: !c.voice_enabled,
    });
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    await reload();
  };

  const onMove = async (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= contacts.length) return;
    const ids = contacts.map((c) => c.id);
    const a = ids[index];
    const b = ids[j];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[j] = a;
    clearMsgs();
    setBusy("reorder");
    const r = await emergencyContactsApi.reorder(ids);
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setContacts(r.data.contacts);
    setVoiceEnabled(r.data.voice_enabled);
  };

  const onVoiceTest = async (id: string) => {
    clearMsgs();
    setBusy(`voicetest:${id}`);
    const r = await emergencyContactsApi.voiceTest(id);
    setBusy(null);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setInfo(r.data.placed ? "Test call placed." : "Test call failed.");
  };

  const pictureHero = pictureFor ? contacts.find((c) => c.id === pictureFor) : undefined;
  if (pictureHero) {
    return (
      <HeroPictureChooser
        hero={pictureHero}
        variant={variant === "kiosk" ? "kiosk" : variant === "mobile" ? "mobile" : "web"}
        onClose={() => setPictureFor(null)}
        onSubmitted={(pending) => {
          // Collapse straight back to the list; the result lands on it.
          const label = pictureHero.label;
          setPictureFor(null);
          setError(null);
          setInfo(`Saving ${label}'s picture…`);
          void pending.then((line) => {
            if (line) {
              setInfo(null);
              setError(`${label}'s picture wasn't saved. ${line}`);
              return;
            }
            setInfo(`Updated ${label}'s picture.`);
            void reload();
          });
        }}
      />
    );
  }

  return (
    <>
      {!smsEnabled ? (
        <div
          className={styles.subtleHint}
          style={{
            color: "var(--zone-warn)",
            background: "var(--zone-warn-soft)",
            border: "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
          }}
        >
          SMS alerts are not configured on the server. Ask the operator
          to set the three <code>TWILIO_*</code> secrets in{" "}
          <code>fly secrets</code> / <code>.env</code>. Adding contacts
          here still works; they won't receive any texts until the rail
          is on.
        </div>
      ) : null}

      <div className={styles.subtleHint}>
        Contacts are texted on <strong>lows</strong> and{" "}
        <strong>urgent lows</strong> (per each contact's notify levels),
        at any time of day.
      </div>

      {/* Cycle 128: voice-call rail status + the ordered-call explainer. */}
      {!voiceEnabled ? (
        <div
          className={styles.subtleHint}
          style={{
            color: "var(--zone-warn)",
            background: "var(--zone-warn-soft)",
            border: "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
          }}
        >
          Voice calls are not configured on the server. Ask the operator
          to set <code>TWILIO_VOICE_FROM_NUMBER</code> (a voice-capable
          number). You can still set the call order and opt contacts in;
          no calls are placed until the rail is on.
        </div>
      ) : null}

      <div className={styles.subtleHint}>
        On an <strong>urgent low</strong>, Sugar Alarm <strong>calls</strong>{" "}
        voice-enabled contacts <strong>in order</strong>, moving to the
        next if the current one doesn't answer and press 1 to confirm.
      </div>

      {error ? (
        <div
          className={styles.subtleHint}
          style={{ color: "var(--zone-urgent)" }}
        >
          {error}
        </div>
      ) : null}
      {info ? (
        <div
          className={styles.subtleHint}
          style={{ color: "var(--accent)" }}
        >
          {info}
        </div>
      ) : null}

      {loading ? (
        <div className={styles.subtleHint}>Loading…</div>
      ) : contacts.length === 0 ? (
        <div className={styles.subtleHint}>No emergency contacts yet.</div>
      ) : (
        <div className={styles.deviceList}>
          {contacts.map((c, index) => {
            // Cycle 89: three states — verified / awaiting the inbound YES
            // / not yet sent.
            const isLast = index === contacts.length - 1;
            const levels = c.notify_levels || [];
            const expanded = expandedIds.has(c.id);
            // Cycle 388: at most one row is in edit mode at a time.
            const editing = editingId === c.id;
            return (
              <div key={c.id} className={styles.ecCard}>
                {/* Cycle 457: the hero's picture (photo, then preset, then
                    monogram) is its own button: it opens "[Name]'s
                    picture" — editable for the hero's own account and the
                    household admin, read-only for everyone else. */}
                <div className={styles.ecHeaderRow}>
                <button
                  type="button"
                  className={styles.ecAvatarButton}
                  onClick={() => setPictureFor(c.id)}
                  aria-label={
                    c.can_change_picture
                      ? `Change ${c.label}'s picture`
                      : `See ${c.label}'s picture`
                  }
                  data-testid="hero-row-avatar"
                >
                  <HeroAvatar
                    preset={c.avatar_preset}
                    photo={c.avatar_photo ?? null}
                    name={c.label}
                    size={48}
                  />
                </button>
                {/* Cycle 132: collapsed by default — the whole header row
                    toggles expand. Collapsed shows only #N + identity +
                    chips + chevron; everything else is in the body. */}
                <button
                  type="button"
                  className={styles.ecHeader}
                  onClick={() => toggleExpanded(c.id)}
                  aria-expanded={expanded}
                >
                  <span className={styles.ecOrderBadge}>#{index + 1}</span>
                  <div className={styles.ecIdentity}>
                    <div className={styles.ecNameRow}>
                      <span className={styles.ecName}>{c.label}</span>
                      {c.verified ? (
                        <Pill tone="ok">Verified</Pill>
                      ) : c.awaiting_optin ? (
                        <Pill tone="warn">pending — waiting for reply</Pill>
                      ) : (
                        <Pill tone="warn">unverified</Pill>
                      )}
                    </div>
                    <div className={styles.ecPhone}>
                      {maskPhone(c.phone_e164)}
                    </div>
                    <div className={styles.ecChips}>
                      {levels.length > 0 ? (
                        levels.map((lv) => {
                          const meta = EC_LEVEL_META[lv];
                          return (
                            <span
                              key={lv}
                              className={styles.ecChip}
                              style={
                                {
                                  ["--ec-chip" as string]:
                                    meta?.color ?? "var(--ink-3)",
                                } as React.CSSProperties
                              }
                            >
                              {meta?.label ?? lv}
                            </span>
                          );
                        })
                      ) : (
                        <span className={styles.deviceSub}>no levels</span>
                      )}
                    </div>
                  </div>
                  <svg
                    className={`${styles.ecChevron} ${
                      expanded ? styles.ecChevronOpen : ""
                    }`}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 4 L10 8 L6 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                </div>

                {!expanded ? null : (
                  <div className={styles.ecExpanded}>
                    {editing ? (
                      /* Cycle 388: inline editor — name, number, and the
                         alert levels this hero is paged on. Everything else
                         (call order, the two rail toggles, opt-in, tests,
                         delete) keeps its own dedicated control and is
                         hidden while editing, so there is exactly one way
                         out of this state: Save or Cancel. */
                      <div className={styles.ecEditor}>
                        <Field label="Name">
                          <input
                            type="text"
                            className={inputClass}
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            placeholder="Mom"
                            maxLength={120}
                          />
                        </Field>
                        <Field label="Phone">
                          <input
                            type="tel"
                            inputMode="tel"
                            className={inputClass}
                            value={editPhone}
                            onChange={(e) =>
                              // Permissive while typing; normalizePhoneE164
                              // squares it to +E.164 on save.
                              setEditPhone(
                                e.target.value.replace(/[^0-9+()\-\s.]/g, ""),
                              )
                            }
                            placeholder="+1 (507) 555-1234"
                            maxLength={20}
                          />
                        </Field>
                        <div>
                          <div className={styles.ecEditorLabel}>
                            Alert levels
                          </div>
                          <div className={styles.ecEditorChips}>
                            {NOTIFY_LEVELS.map((nl) => {
                              const on = editLevels.includes(nl.value);
                              return (
                                <button
                                  key={nl.value}
                                  type="button"
                                  aria-pressed={on}
                                  className={
                                    on ? styles.btnPrimary : styles.btnSecondary
                                  }
                                  onClick={() =>
                                    setEditLevels((prev) =>
                                      prev.includes(nl.value)
                                        ? prev.filter((v) => v !== nl.value)
                                        : [...prev, nl.value],
                                    )
                                  }
                                >
                                  {on ? `✓ ${nl.label}` : nl.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {editLevels.length === 0 ? (
                          <div className={styles.subtleHint}>
                            Pick at least one alert level (Urgent low and/or
                            Low).
                          </div>
                        ) : null}
                        {/* A changed number drops them out of the alarm path
                            until they opt in again — say so BEFORE saving,
                            not after. */}
                        {normalizePhoneE164(editPhone) !== c.phone_e164 &&
                        (c.verified || c.awaiting_optin) ? (
                          <div
                            className={styles.subtleHint}
                            style={{ color: "var(--zone-warn)" }}
                          >
                            Changing the number resets their opt-in — they'll
                            need to reply <strong>YES</strong> to a new
                            confirmation text before Sugar Alarm can text or call
                            them.
                          </div>
                        ) : null}
                        <div className={styles.ecActions}>
                          <button
                            type="button"
                            className={styles.btnPrimary}
                            onClick={() => onSaveEdit(c)}
                            disabled={
                              busy === `edit:${c.id}` || editLevels.length === 0
                            }
                          >
                            {busy === `edit:${c.id}` ? "Saving…" : "Save"}
                          </button>
                          <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={onCancelEdit}
                            disabled={busy === `edit:${c.id}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                    {/* Cycle 128: reorder arrows (now in the expanded body). */}
                    <div className={styles.ecReorderRow}>
                      <span className={styles.ecReorderLabel}>Call order</span>
                      <button
                        type="button"
                        className={styles.ecOrderBtn}
                        onClick={() => onMove(index, -1)}
                        disabled={index === 0 || busy === "reorder"}
                        aria-label={`Move ${c.label} up`}
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        className={styles.ecOrderBtn}
                        onClick={() => onMove(index, 1)}
                        disabled={isLast || busy === "reorder"}
                        aria-label={`Move ${c.label} down`}
                      >
                        ▼
                      </button>
                    </div>
                    {!c.verified && c.awaiting_optin ? (
                      <div className={styles.deviceSub}>
                        A confirmation text was sent — they'll be verified once
                        they reply <strong>YES</strong>.
                      </div>
                    ) : null}

                {/* ─── Cycle 403: whose phone is this? ─────────────────
                    The state line ALWAYS renders when a link exists, on
                    every surface including the kiosk, because a link is a
                    suppression capability and an invisible one is the
                    worst kind. Only the CONTROL is withheld on the kiosk.

                    ⚠ IT SAYS WHAT IT DOES, NOT JUST THAT IT EXISTS. "This
                    is Jean's account" alone would read as a label; the
                    hint names the actual effect, because somebody
                    deciding whether to set this is deciding whose phone
                    goes quiet during a call. */}
                <div className={styles.deviceSub} style={{ marginTop: 6 }}>
                  {c.linked_user_id ? (
                    <>
                      This is <strong>{linkNameFor(c.linked_user_id)}</strong>
                      's account — while we're on the phone with them, their
                      own phone stops repeating the alert and goes quiet.
                      The kiosk stays loud.
                    </>
                  ) : (
                    <>Not linked to an account.</>
                  )}
                </div>
                {!linkReadOnly && linkCandidates.length > 0 ? (
                  <div className={styles.ecToggles}>
                    <label className={styles.ecToggleText}>
                      <span className={styles.ecToggleLabel}>
                        Whose phone is this?
                      </span>
                      <select
                        className={inputClass}
                        value={c.linked_user_id ?? ""}
                        disabled={busy === `link:${c.id}`}
                        onChange={(e) =>
                          void onSetLink(c, e.target.value || null)
                        }
                        aria-label={`Account linked to ${c.label}`}
                      >
                        <option value="">Nobody's — don't link</option>
                        {linkCandidates.map((cand) => (
                          <option key={cand.user_id} value={cand.user_id}>
                            {cand.display_name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : null}

                {/* Cycle 129: labeled toggle switches replace the cryptic
                    Disable / "Calling: on" buttons. */}
                <div className={styles.ecToggles}>
                  <div className={styles.ecToggle}>
                    <Toggle
                      on={c.sms_enabled}
                      onClick={() => onToggleEnabled(c)}
                    />
                    <div className={styles.ecToggleText}>
                      <span className={styles.ecToggleLabel}>Text alerts</span>
                      <span className={styles.ecToggleHint}>
                        SMS on low + urgent low
                      </span>
                    </div>
                  </div>
                  <div className={styles.ecToggle}>
                    <Toggle
                      on={c.voice_enabled}
                      onClick={() => {
                        if (c.verified) onToggleVoice(c);
                      }}
                    />
                    <div className={styles.ecToggleText}>
                      <span className={styles.ecToggleLabel}>Voice calls</span>
                      <span className={styles.ecToggleHint}>
                        {c.verified
                          ? "Call on low + urgent low"
                          : "Verify the contact first"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Cycle 129: explicit-label actions; delete separated to
                    the right edge, away from the safe actions.
                    Cycle 388: Edit leads the row — it's the safe, most-used
                    action, and it sits as far from Delete as the row allows. */}
                <div className={styles.ecActions}>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => onStartEdit(c)}
                    aria-label={`Edit ${c.label}`}
                  >
                    ✏️ Edit
                  </button>
                  {!c.verified ? (
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={() => onStartVerify(c.id)}
                      disabled={
                        !smsEnabled ||
                        verifyCooldown > 0 ||
                        busy === `start:${c.id}`
                      }
                    >
                      {busy === `start:${c.id}`
                        ? "Sending…"
                        : c.awaiting_optin
                          ? "Resend opt-in text"
                          : "Send opt-in text"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={() => onSendTest(c.id)}
                        disabled={!smsEnabled || busy === `test:${c.id}`}
                      >
                        {busy === `test:${c.id}`
                          ? "Sending…"
                          : "Send test text"}
                      </button>
                      <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={() => onVoiceTest(c.id)}
                        disabled={
                          !voiceEnabled ||
                          !c.voice_enabled ||
                          busy === `voicetest:${c.id}`
                        }
                      >
                        {busy === `voicetest:${c.id}` ? "Calling…" : "Test call"}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className={`${styles.btnSecondary} ${styles.ecDangerBtn} ${styles.ecActionsSpacer}`}
                    onClick={() => onDelete(c)}
                    disabled={busy === `delete:${c.id}`}
                    aria-label={`Delete ${c.label}`}
                  >
                    {busy === `delete:${c.id}` ? "…" : "Delete contact"}
                  </button>
                </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAdd ? (
        <div
          className={styles.deviceCard}
          style={{ flexDirection: "column", gap: 8 }}
        >
          <Field label="Label">
            <input
              type="text"
              className={inputClass}
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              placeholder="Mom"
              maxLength={120}
            />
          </Field>
          <Field label="Phone">
            <input
              type="tel"
              inputMode="tel"
              className={inputClass}
              value={addPhone}
              onChange={(e) =>
                // Permissive while typing — digits + common formatting;
                // normalizePhoneE164 squares it to +E.164 on save.
                setAddPhone(e.target.value.replace(/[^0-9+()\-\s.]/g, ""))
              }
              placeholder="+1 (507) 555-1234"
              maxLength={20}
            />
          </Field>
          <div>
            <div
              style={{
                fontSize: "var(--type-floor)",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--ink-3)",
                marginBottom: 4,
              }}
            >
              Alert levels
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {NOTIFY_LEVELS.map((nl) => {
                const on = addLevels.includes(nl.value);
                return (
                  <button
                    key={nl.value}
                    type="button"
                    aria-pressed={on}
                    className={on ? styles.btnPrimary : styles.btnSecondary}
                    onClick={() => {
                      setAddLevels((prev) =>
                        prev.includes(nl.value)
                          ? prev.filter((v) => v !== nl.value)
                          : [...prev, nl.value],
                      );
                    }}
                  >
                    {/* Cycle 132: a visible checkmark on the selected level. */}
                    {on ? `✓ ${nl.label}` : nl.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Cycle 94: point-of-collection consent disclosure + attestation.
              This is the CTA the A2P 10DLC campaign is verified against;
              keep it in sync with the consent.sugalarm.com mockup. */}
          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              background: "var(--zone-warn-soft)",
              border: "0.5px solid var(--line)",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: "var(--type-floor)",
              lineHeight: 1.5,
              color: "var(--ink-2)",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={addConsent}
              onChange={(e) => setAddConsent(e.target.checked)}
              style={{ marginTop: 2, flex: "none" }}
            />
            <span>
              I confirm I have this person's permission to receive text
              alerts. Sugar Alarm will text them a one-time confirmation; they
              receive glucose alerts only after replying <strong>YES</strong>,
              and can reply <strong>STOP</strong> anytime. These are recurring
              automated health alerts — frequency varies; message &amp; data
              rates may apply. See our{" "}
              <a
                href="https://consent.sugalarm.com/privacy.html"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://consent.sugalarm.com/terms.html"
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>
              .
            </span>
          </label>
          {/* Cycle 132: require at least one notify level before saving. */}
          {addLevels.length === 0 ? (
            <div className={styles.subtleHint}>
              Pick at least one alert level (Urgent low and/or Low).
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={onAdd}
              disabled={busy === "add" || !addConsent || addLevels.length === 0}
            >
              {busy === "add" ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => {
                setShowAdd(false);
                setAddConsent(false);
                clearMsgs();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => {
              clearMsgs();
              // Cycle 161.1: seed the country code so a US number can be
              // typed straight through without tripping E.164 validation.
              setAddPhone((p) => (p.trim() === "" ? "+1 " : p));
              setShowAdd(true);
            }}
          >
            Add contact
          </button>
        </div>
      )}
    </>
  );
}

function TestAlarmsSubsection() {
  const preview = useAlarmPreview();
  const thresholds = useThresholds();
  // Tick once a second so the disabled button's `Playing… Ns` label
  // counts down in real time. Cheap — only mounts a timer when a
  // preview is active.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (preview.zone == null) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 250);
    return () => window.clearInterval(id);
  }, [preview.zone, preview.startedAt]);

  const secondsLeft = (() => {
    if (preview.zone == null) return 0;
    const remaining = PREVIEW_DURATION_MS - (Date.now() - preview.startedAt);
    return Math.max(0, Math.ceil(remaining / 1000));
  })();

  return (
    <>
      <div className={styles.previewBtnRow}>
        {PREVIEW_ZONES.map(({ zone, label, semanticZone }) => {
          const playing = preview.zone === zone;
          return (
            <button
              key={zone}
              type="button"
              onClick={() => {
                if (playing) {
                  cancelPreview();
                  return;
                }
                startPreview(zone, sgvForZone(zone, thresholds));
              }}
              className={`${styles.previewBtn} ${
                playing ? styles.previewBtnActive : ""
              }`}
              style={{
                background: zoneSoft(semanticZone),
                color: zoneColor(semanticZone),
                borderColor: zoneColor(semanticZone),
              }}
              aria-pressed={playing}
            >
              {playing ? (
                <span className={styles.previewBtnPlaying}>
                  <span>Playing…</span>
                  <span className="tnum">{secondsLeft}</span>
                </span>
              ) : (
                label
              )}
            </button>
          );
        })}
      </div>
      <div className={styles.subtleHint}>
        Plays the real alarm sound. Does not generate database alerts.
        Real alarms always interrupt previews.
      </div>
    </>
  );
}

// ─── Wi-Fi tab (cycle 224 — Android-tablet kiosk only) ─────────────────
// Status + a no-PIN "Change Wi-Fi network" button that opens the existing
// cycle-223 native device-owner connector ON DEMAND (works online or
// offline). Household-device posture: the only capability the no-PIN button
// grants is JOINING a network on a physically-present home device — it
// cannot exit the kiosk, reach Android Settings, silence alarms, or disable
// admin (the native connector is join-only and never leaves lock-task).
// Status is read from `navigator.onLine` (no probe; SSID is native-only and
// not shown). Graceful degradation: if the native bridge isn't present (the
// new kiosk APK isn't installed yet, or a non-kiosk build), the button is
// disabled with a short note — never a thrown error.
function WifiTabSection() {
  const bridge = kioskWifi.isAvailable();
  // Cycle 247 — native SSID + signal readout (Android-tablet kiosk APK only).
  const sig = useWifiSignal();
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  // Cycle 473 — "Join new networks: Ready / Not ready (why)". The APK's
  // readiness probe (six booleans/enum keys, never an SSID), re-read on
  // every mount of this tab and whenever connectivity flips. Null = no
  // bridge, or an APK older than cycle 473 with no `capability()` — then
  // the line is simply absent, never an error (graceful degradation).
  const [wifiCap, setWifiCap] = useState<kioskWifi.WifiCapability | null>(null);
  useEffect(() => {
    if (!bridge) return;
    let live = true;
    void kioskWifi.capability().then((c) => {
      if (live) setWifiCap(c);
    });
    return () => {
      live = false;
    };
  }, [bridge, online]);
  const readiness = wifiCap ? kioskWifi.wifiReadiness(wifiCap) : null;
  // Cycle 473 (safety-invariant-reviewer, F1): the on-demand connector is a
  // native overlay that would cover a SOUNDING alarm's controls, and the
  // native yield only sees a NEW alarm (the rising edge) — so, exactly like
  // the cycle-455 photo buttons, "Change Wi-Fi network" is disabled while an
  // alarm sounds on the kiosk (any level, the dark-feed alarm included).
  const wifiAlarmBlocked = usePhotoBlockedByAlarm();

  const statusRight = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        color: online ? "var(--zone-ok)" : "var(--zone-warn)",
      }}
    >
      <span
        className={`${styles.connDot} ${
          online ? styles.connDotOk : styles.connDotWarn
        }`}
        aria-hidden
      />
      {online ? "Online" : "Offline"}
    </span>
  );

  return (
    <div className={styles.cardStack}>
      <SubsectionCard
        title="Wi-Fi"
        subtitle="Connect this kiosk to a Wi-Fi network."
        icon={Icon.wifi}
        accent="cyan"
        headerRight={statusRight}
      >
        {bridge ? (
          <>
            {readiness ? (
              <Row label="Join new networks">
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    minWidth: 0,
                    textAlign: "right",
                    color: readiness.ready ? "var(--zone-ok)" : "var(--zone-warn)",
                  }}
                >
                  <span
                    className={`${styles.connDot} ${
                      readiness.ready ? styles.connDotOk : styles.connDotWarn
                    }`}
                    aria-hidden
                  />
                  <span>
                    {readiness.ready
                      ? "Ready"
                      : `Not ready · ${kioskWifi.readinessCopy(readiness.reason)}`}
                  </span>
                </span>
              </Row>
            ) : null}
            {sig.available ? (
              <Row label="Connected to">
                {sig.connected && sig.percent >= 0 ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 14,
                      color: "var(--ink)",
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {sig.ssid ?? "Wi-Fi"}
                    </span>
                    <WifiIndicator
                      connected={sig.connected}
                      level={sig.level}
                      percent={sig.percent}
                    />
                    <span className="tnum" style={{ color: "var(--ink-3)" }}>
                      {sig.percent}%
                    </span>
                  </span>
                ) : (
                  <span style={{ fontSize: 14, color: "var(--ink-3)" }}>
                    Not connected
                  </span>
                )}
              </Row>
            ) : null}
            <Row label="Network">
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={wifiAlarmBlocked}
                aria-disabled={wifiAlarmBlocked}
                onClick={() => {
                  if (photoBlockedByAlarm()) return; // the render the gate has not reached yet
                  void kioskWifi.openConnector();
                }}
              >
                Change Wi-Fi network
              </button>
            </Row>
            <div className={styles.subtleHint}>
              {wifiAlarmBlocked
                ? "Wi-Fi changes are available once the alarm is quiet."
                : "Opens the on-screen Wi-Fi picker — no PIN needed. Pick a " +
                  "network and type its password right here; the kiosk also opens " +
                  "this picker on its own whenever it loses connection."}
            </div>
          </>
        ) : (
          <div className={styles.subtleHint}>
            Changing Wi-Fi from here is available on the Android tablet kiosk.
            On this device, manage Wi-Fi the usual way.
          </div>
        )}
      </SubsectionCard>
    </div>
  );
}

// ─── CGM & devices (cycle 29: merged section) ─────────────────────────

function ConnectionSection({
  cgmStatus,
  activeMemberName,
  onDisconnect,
  onReconnect,
  devices,
  isDeviceSession,
  offersCode,
  onPairDevice,
  onForget,
  onPruneInactive,
  wifi,
  voice,
  onSetWakeWord,
  onRenameDevice,
  only,
  actionRows = false,
}: {
  cgmStatus: cgmApi.CGMStatus | null;
  activeMemberName: string;
  onDisconnect: () => void;
  onReconnect: () => void;
  devices: Device[];
  isDeviceSession: boolean;
  /** Cycle 221: true when this device OFFERS a pairing code (a device
   *  session, or any kiosk-variant device) → the button shows the
   *  code-display takeover and reads "Pair this screen". False on a
   *  web/mobile JWT session, which enters a code via the approve panel. */
  offersCode: boolean;
  onPairDevice: () => void;
  onForget: (d: Device) => void;
  onPruneInactive: () => void;
  wifi: WifiAgentState;
  voice: VoiceAgentState;
  onSetWakeWord: (deviceId: string, key: string | null) => void;
  onRenameDevice: (deviceId: string, name: string) => Promise<boolean>;
  /** Cycle 245: render only a subset of cards. "cgm" → the CGM connection
   *  card (+ Network if a loopback agent is present); "devices" → the
   *  Devices card. Undefined → all cards (web/Pi unchanged). Used by
   *  the tablet's + mobile's split Sensor (cgm) + Account (devices) tabs. */
  only?: "cgm" | "devices";
  /** Cycle 308: mobile — the CGM card's actions render as full-width rows
   *  (Update credentials / Reconnect / red Disconnect) instead of the pill
   *  cluster, and the card takes the Sensor-green accent so its mobile
   *  header band matches the index tile. Default false → byte-identical. */
  actionRows?: boolean;
}) {
  const cgmConnected = !!cgmStatus?.connected;
  const cgmHeaderRight = (
    <>
      <span
        className={styles.connDot}
        style={{
          background: cgmConnected ? "var(--zone-ok)" : "var(--ink-3)",
        }}
      />
      <span>{cgmConnected ? "Connected" : "Not connected"}</span>
    </>
  );

  const wifiStatus = wifi.status;
  const wifiConnected = wifiStatus?.connected === true;
  const wifiOnline = wifiStatus?.internet === true;
  const wifiHeaderRight = (
    <>
      <span
        className={styles.connDot}
        style={{
          background: wifiOnline
            ? "var(--zone-ok)"
            : wifiConnected
              ? "var(--zone-warn)"
              : "var(--ink-3)",
        }}
      />
      <span>
        {wifiConnected ? (wifiStatus?.ssid ?? "—") : "Not connected"}
      </span>
    </>
  );

  // Cycle 471: the header counts ACTIVE rows (the server's `is_active`, the
  // same split the "Show inactive" reveal uses), with the total in the
  // tooltip. Before this the header counted every row ever registered, so a
  // household that owns four devices read "11 devices".
  const deviceCount = countActiveDevices(devices);
  const deviceTotal = devices.length;
  const devicesHeaderRight =
    deviceTotal === 0 ? null : (
      <span title={`${deviceCount} active · ${deviceTotal} total`}>
        {deviceCount} device{deviceCount === 1 ? "" : "s"}
      </span>
    );

  return (
    <div className={styles.cardStack}>
      {only !== "devices" ? (
        <SubsectionCard
          title="CGM connection"
          subtitle="Where readings come from."
          icon={Icon.cgm}
          // Cycle 308: on mobile the Sensor pane's band matches its green
          // index tile; every other surface keeps the accent tone.
          tone={actionRows ? "ok" : "accent"}
          headerRight={cgmHeaderRight}
        >
          <CGMSection
            cgmStatus={cgmStatus}
            activeMemberName={activeMemberName}
            onDisconnect={onDisconnect}
            onReconnect={onReconnect}
            actionRows={actionRows}
          />
        </SubsectionCard>
      ) : null}
      {/* Cycle 56 / 74: Wi-Fi network subsection — kiosk-only, hidden
          when the local agent is unreachable or built without `nmcli`.
          The whole card is omitted on web / mobile / agent-down so
          there is no placeholder gap. */}
      {only !== "devices" && wifi.available ? (
        <SubsectionCard
          title="Network"
          subtitle="Wi-Fi for this kiosk."
          icon={Icon.wifi}
          tone="neutral"
          headerRight={wifiHeaderRight}
        >
          <NetworkSubsection wifi={wifi} />
        </SubsectionCard>
      ) : null}
      {only !== "cgm" ? (
        <SubsectionCard
          title={DEVICES_CARD_LABEL}
          subtitle="Paired devices on this account."
          icon={Icon.devices}
          tone="neutral"
          headerRight={devicesHeaderRight}
        >
          <DevicesSection
            devices={devices}
            isDeviceSession={isDeviceSession}
            offersCode={offersCode}
            onPairDevice={onPairDevice}
            onForget={onForget}
            onPruneInactive={onPruneInactive}
            voice={voice}
            onSetWakeWord={onSetWakeWord}
            onRenameDevice={onRenameDevice}
          />
        </SubsectionCard>
      ) : null}
    </div>
  );
}

// ─── Network (cycle 56) ───────────────────────────────────────────────

function NetworkSubsection({ wifi }: { wifi: WifiAgentState }) {
  const [networks, setNetworks] = useState<wifiApi.WifiNetwork[] | null>(null);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState<wifiApi.WifiNetwork | null>(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionInfo, setActionInfo] = useState<string | null>(null);

  const doScan = useCallback(async () => {
    setScanning(true);
    setScanError(null);
    const r = await wifi.scan();
    setScanning(false);
    if (r.ok) {
      setNetworks(r.data);
    } else {
      setScanError(r.error.message);
    }
  }, [wifi]);

  const onPick = (n: wifiApi.WifiNetwork) => {
    setSelected(n);
    setPassword("");
    setActionError(null);
    setActionInfo(null);
  };

  const isSecured = (n: wifiApi.WifiNetwork) =>
    n.security != null && n.security.trim() !== "";

  const onConnect = async () => {
    if (!selected) return;
    setBusy(true);
    setActionError(null);
    setActionInfo(null);
    const r = await wifi.connect({
      ssid: selected.ssid,
      password: isSecured(selected) ? password : "",
    });
    setBusy(false);
    if (!r.ok) {
      setActionError(r.error.message);
      return;
    }
    const body = r.data;
    if (body.ok && body.connected) {
      setActionInfo(
        body.internet
          ? `Connected to ${selected.ssid}.`
          : `Joined ${selected.ssid} but no internet yet.`,
      );
      setSelected(null);
      setPassword("");
      void doScan();
    } else {
      setActionError(body.error ?? "Could not connect.");
    }
  };

  const current = wifi.status;

  return (
    <>
      <Row label="Status">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span
            className={styles.connDot}
            style={{
              background:
                current?.internet === true
                  ? "var(--zone-ok)"
                  : current?.connected === true
                  ? "var(--zone-warn)"
                  : "var(--zone-urgent)",
            }}
          />
          {current?.connected
            ? current.internet
              ? `Online · ${current.ssid ?? "—"}`
              : `Joined ${current.ssid ?? "—"} · no internet`
            : "Not connected"}
          {current?.ip ? (
            <span className="tnum" style={{ color: "var(--ink-3)", marginLeft: 4 }}>
              · {current.ip}
            </span>
          ) : null}
        </span>
      </Row>
      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={doScan}
          disabled={scanning || busy}
        >
          {scanning ? "Scanning…" : networks == null ? "Scan for networks" : "Rescan"}
        </button>
        {selected ? (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => {
              setSelected(null);
              setPassword("");
            }}
          >
            Clear selection
          </button>
        ) : null}
      </div>
      {scanError ? (
        <div className={styles.subtleHint} style={{ color: "var(--zone-warn)" }}>
          {scanError}
        </div>
      ) : null}
      {networks != null && networks.length === 0 ? (
        <div className={styles.subtleHint}>No Wi-Fi networks found nearby.</div>
      ) : null}
      {networks != null && networks.length > 0 ? (
        <div className={styles.deviceList}>
          {networks.map((n) => {
            const isSel = selected?.ssid === n.ssid;
            return (
              <button
                key={n.ssid}
                type="button"
                onClick={() => onPick(n)}
                className={styles.deviceCard}
                style={{
                  background: isSel ? "var(--accent-soft)" : undefined,
                  borderColor: isSel ? "var(--accent)" : undefined,
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div className={styles.deviceMeta}>
                  <div className={styles.deviceName}>
                    {n.ssid}
                    {n.in_use ? (
                      <span
                        style={{
                          color: "var(--zone-ok)",
                          fontSize: "var(--type-floor)",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        connected
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.deviceSub}>
                    {isSecured(n) ? "secured" : "open"} · signal {n.signal}%
                  </div>
                </div>
                <span style={{ color: "var(--ink-3)", fontSize: 14 }}>
                  {isSecured(n) ? "🔒" : ""}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
      {selected ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 8,
            marginTop: 8,
          }}
        >
          <div className={styles.subtleHint}>
            Connecting to <strong>{selected.ssid}</strong>.
          </div>
          {isSecured(selected) ? (
            <Field label="Wi-Fi password">
              <input
                type="password"
                className={inputClass}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !busy && password.length > 0) {
                    void onConnect();
                  }
                }}
              />
            </Field>
          ) : null}
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={busy || (isSecured(selected) && password.length === 0)}
              onClick={onConnect}
            >
              {busy ? "Connecting…" : "Connect"}
            </button>
          </div>
        </div>
      ) : null}
      {actionError ? (
        <div className={styles.subtleHint} style={{ color: "var(--zone-warn)" }}>
          {actionError}
        </div>
      ) : null}
      {actionInfo ? (
        <div className={styles.subtleHint} style={{ color: "var(--accent)" }}>
          {actionInfo}
        </div>
      ) : null}
    </>
  );
}

function CGMSection({
  cgmStatus,
  activeMemberName,
  onDisconnect,
  onReconnect,
  actionRows = false,
}: {
  cgmStatus: cgmApi.CGMStatus | null;
  activeMemberName: string;
  onDisconnect: () => void;
  /** Cycle 234: opens the ProviderPicker → CGMConnectForm modal (owned by
   *  SettingsScreen). Before cycle 234 this prop was wired to
   *  `nav("/login")`, so the not-connected "Connect to CGM" + connected
   *  "Reconnect" buttons bounced an authenticated session to the sign-in
   *  splash — a dead-end that could never complete a fresh CGM link, which
   *  is why a Dexcom→Libre switch (disconnect then reconnect) looped. The
   *  handler now opens an in-place modal instead. */
  onReconnect: () => void;
  /** Cycle 308: mobile — render the connected-state actions as full-width
   *  tappable rows (Update credentials / Reconnect / red Disconnect CGM +
   *  consequence caption) instead of the three pill buttons. Every handler
   *  is the same object either way — presentation only. */
  actionRows?: boolean;
}) {
  const connected = !!cgmStatus?.connected;
  const provider = cgmStatus?.provider ?? null;
  // Cycle 88: in-place "update credentials" form (no ProviderPicker).
  const [updating, setUpdating] = useState(false);
  const latest =
    cgmStatus &&
    typeof cgmStatus.latest_reading === "object" &&
    cgmStatus.latest_reading !== null &&
    "timestamp" in cgmStatus.latest_reading
      ? (cgmStatus.latest_reading as { timestamp: string })
      : null;

  return (
    <>
      <div className={styles.subtleHint}>
        Linking CGM for: <strong>{activeMemberName}</strong>. To link a
        different member's CGM, switch active member or use Settings →
        Members.
      </div>
      <Row label="Provider">
        <strong style={{ fontWeight: 700 }}>
          {provider
            ? provider[0]!.toUpperCase() + provider.slice(1)
            : "Not linked"}
        </strong>
      </Row>
      <Row label="Status">
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            className={styles.connDot}
            style={{
              background: connected ? "var(--zone-ok)" : "var(--zone-urgent)",
            }}
          />
          {connected ? "Connected" : "Not connected"}
        </span>
      </Row>
      {latest && (
        <Row label="Latest reading">
          <span className="tnum">
            {fmtTime(latest.timestamp)} ·{" "}
            {fmtAge(
              Math.max(
                0,
                Math.floor(
                  (Date.now() - new Date(latest.timestamp).getTime()) /
                    60_000,
                ),
              ),
            )}
          </span>
        </Row>
      )}
      {actionRows && connected ? (
        // Cycle 308 (mobile): the three floating pills become full-width
        // rows — same handlers, destructive action isolated at the bottom
        // in red behind the existing ConfirmModal (onDisconnect confirms).
        <>
          {/* Cycle 88: in-place credential update (just username +
              password) — distinct from Reconnect, which re-runs the
              whole provider-picker flow. */}
          <div>
            <MobileActionRow
              icon={Icon.lock}
              tile="var(--settile-passkey)"
              label="Update credentials"
              expanded={updating}
              onClick={() => setUpdating((v) => !v)}
            />
            <MobileActionRow
              icon={Icon.refresh}
              tile="var(--settile-sensor)"
              label="Reconnect"
              onClick={onReconnect}
            />
            <MobileActionRow
              icon={<DisconnectIcon size={16} />}
              tile="var(--settile-alarms)"
              label="Disconnect CGM"
              red
              onClick={onDisconnect}
            />
            <div className={styles.mRowCaption}>
              Disconnecting stops readings and alarms for {activeMemberName}{" "}
              until a CGM is linked again. You&rsquo;ll be asked to confirm.
            </div>
          </div>
        </>
      ) : (
        <div className={styles.actionRow}>
          {connected ? (
            <>
              {/* Cycle 88: in-place credential update (just username +
                  password) — distinct from Reconnect, which re-runs the
                  whole provider-picker flow. */}
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => setUpdating((v) => !v)}
              >
                {updating ? "Cancel" : "Update credentials"}
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={onReconnect}
              >
                Reconnect
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={onDisconnect}
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={onReconnect}
            >
              Connect to CGM
            </button>
          )}
        </div>
      )}
      {connected && updating && provider ? (
        <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
          <CGMConnectForm
            provider={findProvider(provider)}
            mode="update"
            initialIdentifier={cgmStatus?.username ?? ""}
            initialRegion={cgmStatus?.region === "OUS" ? "OUS" : "US"}
            onChangeProvider={() => {}}
            onCancel={() => setUpdating(false)}
            onConnected={() => setUpdating(false)}
          />
        </div>
      ) : null}
    </>
  );
}

// ─── Devices ───────────────────────────────────────────────────────────

function DevicesSection({
  devices,
  isDeviceSession,
  offersCode,
  onPairDevice,
  onForget,
  onPruneInactive,
  voice,
  onSetWakeWord,
  onRenameDevice,
}: {
  devices: Device[];
  isDeviceSession: boolean;
  /** Cycle 221: device OFFERS a code (device session or kiosk variant) →
   *  label reads "Pair this screen" and the button opens the code display. */
  offersCode: boolean;
  onPairDevice: () => void;
  onForget: (d: Device) => void;
  onPruneInactive: () => void;
  voice: VoiceAgentState;
  onSetWakeWord: (deviceId: string, key: string | null) => void;
  onRenameDevice: (deviceId: string, name: string) => Promise<boolean>;
}) {
  const currentDeviceId = useMemo(() => getDeviceId(), []);
  // Cycle 175: split on the derived `active` flag (the single source of
  // truth shared with prune). Active rows show by default; inactive
  // (revoked / long-unseen — exactly what prune would delete) fold behind
  // a "Show inactive (N)" reveal so the list stays clean. A missing flag
  // (older server) is treated as active so nothing hides wrongly.
  const activeDevices = devices.filter((d) => d.active !== false);
  const inactiveDevices = devices.filter((d) => d.active === false);
  const [showInactive, setShowInactive] = useState(false);
  // Cycle 377: inline rename, same shape as the cycle-300 member rename.
  // `renameFor` = the device id currently being edited (null = none).
  const [renameFor, setRenameFor] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const submitRename = async (deviceId: string) => {
    const next = renameDraft.trim();
    if (!next) return;
    setRenameBusy(true);
    const ok = await onRenameDevice(deviceId, next);
    setRenameBusy(false);
    // Keep the editor open on failure so the typed name isn't lost.
    if (ok) setRenameFor(null);
  };
  const visibleDevices = showInactive
    ? [...activeDevices, ...inactiveDevices]
    : activeDevices;
  // Cycle 131 prune affordance — show only when there's something to clear
  // (an inactive row). Now keyed off `active` so a revoked-but-tokened row
  // counts too (the old `!has_token` test missed those).
  const hasInactive = inactiveDevices.length > 0;

  return (
    <>
      {/* Cycle 54: re-added the "Pair a device" entry. Opens the
          existing `/pair-approve` panel where a paired kiosk's 6-digit
          code is entered (cycle-8 pairing flow). Sits above the
          devices list so it's the first thing a user sees in the
          devices block, matching the muscle memory of finding pairing
          under Settings → Devices. */}
      <div className={styles.deviceActionsRow}>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={onPairDevice}
        >
          {/* Cycle 175/221: a device that OFFERS a code (a device-token
              kiosk, or any kiosk-variant device on a JWT session) reads
              "Pair this screen" → the code-display takeover; a web/mobile
              JWT session reads "Pair a device" → the approve (entry) panel. */}
          {offersCode ? "Pair this screen" : PAIR_A_DEVICE_LABEL}
        </button>
        {hasInactive ? (
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onPruneInactive}
          >
            Remove revoked / inactive devices
          </button>
        ) : null}
      </div>
      {devices.length === 0 ? (
        <div className={styles.subtleHint}>No devices yet.</div>
      ) : visibleDevices.length === 0 ? (
        <div className={styles.subtleHint}>No active devices.</div>
      ) : (
        <div className={styles.deviceList}>
          {visibleDevices.map((d) => {
            const isThis =
              currentDeviceId != null && d.id === currentDeviceId;
            const lastSeenAge =
              d.last_seen_at != null
                ? Math.max(
                    0,
                    Math.floor(
                      (Date.now() - new Date(d.last_seen_at).getTime()) /
                        60_000,
                    ),
                  )
                : null;
            const isKioskRow = d.device_type === "kiosk";
            // Cycle 100 — for the LOCAL kiosk row (this device + agent
            // reachable) prefer the agent's actual `.ppn` options so the
            // menu can only offer words the device can load; otherwise use
            // the platform catalog so an owner can assign from their phone.
            const localKiosk = isKioskRow && isThis && voice.available;
            const catalogOptions = localKiosk ? voice.options : WAKE_WORD_CATALOG;
            // If the saved word isn't in the option set (e.g. the chosen
            // `.ppn` isn't on this Pi, or a legacy/unknown key), include it
            // so the dropdown reflects the stored value rather than silently
            // snapping to the first option.
            const wakeOptions =
              d.wake_word && !catalogOptions.some((o) => o.key === d.wake_word)
                ? [
                    ...catalogOptions,
                    { key: d.wake_word, label: humanizeWakeKey(d.wake_word) },
                  ]
                : catalogOptions;
            const wakeMismatch =
              localKiosk &&
              d.wake_word != null &&
              !voice.options.some((o) => o.key === d.wake_word);
            return (
              <div key={d.id} className={styles.deviceCard}>
                <div className={styles.deviceMeta}>
                  {/* Cycle 377: inline rename. Before this the name was
                      write-once (set at pairing/registration), so a kiosk
                      approved with the pre-filled default stayed
                      mislabelled forever — and since a kiosk renders its
                      OWNER account's active member regardless of its
                      name, a wrong label reads as the wrong child's
                      glucose. Renaming is display-only and leaves the
                      device token alone, so the device stays paired. */}
                  {renameFor === d.id ? (
                    <div className={styles.renameRow}>
                      <input
                        className={styles.renameInput}
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        maxLength={100}
                        autoFocus
                        aria-label="Device name"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void submitRename(d.id);
                          if (e.key === "Escape") setRenameFor(null);
                        }}
                      />
                      <button
                        type="button"
                        className={styles.renameSave}
                        disabled={renameBusy || !renameDraft.trim()}
                        onClick={() => void submitRename(d.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className={styles.renameCancel}
                        onClick={() => setRenameFor(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                  <div className={styles.deviceName}>
                    {d.name}
                    <button
                      type="button"
                      className={styles.renameBtn}
                      title={`Rename "${d.name}"`}
                      aria-label={`Rename "${d.name}"`}
                      onClick={() => {
                        setRenameDraft(d.name);
                        setRenameFor(d.id);
                      }}
                    >
                      ✏️
                    </button>
                    {isThis ? (
                      <span
                        style={{
                          color: "var(--accent)",
                          fontSize: "var(--type-floor)",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                        }}
                      >
                        this device
                      </span>
                    ) : null}
                  </div>
                  )}
                  <div className={styles.deviceSub}>
                    {d.device_type}
                    {lastSeenAge != null
                      ? ` · last seen ${fmtAge(lastSeenAge)}`
                      : " · never seen"}
                    {d.last_ip ? ` · ${d.last_ip}` : ""}
                  </div>
                  {/* Cycle 100 — per-kiosk wake-word selector. Writes
                      through PATCH /api/devices/{id} (server-authoritative);
                      the kiosk pushes it to its agent on its next refresh /
                      reload. */}
                  {isKioskRow ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <label
                        className={styles.deviceSub}
                        style={{ flexShrink: 0 }}
                      >
                        Wake word
                      </label>
                      <select
                        className={styles.timeField}
                        style={{ maxWidth: 180 }}
                        value={d.wake_word ?? ""}
                        onChange={(e) =>
                          onSetWakeWord(d.id, e.target.value || null)
                        }
                      >
                        <option value="">Default (unset)</option>
                        {wakeOptions.map((o) => (
                          <option key={o.key} value={o.key}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                  {wakeMismatch ? (
                    <div
                      className={styles.subtleHint}
                      style={{ color: "var(--zone-warn)" }}
                    >
                      “{humanizeWakeKey(d.wake_word!)}” isn’t installed on this
                      kiosk — it’s still listening for{" "}
                      {voice.activeKey
                        ? `“${humanizeWakeKey(voice.activeKey)}”`
                        : "its default word"}
                      .
                    </div>
                  ) : null}
                </div>
                {/* Cycle 471: on a device session (a paired kiosk) Forget
                    is shown on THIS screen's row only, and it signs the
                    screen out (which self-revokes server-side). Other rows
                    are managed from a phone: the delete is JWT-only now. */}
                {isDeviceSession && !isThis ? null : (
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => onForget(d)}
                    title={
                      isDeviceSession && isThis
                        ? "Signs this screen out; it must be paired again."
                        : undefined
                    }
                  >
                    Forget
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Cycle 175: reveal the hidden inactive rows (revoked / long-unseen).
          Omitted entirely when there are none. Revealed rows keep every
          per-row control (Forget/revoke + wake-word). */}
      {inactiveDevices.length > 0 ? (
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => setShowInactive((v) => !v)}
          aria-expanded={showInactive}
        >
          {showInactive
            ? "Hide inactive"
            : `Show inactive (${inactiveDevices.length})`}
        </button>
      ) : null}
    </>
  );
}

// ─── Export ────────────────────────────────────────────────────────────

function ExportSection({ onError }: { onError: (msg: string) => void }) {
  const [hours, setHours] = useState("720");

  const onGlucose = async () => {
    const r = await exportApi.downloadGlucoseCsv(Number(hours));
    if (!r.ok) onError(r.message ?? "Download failed");
  };
  const onFull = async () => {
    const r = await exportApi.downloadFullCsv(Number(hours));
    if (!r.ok) onError(r.message ?? "Download failed");
  };

  return (
    <>
      <Row label="Window">
        <select
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          className={styles.timeField}
        >
          <option value="168">7 days</option>
          <option value="720">30 days</option>
          <option value="2160">90 days</option>
        </select>
      </Row>
      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={onGlucose}
        >
          Export glucose CSV
        </button>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={onFull}
        >
          Export full CSV
        </button>
      </div>
      <div className={styles.subtleHint}>
        Streamed download — no server-side buffering, even at 90 days.
      </div>
    </>
  );
}

// ─── Members (cycle 14: moved here from the Household tab) ────────────

type ConnectStep =
  | { kind: "pick" }
  | {
      kind: "form";
      provider: Provider;
      // Cycle 107: "update" mode (from the connection-pill sheet) renders
      // CGMConnectForm in update mode — provider read-only, username
      // prefilled, password blank — instead of the connect flow.
      mode?: "connect" | "update";
      initialIdentifier?: string;
      initialRegion?: "US" | "OUS";
    };

function memberSort(a: Member, b: Member): number {
  if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
  return a.display_name.localeCompare(b.display_name);
}

function providerLabel(provider: string | null): string {
  if (!provider) return "Not connected";
  if (provider === "dexcom") return "Connected to Dexcom";
  if (provider === "libre") return "Connected to Libre";
  return `Connected to ${provider[0]!.toUpperCase()}${provider.slice(1)}`;
}

function MembersSection({
  activeMemberId,
  refreshActive,
  currentUserId,
  variant,
}: {
  activeMemberId: string | null;
  refreshActive: () => Promise<void>;
  currentUserId: string | null;
  variant: FrameVariant;
}) {
  const { confirm } = useConfirm();
  const [members, setMembers] = useState<Member[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Cycle 234.8: a non-fatal connect-time WARNING (amber), distinct from the
  // neutral `info` banner — e.g. a household member's Libre link succeeded
  // but no sensor is shared yet. Rendered with the same ⚠ "Almost there"
  // framing as the active-member modal so it isn't read as a success note.
  const [warn, setWarn] = useState<string | null>(null);

  const [draftName, setDraftName] = useState("");
  const [draftLabel, setDraftLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const [connectFor, setConnectFor] = useState<Record<string, ConnectStep>>({});

  const reload = useCallback(
    async (archived = showArchived) => {
      // Cycle 113: source the Members pane from the ACCESSIBLE set
      // (owned ∪ granted via `member_links`) — the same `GET /api/me/members`
      // list the Household dashboard and the active-member switcher use —
      // instead of the owner-only `GET /api/members`. The owner-only source
      // was why a granted ("shared") member appeared on the dashboard but
      // not here, so the member couldn't manage member D's avatar from this pane.
      // Per-row owner-only controls (Archive, the link panel) are gated
      // below by `owner_user_id`; the avatar + CGM controls are already
      // server-authorized via the accessible-member set (cycle 105 / cycle
      // 60 `MemberPath`).
      const r = await membersApi.listMine(archived);
      if (r.ok) setMembers(r.data.members);
      else setError(r.error.message);
    },
    [showArchived],
  );

  // ── Cycle 105: per-member avatar upload ────────────────────────────
  const isKioskVariant = variant === "kiosk";

  // Cycle 330.4 — the kiosk can now take an avatar photo with its FRONT
  // camera. The cycle-105/108 rule ("kiosk is display-only, no upload") was
  // written when no kiosk had a camera; that hasn't been true since cycle
  // 200, and cycle 328 already opened BOTH remaining gates:
  //   * server — POST /api/members/{id}/avatar accepts a camera-capable
  //     kiosk (CurrentUserPhotoCapable + devices.camera_capable);
  //   * native — KioskWebChromeClient routes a capture-enabled file input to
  //     CameraCaptureActivity in MODE_AVATAR_FRONT (front lens), in-package,
  //     under lock-task.
  // Only this screen was still refusing. It now offers the same capture the
  // cycle-328 nudge does — as a deliberate control, not just a prompt.
  //
  // `isAndroidTabletClassRuntime()` is the SAME predicate cycle 328 uses.
  // Post-cycle-267 the `.kiosk` variant IS the native Android kiosk shell
  // (iPad + non-official tablets resolve to `mobile`; the Pi is retired), and
  // cycle 326 makes that shell always tablet-class — so in practice every
  // real kiosk qualifies. A camera-less kiosk would simply keep the old
  // display-only avatar rather than offering a button that 403s.
  const kioskCanCapture = isKioskVariant && isAndroidTabletClassRuntime();
  const canPickAvatar = !isKioskVariant || kioskCanCapture;

  // Cycle 108: ONE hidden file input with NO `capture` attribute. Tapping
  // the avatar clicks it directly, so iOS presents its native Take Photo /
  // Photo Library sheet (Android/desktop → native picker) — collapsing the
  // cycle-107 double-navigation. The picked file flows into the unchanged
  // cycle-105 pipeline (HEIC → cropper → JPEG → upload). `avatarMemberRef`
  // remembers which member the open input targets.
  //
  // Cycle 330.4: on the kiosk this input instead carries `capture="user"`.
  // That attribute is LOAD-BEARING, not a hint — it is exactly what
  // `FileChooserParams.isCaptureEnabled()` reads, and it is the discriminator
  // the native side branches on. WITHOUT it a kiosk tap falls through to the
  // MEAL chooser (rear lens, 16:9). With it, the front-lens avatar capture
  // opens. Do not "simplify" it away.
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  // Cycle 330.8: a SECOND input carrying `capture="user"` (front lens). Used
  // by the kiosk directly, and by the Android "Take photo" chooser option.
  const avatarCaptureInputRef = useRef<HTMLInputElement | null>(null);
  const avatarMemberRef = useRef<string | null>(null);
  const [avatarCropSource, setAvatarCropSource] = useState<Blob | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  // Cycle 107: member id whose CGM connection sheet is open (the photo
  // ActionSheet was removed in cycle 108).
  const [cgmSheetFor, setCgmSheetFor] = useState<string | null>(null);

  // Cycle 300: inline display-name editing. `renameFor` = the member id
  // currently being renamed; owned members only (a shared member you don't
  // own can't be renamed here). Reuses PATCH /api/members/{id}.
  const [renameFor, setRenameFor] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const onRenameSave = async (m: Member) => {
    const name = renameDraft.trim();
    if (!name || name === m.display_name) {
      setRenameFor(null);
      return;
    }
    setRenameBusy(true);
    setError(null);
    const r = await membersApi.patch(m.id, { display_name: name });
    setRenameBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setRenameFor(null);
    await reload();
    await refreshActive();
    setInfo(`Renamed to ${r.data.display_name}.`);
  };

  const onAvatarPicked = async (memberId: string, f: File | undefined) => {
    if (!f) return;
    avatarMemberRef.current = memberId;
    setError(null);
    try {
      setAvatarCropSource(await toCroppableBlob(f));
    } catch {
      setError("Couldn't read that image. Try a different photo or format.");
    }
  };

  const onAvatarConfirm = async (blob: Blob, w: number, h: number) => {
    const memberId = avatarMemberRef.current;
    setAvatarCropSource(null);
    if (!memberId) return;
    setAvatarBusy(true);
    const r = await avatarsApi.upload(memberId, blob, w, h, "avatar.jpg");
    setAvatarBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setInfo("Updated photo.");
    await reload();
    await refreshActive();
  };

  const onAvatarRemove = async (m: Member) => {
    // Cycle 106: the remove control is now a one-tap destructive icon, so
    // gate it behind a confirm (matching the section's app-confirm
    // pattern used by Disconnect / Archive).
    if (!(await confirm({ message: `Remove ${m.display_name}'s photo?`, destructive: true }))) return;
    const r = await avatarsApi.remove(m.id);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setInfo(`Removed ${m.display_name}'s photo.`);
    await reload();
    await refreshActive();
  };

  useEffect(() => {
    void reload(showArchived);
  }, [reload, showArchived]);

  // ── Cycle 330.8: Android needs an explicit camera path ───────────────
  //
  // Cycle 108 deliberately dropped the `capture` attribute so iOS presents
  // its own native "Take Photo / Photo Library" sheet — which works, and is
  // why iOS is left exactly as-is here.
  //
  // ANDROID has no such sheet: the WebView's file chooser offers PHOTOS ONLY,
  // so there was no way to take a picture (operator-reported). Android
  // therefore gets an explicit two-option chooser, whose "Take photo" fires a
  // SECOND input carrying `capture="user"` (front/selfie lens). That mirrors
  // what the cycle-328 AvatarNudge already does on Android.
  //
  // Kiosk is unchanged — it clicks the capture input directly (its native
  // CameraCaptureActivity is the chooser).
  //
  // Cycle 458: "phone" here is decided by the kiosk APK signal, never by the
  // layout — an A9+ running the standard phone app in landscape renders the
  // kiosk VARIANT and used to lose its "Choose from library" button. The kiosk
  // APK itself is camera-only: no library button, no gallery input.
  const avatarCameraOnly = cameraOnlyDevice();
  const isAndroidPhone =
    !avatarCameraOnly &&
    (capacitorPlatform() === "android" ||
      (typeof navigator !== "undefined" && /android/i.test(navigator.userAgent)));

  const openPhotoChooser = (memberId: string) => {
    avatarMemberRef.current = memberId;
    if (avatarCameraOnly || kioskCanCapture || isAndroidPhone) {
      // Kiosk + Android phone: straight to the camera capture input — no
      // intermediate app sheet. Android's no-`capture` chooser is reported
      // to offer photos only (no camera), which is why it can't be the
      // single tap target the way iOS/desktop's OS chooser can; a separate
      // "Choose from library" button (below, Android-only) reaches
      // avatarInputRef directly for the other case.
      // Cycle 455: "avatar" to the kiosk APK right before the click → the
      // cycle-328 front square camera (a silent no-op off the kiosk).
      openPhotoInput(avatarCaptureInputRef.current, "avatar");
      return;
    }
    // iOS / desktop web: the OS chooser already offers Take Photo + Photo
    // Library from one tap.
    avatarInputRef.current?.click();
  };

  const openGalleryChooser = (memberId: string) => {
    if (avatarCameraOnly) return;
    avatarMemberRef.current = memberId;
    openPhotoInput(avatarInputRef.current, "avatar");
  };
  // Cycle 455: on the kiosk the photo buttons are disabled while any alarm
  // sounds (lib/nativeCamera) — the camera must never open over an alarm.
  const photoBlocked = usePhotoBlockedByAlarm();

  // Cycle 107: "Update credentials" from the CGM sheet — open the inline
  // CGMConnectForm in update mode for the member's current provider,
  // prefilled with the stored non-secret username/region (the same path
  // the cycle-88 CgmAuthModal uses). Falls back to a bare update form if
  // the status fetch fails.
  const openUpdateCredentials = async (m: Member) => {
    if (!m.cgm_provider) return;
    const provider = findProvider(m.cgm_provider as ProviderId);
    const r = await membersApi.cgmStatus(m.id);
    const status = r.ok ? r.data : null;
    const region: "US" | "OUS" =
      status?.region === "OUS" ? "OUS" : "US";
    setConnectFor((prev) => ({
      ...prev,
      [m.id]: {
        kind: "form",
        provider,
        mode: "update",
        initialIdentifier: status?.username ?? "",
        initialRegion: region,
      },
    }));
  };

  const onSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const display_name = draftName.trim();
    if (!display_name) return;
    const r = await membersApi.create({
      display_name,
      relationship_label: draftLabel.trim() || null,
    });
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setDraftName("");
    setDraftLabel("");
    setAdding(false);
    setError(null);
    setInfo(`Added ${r.data.display_name}.`);
    await reload();
    await refreshActive();
  };

  // Cycle 233.2: per-card removal behind a brief INLINE confirm step (no
  // browser dialog). Two kinds, by member relationship:
  //   - "archive" → a member I OWN (POST /api/members/{id}/archive)
  //   - "unlink"  → a member SHARED to me (DELETE /api/me/members/{id}/link)
  // Only one card can be mid-confirm at a time; opening the confirm on
  // another card auto-cancels the prior. All state is local `useState` and
  // is not persisted across reload().
  type RemoveKind = "archive" | "unlink";
  const [pendingRemove, setPendingRemove] = useState<
    { id: string; kind: RemoveKind } | null
  >(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<
    { id: string; msg: string } | null
  >(null);

  const requestRemove = (m: Member, kind: RemoveKind) => {
    // Opening the confirm on one card auto-cancels any other pending confirm.
    setRemoveError(null);
    setPendingRemove({ id: m.id, kind });
  };
  const cancelRemove = () => setPendingRemove(null);
  const confirmRemove = async (m: Member) => {
    if (!pendingRemove || pendingRemove.id !== m.id) return;
    const kind = pendingRemove.kind;
    setRemoving(true);
    const r =
      kind === "archive"
        ? await membersApi.archive(m.id)
        : await membersApi.unlinkOwnGrant(m.id);
    setRemoving(false);
    if (!r.ok) {
      // Inline error on the card; clear the pending state.
      setRemoveError({ id: m.id, msg: r.error.message });
      setPendingRemove(null);
      return;
    }
    setPendingRemove(null);
    setRemoveError(null);
    setError(null);
    setInfo(
      kind === "archive"
        ? `Archived ${m.display_name}.`
        : `Removed ${m.display_name} from your list.`,
    );
    await reload();
    await refreshActive();
  };

  const onDisconnect = async (m: Member) => {
    const provider = m.cgm_provider
      ? m.cgm_provider[0]!.toUpperCase() + m.cgm_provider.slice(1)
      : "CGM";
    if (
      !(await confirm({
        message: `Disconnect ${m.display_name}'s ${provider} account? You can re-link any time.`,
        confirmLabel: "Disconnect",
        destructive: true,
      }))
    ) {
      return;
    }
    const r = await membersApi.disconnectCgm(m.id);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    setError(null);
    setWarn(null);
    setInfo(`Disconnected ${m.display_name}'s CGM.`);
    await reload();
  };

  const openConnect = (m: Member) => {
    // Clear a stale "no sensor shared" warning when re-attempting a connect
    // (e.g. after the user set up sharing and is reconnecting).
    setWarn(null);
    setConnectFor((prev) => ({ ...prev, [m.id]: { kind: "pick" } }));
  };
  const closeConnect = (m: Member) =>
    setConnectFor((prev) => {
      const next = { ...prev };
      delete next[m.id];
      return next;
    });
  const onPickProvider = (m: Member, p: Provider) =>
    setConnectFor((prev) => ({
      ...prev,
      [m.id]: { kind: "form", provider: p },
    }));
  const onChangeProvider = (m: Member) =>
    setConnectFor((prev) => ({ ...prev, [m.id]: { kind: "pick" } }));

  const onConnected = async (m: Member, result: cgmApi.CGMConnectResult) => {
    closeConnect(m);
    setError(null);
    // Cycle 234.6/234.8: surface a connect-time warning (e.g. a household
    // member's Libre link succeeded but no sensor is shared yet) in the amber
    // WARNING banner so the setup step isn't missed — actionable, not an error.
    if (result.warning) {
      setInfo(null);
      setWarn(result.warning);
      await reload();
      return;
    }
    setWarn(null);
    const n = result.backfill_count ?? 0;
    setInfo(
      n > 0
        ? `Linked ${m.display_name}'s CGM. Pulled ${n} reading${
            n === 1 ? "" : "s"
          } from the last 24 hours.`
        : `Linked ${m.display_name}'s CGM.`,
    );
    await reload();
  };

  const submitConnect =
    (memberId: string) =>
    (
      body: cgmApi.CGMConnectIn,
    ): Promise<ApiResult<cgmApi.CGMConnectResult>> =>
      membersApi.connectCgm(memberId, body);

  const sorted = useMemo(() => members.slice().sort(memberSort), [members]);

  return (
    <SubsectionCard
      title="Members"
      subtitle="Add household members, link each one's CGM, archive when needed."
      icon={Icon.users}
      tone="neutral"
    >
      {/* Cycle 108: one hidden file input. On web + mobile it carries NO
          `capture`, so the OS shows its native chooser (iOS: Take Photo /
          Photo Library) directly.

          Cycle 330.4: on the kiosk the SAME input carries `capture="user"`
          — the front/selfie lens. That attribute is what the native
          `FileChooserParams.isCaptureEnabled()` reads, and it is the
          discriminator that routes to the front-lens avatar capture instead
          of the rear-lens meal chooser. */}
      {canPickAvatar ? (
        <>
          {/* Gallery / OS-chooser input — NO `capture`. On iOS this is what
              makes the OS present its own Take Photo / Photo Library sheet
              (cycle 108), which is why iOS needs nothing else. */}
          {/* Cycle 458: not mounted on the kiosk APK (camera-only). */}
          {avatarCameraOnly ? null : (
            <input
              ref={avatarInputRef}
              type="file"
              accept={ACCEPT_IMAGE_GALLERY}
              style={{ display: "none" }}
              onChange={(e) => {
                void onAvatarPicked(
                  avatarMemberRef.current ?? "",
                  e.target.files?.[0],
                );
                e.target.value = "";
              }}
            />
          )}
          {/* CAMERA input — `capture="user"` (front/selfie lens). This
              attribute is load-bearing on the kiosk: it is exactly what
              `FileChooserParams.isCaptureEnabled()` reads, and it is the
              discriminator that routes the native WebView to the front-lens
              avatar capture instead of the rear-lens meal chooser. On an
              Android phone it is what actually opens the camera. */}
          <input
            ref={avatarCaptureInputRef}
            type="file"
            accept={ACCEPT_IMAGE_CAMERA}
            capture="user"
            style={{ display: "none" }}
            onChange={(e) => {
              void onAvatarPicked(
                avatarMemberRef.current ?? "",
                e.target.files?.[0],
              );
              e.target.value = "";
            }}
          />
        </>
      ) : null}
      {avatarCropSource ? (
        <AvatarCropper
          source={avatarCropSource}
          outputMaxLongEdge={512}
          busy={avatarBusy}
          onCancel={() => setAvatarCropSource(null)}
          onConfirm={(blob, w, h) => void onAvatarConfirm(blob, w, h)}
        />
      ) : null}
      {/* Cycle 107: connection sheet — Update credentials / Disconnect. */}
      {(() => {
        const cm = members.find((x) => x.id === cgmSheetFor);
        if (!cm || !cm.cgm_provider) return null;
        const opts: ActionSheetOption[] = [
          {
            key: "update",
            label: "Update credentials",
            icon: <KeyIcon size={20} />,
            onSelect: () => void openUpdateCredentials(cm),
          },
          {
            key: "disconnect",
            label: "Disconnect CGM",
            icon: <DisconnectIcon size={20} />,
            intent: "warn",
            divider: true,
            onSelect: () => onDisconnect(cm),
          },
        ];
        return (
          <ActionSheet
            open
            caption={`Connected to ${providerLabel(cm.cgm_provider)}`}
            options={opts}
            onClose={() => setCgmSheetFor(null)}
          />
        );
      })()}
      {/* Cycle 60: accept a link code from another household. Always
          visible — the entered code is matched to the inviting owner's
          member at submit time. */}
      <AcceptLinkCodeRow
        onAccepted={async () => {
          setInfo("Linked! Their member now appears in your switcher.");
          await reload();
          await refreshActive();
        }}
        onError={setError}
      />

      {(error || warn || info) && (
        <div
          className={styles.subtleHint}
          style={{
            color: error
              ? "var(--zone-urgent)"
              : warn
                ? "var(--zone-warn)"
                : "var(--ink)",
            background: error
              ? "var(--zone-urgent-soft)"
              : warn
                ? "var(--zone-warn-soft)"
                : "var(--accent-soft)",
            border: error
              ? "0.5px solid var(--line)"
              : warn
                ? "0.5px solid var(--zone-warn)"
                : "0.5px solid var(--line)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: "var(--type-floor)",
            lineHeight: warn && !error ? 1.45 : undefined,
          }}
        >
          {error ?? (warn ? (
            <>
              <strong style={{ fontWeight: 700 }}>⚠ Almost there — </strong>
              {warn}
            </>
          ) : (
            info
          ))}
        </div>
      )}

      {/* Add-member form */}
      {adding ? (
        <form
          onSubmit={onSubmitAdd}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: 12,
            border: "0.5px solid var(--line)",
            borderRadius: 12,
            background: "var(--bg)",
          }}
        >
          <Field label="Display name">
            <input
              className={inputClass}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="Elena"
              maxLength={100}
              autoFocus
            />
          </Field>
          <Field label="Relationship (optional)">
            <input
              className={inputClass}
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              placeholder="Partner"
              maxLength={50}
            />
          </Field>
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" disabled={!draftName.trim()}>
              Add
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAdding(false);
                setDraftName("");
                setDraftLabel("");
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Button onClick={() => setAdding(true)}>Add member</Button>
          <label
            style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)", display: "flex", gap: 6, alignItems: "center" }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
          </label>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {sorted.length === 0 ? (
          <div className={styles.subtleHint}>No members yet.</div>
        ) : null}
        {sorted.map((m) => {
          const isActive = m.id === activeMemberId;
          const isArchived = m.archived_at != null;
          // Cycle 113: a member I own (or a legacy row with a null owner)
          // vs one I only reach through a `member_links` grant ("shared").
          // Owner-only controls (Archive, the MemberLinkPanel) gate on this
          // so they never render on a shared row — matching the existing
          // cycle-60 server authorization (those routes stay owner-strict).
          const isOwned =
            m.owner_user_id == null || m.owner_user_id === currentUserId;
          // Cycle 233.2: `m.is_self` is the RAW column — true for a member
          // that is the self-member of ITS OWN account, even when that
          // account granted it to me. From the VIEWER's perspective a member
          // is "you" only when it's a self-member I also own; otherwise a
          // granted self-member (the member / member D, owned by another account)
          // is a SHARED member, not me. Drive the "you" pill + relationship
          // label off this viewer-relative flag so shared members read
          // correctly. (The archive gate already AND-ed `isOwned`, so it was
          // never wrong — only the label was.)
          const isViewerSelf = m.is_self && isOwned;
          const step = connectFor[m.id];
          return (
            <div
              key={m.id}
              style={{
                background: "var(--bg)",
                border: "0.5px solid",
                borderColor: isActive ? "var(--accent)" : "var(--line)",
                borderRadius: 12,
                padding: 12,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                opacity: isArchived ? 0.6 : 1,
              }}
            >
              {/* Cycle 108: two-line layout. Line 1 = avatar + name + chip
                  (the name gets the full line so it no longer truncates to
                  "M…"). Line 2 = the connection pill + (when an avatar
                  exists, non-kiosk) a Remove photo text button. */}
              <div className={styles.memberMain}>
                {/* Cycle 107/108: the avatar IS the photo trigger — a ≥44px
                    button with a camera badge that clicks the hidden input
                    directly. Cycle 330.4: this now includes the kiosk, whose
                    input opens the FRONT camera. Only a camera-less kiosk
                    (none in the field) still falls through to the
                    display-only avatar, rather than offering a button that
                    would 403 at the server. */}
                {!canPickAvatar ? (
                  <MemberAvatar
                    avatarId={m.avatar_id}
                    name={m.display_name}
                    size={80}
                    shape="circle"
                  />
                ) : (
                  <button
                    type="button"
                    className={styles.avatarBtn}
                    aria-label={`Add or change ${m.display_name}'s photo`}
                    disabled={avatarBusy || photoBlocked}
                    onClick={() => openPhotoChooser(m.id)}
                  >
                    <span className={styles.avatarWrap}>
                      <MemberAvatar
                        avatarId={m.avatar_id}
                        name={m.display_name}
                        size={80}
                        shape="circle"
                      />
                      <span className={styles.cameraBadge} aria-hidden>
                        <CameraIcon size={15} />
                      </span>
                    </span>
                  </button>
                )}
                <div className={styles.memberBody}>
                  <div className={styles.memberLine1}>
                    <div className={styles.memberNameBlock}>
                      {renameFor === m.id ? (
                        <div className={styles.renameRow}>
                          <input
                            className={styles.renameInput}
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            maxLength={100}
                            autoFocus
                            aria-label="Display name"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") void onRenameSave(m);
                              if (e.key === "Escape") setRenameFor(null);
                            }}
                          />
                          <button
                            type="button"
                            className={styles.renameSave}
                            disabled={renameBusy}
                            onClick={() => void onRenameSave(m)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className={styles.renameCancel}
                            onClick={() => setRenameFor(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className={styles.memberNameRow}>
                          <span className={styles.memberNameText}>
                            {m.display_name}
                          </span>
                          {/* Cycle 300: edit the display name (owned members
                              only — a shared member you don't own can't be
                              renamed here). */}
                          {isOwned && !isArchived ? (
                            <button
                              type="button"
                              className={styles.renameBtn}
                              aria-label={`Edit ${m.display_name}'s display name`}
                              title="Edit name"
                              onClick={() => {
                                setRenameDraft(m.display_name);
                                setRenameFor(m.id);
                              }}
                            >
                              <span aria-hidden>✏️</span>
                            </button>
                          ) : null}
                        </div>
                      )}
                      <div className={styles.memberRel}>
                        {m.relationship_label ||
                          (isViewerSelf ? "You" : "Member")}
                      </div>
                    </div>
                    <div className={styles.memberChips}>
                      {isViewerSelf ? <Pill tone="neutral">you</Pill> : null}
                      {/* Cycle 60/233.2: a member I reach via a `member_links`
                          grant rather than ownership reads as "shared" — even
                          when its raw `is_self` is true (a granted self-member
                          of another account). Gate on ownership, not the raw
                          self flag, so granted self-members stop showing
                          "you". */}
                      {!isOwned ? <Pill tone="ok">shared</Pill> : null}
                      {isArchived ? (
                        <Pill tone="neutral">archived</Pill>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.memberLine2}>
                    {/* Cycle 107: a connected member's pill is the CGM
                        trigger (provider + chevron → manage sheet). The
                        "Not connected" pill stays inert. */}
                    {m.cgm_provider && !isArchived ? (
                      <button
                        type="button"
                        className={styles.connPillBtn}
                        aria-label="Manage CGM connection"
                        onClick={() => setCgmSheetFor(m.id)}
                      >
                        {providerLabel(m.cgm_provider)}
                        <ChevronDownIcon size={13} />
                      </button>
                    ) : (
                      <Pill tone="neutral">
                        {providerLabel(m.cgm_provider)}
                      </Pill>
                    )}
                    {/* Cycle 340: Android phones — the avatar tap above now
                        goes straight to the camera (no intermediate app
                        sheet), so this is the one-tap path to the photo
                        library instead. iOS/desktop skip this: their avatar
                        tap already opens the OS chooser with both Take
                        Photo and Photo Library in one native prompt. */}
                    {canPickAvatar && isAndroidPhone ? (
                      <button
                        type="button"
                        className={styles.chooseLibraryBtn}
                        aria-label={`Choose a photo for ${m.display_name} from your library`}
                        disabled={avatarBusy || photoBlocked}
                        onClick={() => openGalleryChooser(m.id)}
                      >
                        <span aria-hidden>🖼️</span>
                        Choose from library
                      </button>
                    ) : null}
                    {/* Cycle 108: Remove photo relocated here (only when an
                        avatar exists). Runs the cycle-106 confirm + delete.
                        Cycle 330.4: also on the kiosk now — a surface that
                        can TAKE an avatar must be able to clear a bad one.
                        Server-side this already worked: DELETE
                        /api/members/{id}/avatar carries no non-kiosk guard
                        (MemberPath only), so no route change was needed. */}
                    {canPickAvatar && m.avatar_id ? (
                      <button
                        type="button"
                        className={styles.removePhotoBtn}
                        aria-label={`Remove ${m.display_name}'s photo`}
                        disabled={avatarBusy}
                        onClick={() => void onAvatarRemove(m)}
                      >
                        {/* Cycle 218: 🗑️ emoji replaces the trash SVG.
                            Decorative — the aria-label carries the real
                            accessible name; the button shell + red hover
                            stay token-driven. */}
                        <span className={styles.removePhotoEmoji} aria-hidden>
                          🗑️
                        </span>
                        Remove photo
                      </button>
                    ) : null}
                  </div>
                </div>
                {/* Cycle 233.2: removal icon at the card's trailing edge.
                    A member I OWN (non-self) → archive (trash); a member
                    SHARED to me (any owner ≠ me, incl. a granted self-member)
                    → unlink (remove from my list). My own self-member shows
                    no icon. First tap opens the inline confirm row below. */}
                {!m.is_self && isOwned ? (
                  <button
                    type="button"
                    className={styles.archiveIconBtn}
                    aria-label="Archive member"
                    onClick={() => requestRemove(m, "archive")}
                  >
                    <TrashIcon size={18} />
                  </button>
                ) : !isOwned ? (
                  <button
                    type="button"
                    className={styles.archiveIconBtn}
                    aria-label="Remove shared member"
                    onClick={() => requestRemove(m, "unlink")}
                  >
                    <DisconnectIcon size={18} />
                  </button>
                ) : null}
              </div>

              {/* Cycle 233.2: inline confirm row — only for the card whose
                  icon was tapped. Confirm archives (owner-strict
                  POST /api/members/{id}/archive) or unlinks (grantee-side
                  DELETE /api/me/members/{id}/link) per the pending kind. */}
              {pendingRemove?.id === m.id ? (
                <div className={styles.archiveConfirm}>
                  <div className={styles.archiveConfirmMsg}>
                    {pendingRemove.kind === "archive"
                      ? `Archive ${m.display_name}?`
                      : `Remove ${m.display_name} from your list?`}
                  </div>
                  <div className={styles.archiveConfirmActions}>
                    <button
                      type="button"
                      className={styles.btnDanger}
                      disabled={removing}
                      onClick={() => void confirmRemove(m)}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      disabled={removing}
                      onClick={cancelRemove}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}

              {removeError?.id === m.id ? (
                <div className={styles.archiveError}>{removeError.msg}</div>
              ) : null}

              {/* Cycle 107: the icon strip is gone — CGM actions live in
                  the connection-pill sheet, photo actions in the avatar
                  sheet.
                  Cycle 233.2: the Archive text button was removed in favor of
                  the trailing-edge trash icon + inline confirm above, so this
                  row keeps only Connect CGM (when not connected). Connect CGM
                  stays available on a shared row without a provider (the
                  cycle-60 `MemberPath` accepts the accessible set). */}
              {!isArchived && !m.cgm_provider && !step ? (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Button size="sm" onClick={() => openConnect(m)}>
                    Connect CGM
                  </Button>
                </div>
              ) : null}

              {step ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    paddingTop: 8,
                    borderTop: "0.5px dashed var(--line)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: "var(--type-floor)", fontWeight: 600 }}>
                      {step.kind === "form" && step.mode === "update"
                        ? `Update ${m.display_name}'s credentials`
                        : `Link ${m.display_name}'s CGM`}
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => closeConnect(m)}>
                      Cancel
                    </Button>
                  </div>
                  {step.kind === "pick" ? (
                    <ProviderPicker onPick={(p) => onPickProvider(m, p)} />
                  ) : (
                    <CGMConnectForm
                      provider={findProvider(step.provider.id as ProviderId)}
                      title={
                        step.mode === "update"
                          ? `Update ${m.display_name}'s ${step.provider.name} credentials`
                          : `Connect ${m.display_name}'s ${step.provider.name}`
                      }
                      mode={step.mode ?? "connect"}
                      initialIdentifier={step.initialIdentifier}
                      initialRegion={step.initialRegion}
                      submit={submitConnect(m.id)}
                      onChangeProvider={() => onChangeProvider(m)}
                      onConnected={(result) => void onConnected(m, result)}
                    />
                  )}
                </div>
              ) : null}

              {/* Cycle 60: link this member to another person's
                  account. Non-self, non-archived, owned-by-me only —
                  the panel's actions are owner-only on the server, so
                  hiding it on a shared member (where I'm only granted
                  access) avoids surfacing 404s in the UI. */}
              {!m.is_self && !isArchived && isOwned ? (
                <MemberLinkPanel
                  member={m}
                  onChanged={async () => {
                    await reload();
                    await refreshActive();
                  }}
                  onError={setError}
                  onInfo={setInfo}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </SubsectionCard>
  );
}

// ─── Cycle 60: Accept-link-code + per-member link panel ─────────────

function AcceptLinkCodeRow({
  onAccepted,
  onError,
}: {
  onAccepted: () => Promise<void> | void;
  onError: (msg: string) => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length !== 6 || !/^\d{6}$/.test(trimmed)) {
      onError("Enter the 6-digit code from the owner.");
      return;
    }
    setBusy(true);
    const r = await memberLinksApi.acceptInvite(trimmed);
    setBusy(false);
    if (!r.ok) {
      onError(
        r.error.message === "email_mismatch"
          ? "This code was issued to a different email."
          : r.error.message === "expired"
            ? "That code has expired. Ask the owner to send a new one."
            : r.error.message === "revoked"
              ? "That code was revoked."
              : "We couldn't accept that code.",
      );
      return;
    }
    setInfo(
      `You're now sharing data with ${r.data.owner_display_name || "the inviting account"}.`,
    );
    setCode("");
    await onAccepted();
  };

  return (
    <div
      style={{
        background: "var(--bg)",
        border: "0.5px dashed var(--line)",
        borderRadius: 12,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ fontSize: "var(--type-floor)", fontWeight: 600 }}>
        Have a link code from another household?
      </div>
      <form
        onSubmit={onSubmit}
        style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
      >
        <Field label="6-digit code">
          <input
            className={inputClass}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="123456"
            maxLength={6}
            style={{ width: 120, fontFamily: "var(--hero-font)", letterSpacing: "0.1em" }}
          />
        </Field>
        <Button type="submit" size="sm" disabled={busy || code.length !== 6}>
          {busy ? "Linking…" : "Link"}
        </Button>
      </form>
      {info ? (
        <div className={styles.subtleHint} style={{ color: "var(--accent)" }}>
          {info}
        </div>
      ) : null}
    </div>
  );
}


function MemberLinkPanel({
  member,
  onChanged,
  onError,
  onInfo,
}: {
  member: Member;
  onChanged: () => Promise<void> | void;
  onError: (msg: string) => void;
  onInfo: (msg: string) => void;
}) {
  const { confirm } = useConfirm();
  const [invites, setInvites] = useState<MemberLinkInvite[]>([]);
  const [grants, setGrants] = useState<MemberLinkGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [email, setEmail] = useState("");
  const [freshCode, setFreshCode] = useState<MemberLinkInviteWithCode | null>(
    null,
  );
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const [invitesRes, grantsRes] = await Promise.all([
      memberLinksApi.listInvites(member.id),
      memberLinksApi.listGrants(member.id),
    ]);
    if (invitesRes.ok) setInvites(invitesRes.data.invites);
    if (grantsRes.ok) setGrants(grantsRes.data.links);
    setLoading(false);
  }, [member.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      onError("Enter the email address of the person to link.");
      return;
    }
    setBusy("issue");
    const r = await memberLinksApi.createInvite(member.id, { email: trimmed });
    setBusy(null);
    if (!r.ok) {
      onError(r.error.message);
      return;
    }
    setFreshCode(r.data);
    setEmail("");
    setIssuing(false);
    await reload();
  };

  const onRevoke = async (inviteId: string) => {
    if (!(await confirm({ message: "Revoke this pending invite?", destructive: true }))) return;
    setBusy(`revoke:${inviteId}`);
    const r = await memberLinksApi.revokeInvite(inviteId);
    setBusy(null);
    if (!r.ok) {
      onError(r.error.message);
      return;
    }
    onInfo("Invite revoked.");
    await reload();
  };

  const onUnlink = async (g: MemberLinkGrant) => {
    if (
      !(await confirm({
        message: `Unlink ${g.display_name || g.email}? Their account will lose access to ${member.display_name}'s data; historical data stays merged.`,
        confirmLabel: "Unlink",
        destructive: true,
      }))
    ) {
      return;
    }
    setBusy(`unlink:${g.user_id}`);
    const r = await memberLinksApi.unlink(member.id, g.user_id);
    setBusy(null);
    if (!r.ok) {
      onError(r.error.message);
      return;
    }
    onInfo(`Unlinked ${g.display_name || g.email}.`);
    await onChanged();
    await reload();
  };

  if (loading) return null;
  const pending = invites.filter((i) => i.status === "pending");
  const hasAnything = grants.length > 0 || pending.length > 0 || freshCode || issuing;

  return (
    <div
      style={{
        paddingTop: 8,
        borderTop: "0.5px dashed var(--line)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "var(--type-floor)", fontWeight: 600 }}>
          Link to their account{" "}
          {grants.length > 0 ? (
            <Pill tone="ok">shared</Pill>
          ) : null}
        </div>
        {!issuing && !freshCode ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIssuing(true)}
            disabled={busy === "issue"}
          >
            {grants.length > 0 ? "Invite another" : "Invite"}
          </Button>
        ) : null}
      </div>

      {/* Issue form */}
      {issuing ? (
        <form
          onSubmit={onIssue}
          style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
        >
          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              autoFocus
              maxLength={320}
            />
          </Field>
          <Button type="submit" size="sm" disabled={busy === "issue"}>
            Send invite
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setIssuing(false);
              setEmail("");
            }}
          >
            Cancel
          </Button>
        </form>
      ) : null}

      {/* Fresh-issue code panel — shown once after the invite is created */}
      {freshCode ? (
        <div
          style={{
            background: "var(--accent-soft)",
            border: "0.5px solid var(--accent)",
            borderRadius: 10,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-2)" }}>
            Share this 6-digit code with {freshCode.invited_email}. They enter
            it in their own Sugar Alarm account → Settings → Members → "Have a
            link code?"
          </div>
          <div
            style={{
              fontFamily: "var(--hero-font)",
              fontSize: 32,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.18em",
              fontWeight: 700,
            }}
          >
            {freshCode.code}
          </div>
          <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>
            Expires {new Date(freshCode.expires_at).toLocaleString()}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setFreshCode(null)}
          >
            Done
          </Button>
        </div>
      ) : null}

      {/* Existing grants */}
      {grants.map((g) => (
        <div
          key={g.user_id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ minWidth: 0, fontSize: "var(--type-floor)" }}>
            <div style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Linked to {g.display_name || g.email}
            </div>
            <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>
              {g.email} · {g.role}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void onUnlink(g)}
            disabled={busy === `unlink:${g.user_id}`}
          >
            Unlink
          </Button>
        </div>
      ))}

      {/* Pending invites */}
      {pending.map((inv) => (
        <div
          key={inv.id}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ minWidth: 0, fontSize: "var(--type-floor)" }}>
            <div style={{ fontWeight: 600 }}>
              Pending: {inv.invited_email}
            </div>
            <div style={{ fontSize: "var(--type-floor)", color: "var(--ink-3)" }}>
              expires {new Date(inv.expires_at).toLocaleString()}
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void onRevoke(inv.id)}
            disabled={busy === `revoke:${inv.id}`}
          >
            Revoke
          </Button>
        </div>
      ))}

      {!hasAnything ? (
        <div className={styles.subtleHint} style={{ fontSize: "var(--type-floor)" }}>
          Inviting someone here ties this member to their account — they'll see
          and log {member.display_name}'s data from their own login.
        </div>
      ) : null}
    </div>
  );
}


// ─── Account (cycle 29: absorbs the Export pane above the existing
//     account actions) ───────────────────────────────────────────────

function AccountSection({
  auth,
  variant,
  onExportError,
  onReset,
  focusExport = false,
  leadingCards,
  mobileRows = false,
  devicesBody,
  deviceCount = 0,
  deviceTotal = 0,
}: {
  auth: AuthState;
  variant: FrameVariant;
  onExportError: (msg: string) => void;
  onReset: () => void;
  /** Cycle 161: the mobile index's "Export Data" row deep-links here
   *  with `?focus=export` — scroll the EXISTING Export card into view
   *  and ring it briefly. The card and its buttons are unchanged. */
  focusExport?: boolean;
  /** Cycle 224 (A4): Android tablet — the relocated CGM-connection +
   *  Devices cards, mounted ABOVE Export/Account. Undefined elsewhere. */
  leadingCards?: React.ReactNode;
  /** Cycle 308: mobile — recompose the pane as three row-based cards
   *  (Account / Devices / Danger Zone). Every action fires the same
   *  handler as the non-mobile layout — moved, not removed. Default
   *  false → kiosk/web/narrow-web render the existing stack
   *  byte-identical. */
  mobileRows?: boolean;
  /** Cycle 308: mobile — the relocated paired-devices list content (the
   *  same DevicesSection + hoisted handlers `connectionEl` mounts),
   *  revealed inline by the Devices card's "Manage devices" row. */
  devicesBody?: React.ReactNode;
  /** Cycle 471: ACTIVE rows (the pill's number) and every row (what gates
   *  the pill and sits in its tooltip), so a phone whose rows are all
   *  inactive still shows "0 devices" like the desktop header does. */
  deviceCount?: number;
  deviceTotal?: number;
}) {
  const exportCardRef = useRef<HTMLDivElement | null>(null);
  // Cycle 464: the Subscription card leads the pane. On ANY kiosk surface
  // (the variant, or a kiosk shell / device session in another layout) it
  // is the read-only status card with no controls.
  const subscriptionCard =
    variant === "kiosk" || isKioskSurface() ? (
      <KioskSubscriptionCard />
    ) : (
      <SubscriptionCard accountId={auth.user?.id ?? null} />
    );
  // Cycle 308: mobile row-expansion state. The Export row auto-opens when
  // the `?focus=export` deep link lands so the existing scroll + pulse
  // reveals the live CSV buttons, not a collapsed row.
  const [passkeysOpen, setPasskeysOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(() => focusExport);
  useEffect(() => {
    if (!focusExport) return;
    if (mobileRows) setExportOpen(true);
    const el = exportCardRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "start", behavior: "smooth" });
    el.classList.add(mi.exportPulse!);
    const t = setTimeout(() => el.classList.remove(mi.exportPulse!), 1800);
    return () => clearTimeout(t);
  }, [focusExport, mobileRows]);

  // Self-serve account deletion (Play/App-Store account-deletion
  // requirement). Strictly self-scoped on the server (DELETE /api/account
  // has NO target param, JWT-only). A strong destructive confirm, then the
  // call, then a hard sign-out so the now-deleted session can't linger.
  const { confirm, alert } = useConfirm();
  const [deletingAccount, setDeletingAccount] = useState(false);
  const handleDeleteAccount = useCallback(async () => {
    // Cycle 463.1: the confirmation says what happens to billing BEFORE the
    // person confirms — an App Store / Google Play subscription must be
    // cancelled in the store; a web subscription is cancelled by the delete.
    const preview = await getDeletionPreview();
    const ok = await confirm({
      title: "Delete account",
      message: deletionConfirmMessage(preview.ok ? preview.data : null),
      confirmLabel: "Delete my account",
      destructive: true,
    });
    if (!ok) return;
    setDeletingAccount(true);
    const r = await deleteMyAccount();
    if (!r.ok) {
      setDeletingAccount(false);
      await alert({
        title: "Couldn't delete",
        message: deletionFailureMessage(r.error),
      });
      return;
    }
    // Wipe the local session; the app falls back to the signed-out routes.
    await auth.signOut();
  }, [confirm, alert, auth]);

  // ─── Cycle 308: the mobile recomposition — three row-based cards.
  //     Account (sign-in + security) → Devices (paired list + export) →
  //     Danger Zone (destructive, red-banded). Every control that existed
  //     in the stacked layout below exists here: Passkeys, Change password
  //     (still the disabled future-cycle placeholder), Log out, the full
  //     DevicesSection (pair / forget / prune / wake-word), both CSV
  //     exports, Reset settings, and Delete account — same handlers,
  //     confirm flows, and server calls; only the presentation changed. ──
  if (mobileRows) {
    return (
      <div className={styles.cardStack}>
        {subscriptionCard}
        <SubsectionCard
          title="Account"
          subtitle="Sign-in and security."
          icon={Icon.account}
          accent="steel"
        >
          <Row label="Display name">
            <span>{auth.user?.display_name ?? "—"}</span>
          </Row>
          <Row label="Email">
            <span>{auth.user?.email}</span>
          </Row>
          <div>
            <MobileActionRow
              icon={Icon.lock}
              tile="var(--settile-passkey)"
              label="Passkeys"
              note="Face ID, Touch ID, PIN"
              expanded={passkeysOpen}
              onClick={() => setPasskeysOpen((v) => !v)}
            />
            {passkeysOpen ? (
              <div className={styles.mRowBody}>
                <PasskeysSection />
              </div>
            ) : null}
            <MobileActionRow
              icon={<KeyIcon size={16} />}
              tile="var(--settile-members)"
              label="Change password"
              disabled
              title="Coming in a future cycle"
            />
            <MobileActionRow
              icon={Icon.logout}
              tile="var(--settile-account)"
              label="Log out"
              onClick={() => auth.signOut()}
            />
          </div>
        </SubsectionCard>

        <div ref={exportCardRef}>
          <SubsectionCard
            title={DEVICES_CARD_LABEL}
            subtitle="Paired devices on this account."
            icon={Icon.devices}
            accent="cyan"
            headerRight={
              deviceTotal > 0 ? (
                <span
                  className={styles.mHeaderPill}
                  title={`${deviceCount} active · ${deviceTotal} total`}
                >
                  {deviceCount} device{deviceCount === 1 ? "" : "s"}
                </span>
              ) : undefined
            }
          >
            <div>
              <MobileActionRow
                icon={Icon.devices}
                tile="var(--settile-members)"
                label={MANAGE_DEVICES_LABEL}
                note="pair, forget, wake word"
                expanded={devicesOpen}
                onClick={() => setDevicesOpen((v) => !v)}
              />
              {devicesOpen ? (
                <div className={styles.mRowBody}>{devicesBody}</div>
              ) : null}
              <MobileActionRow
                icon={Icon.download}
                tile="var(--settile-voice)"
                label="Export data"
                note="glucose / full CSV"
                expanded={exportOpen}
                onClick={() => setExportOpen((v) => !v)}
              />
              {exportOpen ? (
                <div className={styles.mRowBody}>
                  <ExportSection onError={onExportError} />
                </div>
              ) : null}
            </div>
          </SubsectionCard>
        </div>

        <SubsectionCard
          title="Danger Zone"
          subtitle="These actions can't be undone easily."
          icon={Icon.warning}
          accent="rose"
        >
          <div>
            <MobileActionRow
              icon={Icon.refresh}
              tile="var(--settile-contacts)"
              label="Reset settings"
              red
              onClick={onReset}
            />
            <div className={styles.mRowCaption}>
              Returns every setting to defaults. CGM credentials and device
              tokens are not affected.
            </div>
            <MobileActionRow
              icon={<TrashIcon size={16} />}
              tile="var(--settile-danger)"
              label={deletingAccount ? "Deleting…" : "Delete account…"}
              red
              disabled={deletingAccount}
              onClick={handleDeleteAccount}
            />
            <div className={styles.mRowCaption}>
              Permanently deletes your account and all data — every member,
              glucose history, treatments, alerts, devices, and reminders.
            </div>
          </div>
        </SubsectionCard>
      </div>
    );
  }

  return (
    <div className={styles.cardStack}>
      {/* Cycle 224 (A4): Android tablet — CGM connection + Devices cards
          relocated to the top of the Account pane (CGM → Devices → Export
          → Account). Undefined on every other variant. */}
      {leadingCards}
      {subscriptionCard}
      {/* CSV export is a phone/desktop affordance — a kiosk has nowhere
          to put a downloaded file. */}
      {variant !== "kiosk" ? (
        <div ref={exportCardRef}>
          <SubsectionCard
            title="Export"
            subtitle="Download your data as CSV."
            icon={Icon.download}
            tone="neutral"
          >
            <ExportSection onError={onExportError} />
          </SubsectionCard>
        </div>
      ) : null}
      {/* Cycle 152: passkeys are a web/mobile affordance — the kiosk
          authenticates via device-token pairing, never a passkey. */}
      {variant !== "kiosk" ? (
        <SubsectionCard
          title="Passkeys"
          subtitle="Sign in with Face ID, Touch ID, or your device PIN."
          icon={Icon.account}
          tone="accent"
        >
          <PasskeysSection />
        </SubsectionCard>
      ) : null}
      <SubsectionCard
        title="Account"
        subtitle="Sign-in, password, and destructive actions."
        icon={Icon.account}
        tone="warn"
      >
        <Row label="Display name">
          <span>{auth.user?.display_name ?? "—"}</span>
        </Row>
        <Row label="Email">
          <span>{auth.user?.email}</span>
        </Row>
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.btnSecondary}
            disabled
            title="Coming in a future cycle"
          >
            Change password
          </button>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => auth.signOut()}
          >
            Log out
          </button>
        </div>
        <div className={styles.subtleHint} style={{ marginTop: 14 }}>
          Reset every setting in this screen back to its default. CGM
          credentials and device tokens are not affected.
        </div>
        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.btnDanger}
            onClick={onReset}
          >
            Reset settings
          </button>
        </div>
        {/* Self-serve account deletion. Web/mobile only — a shared kiosk
            (device-token session) must not be able to delete the owner's
            account; the server also refuses it (JWT-only). */}
        {variant !== "kiosk" ? (
          <>
            <div className={styles.subtleHint} style={{ marginTop: 14 }}>
              Permanently delete your account and all of your data (every
              member, their glucose history, treatments, alerts, devices,
              and reminders). This cannot be undone.
            </div>
            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.btnDanger}
                disabled={deletingAccount}
                onClick={handleDeleteAccount}
              >
                {deletingAccount ? "Deleting…" : "Delete account"}
              </button>
            </div>
          </>
        ) : null}
      </SubsectionCard>
    </div>
  );
}
